import { App } from './App';
import { AnimationScheduler } from './AnimationScheduler';
import { algorithms, formatCodeWithHighlight } from '../data/algorithms';
import { analysisModule } from '../modules/analysis/AnalysisModule';
import { settingsModule } from '../modules/settings/SettingsModule';
import { blocksModule } from '../modules/blocks/BlocksModule';
import { aboutModule } from '../modules/about/AboutModule';
import { guideModule } from '../modules/guide/GuideModule';
import { PROBLEM_INFO, PROBLEM_ANSWERS, PROBLEM_TEST_CASES } from '../data/problems';

// Wandbox API 配置 (日本在线编译服务，稳定可靠)
const WANDBOX_API_URL = 'https://wandbox.org/api/compile.json';

// 语言映射 (Wandbox 编译器)
const LANGUAGE_MAP: Record<string, string> = {
  'javascript': 'nodejs-20.17.0',
  'python': 'cpython-3.12.7',
  'java': 'openjdk-jdk-22+36',
  'cpp': 'gcc-head'
};

/**
 * UI 控制器 - 处理所有用户界面交互
 */
export class UIController {
  private app: App;
  private animationScheduler: AnimationScheduler | null = null;
  private isPlaying = false;
  private currentSpeed = 0.5;
  private currentAlgorithmId = '';
  private currentCodeLang = 'javascript';
  private currentModule = 'algorithms';  // 当前模块
  private judge0Available = false;       // Judge0 API可用性
  private currentProblemId = '';         // 当前题目ID
  private codeMirrorEditor: any = null;  // CodeMirror编辑器实例
  private previousTab = 'home';          // 进入编辑器前的TAB（home/favorites/history）

  constructor(app: App) {
    this.app = app;
  }

    /**
   * 初始化 UI 事件绑定
   */
  initialize(): void {
    // 初始化时检测设备类型（基于屏幕高度，不随窗口变化改变）
    this.detectDeviceType();
    
    this.bindNavigationEvents();
    this.bindPlaybackControls();
    this.bindSpeedControl();
    this.bindTemplateSelector();
    this.bindLogClear();
    this.bindLangTabs();
    this.bindRightPanelTabs();  // 绑定右侧面板TAB切换（小屏设备）
    this.bindPracticeEvents();  // 绑定实操大厅事件
    this.initAnalysisModule();   // 初始化分析模块
    this.initSettingsModule();   // 初始化设置模块
    this.testJudge0Connection(); // 测试灵梦编辑器连接
  }

  /**
   * 设置动画调度器
   */
  setAnimationScheduler(scheduler: AnimationScheduler): void {
    this.animationScheduler = scheduler;
    
    // 注册播放状态变化回调，以便在播放完成时自动更新UI
    scheduler.setOnPlayStateChange((isPlaying: boolean, finished?: boolean) => {
      this.isPlaying = isPlaying;
      const playBtn = document.getElementById('btn-play');
      if (playBtn) {
        playBtn.innerHTML = isPlaying 
          ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>'
          : '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
      }
      
      // 如果是自然播放完成，记录日志
      if (!isPlaying && finished) {
        this.app.log('播放完成 ✅', 'success');
      }
    });
  }

  /**
   * 绑定导航事件
   */
  private bindNavigationEvents(): void {
    const navButtons = document.querySelectorAll('.nav-btn');
    
    // 初始化导航指示器
    this.initNavIndicator();
    
    navButtons.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const module = target.dataset.module;
        
        // 更新激活状态
        navButtons.forEach(b => b.classList.remove('active'));
        target.classList.add('active');
        
        // 更新导航指示器
        this.updateNavIndicator(target);
        
        // 更新状态栏
        const modeEl = document.getElementById('status-mode');
        if (modeEl) {
          const newText = `模式: ${target.textContent}`;
          if (modeEl.textContent !== newText) {
            modeEl.textContent = newText;
            modeEl.classList.remove('status-glow');
            void modeEl.offsetWidth;
            modeEl.classList.add('status-glow');
          }
        }
        
        // 切换页面
        this.switchModule(module || 'algorithms');
        
        this.app.log(`切换到 ${target.textContent} 模块`, 'info');
        
                // TODO: 实际切换模块内容
        console.log('切换模块:', module);
      });
    });
  }

  /**
   * 检测设备类型（基于初始屏幕高度，不随窗口变化改变）
   */
  private detectDeviceType(): void {
    const screenHeight = window.screen.height; // 使用屏幕实际高度，不受虚拟键盘影响
    const isSmallScreen = screenHeight <= 700;
    
    if (isSmallScreen) {
      document.body.classList.add('small-screen');
      console.log(`📱 小屏设备模式 (屏幕高度: ${screenHeight}px)`);
      
      // 小屏设备：固定视口高度，防止虚拟键盘影响布局
      const initialHeight = window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${initialHeight}px`);
      console.log(`📏 固定视口高度: ${initialHeight}px`);
    } else {
      document.body.classList.remove('small-screen');
      console.log(`🖥️ 大屏设备模式 (屏幕高度: ${screenHeight}px)`);
    }
  }

  /**
   * 初始化导航指示器
   */
  private initNavIndicator(): void {
    const navMenu = document.querySelector('.nav-menu');
    if (!navMenu) return;
    
    // 创建指示器元素
    let indicator = navMenu.querySelector('.nav-indicator') as HTMLElement;
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'nav-indicator';
      navMenu.insertBefore(indicator, navMenu.firstChild);
    }
    
    // 多次尝试初始化位置
    const initPosition = () => {
      const activeBtn = navMenu.querySelector('.nav-btn.active') as HTMLElement;
      if (activeBtn && activeBtn.getBoundingClientRect().width > 0) {
        this.updateNavIndicator(activeBtn);
      }
    };
    
    initPosition();
    setTimeout(initPosition, 100);
    setTimeout(initPosition, 300);
  }

  /**
   * 更新导航指示器位置（垂直滑动）
   */
  private updateNavIndicator(activeBtn: HTMLElement): void {
    if (!activeBtn) return;
    
    const navMenu = document.querySelector('.nav-menu') as HTMLElement;
    const indicator = navMenu?.querySelector('.nav-indicator') as HTMLElement;
    if (!navMenu || !indicator) return;
    
    const menuRect = navMenu.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    
    // 垂直布局：计算top和height
    const top = btnRect.top - menuRect.top;
    const height = btnRect.height;
    
    indicator.style.top = `${top}px`;
    indicator.style.height = `${height}px`;
  }

  /**
   * 绑定运行控制
   */
  private bindPlaybackControls(): void {
    // 播放/暂停
    const playBtn = document.getElementById('btn-play');
    if (playBtn) {
      playBtn.addEventListener('click', () => {
        this.togglePlay();
      });
    }

    // 重置
    const resetBtn = document.getElementById('btn-reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', () => {
        this.reset();
      });
    }

    // 后退一步
    const stepBackBtn = document.getElementById('btn-step-back');
    if (stepBackBtn) {
      stepBackBtn.addEventListener('click', () => {
        this.stepBack();
      });
    }

    // 前进一步
    const stepForwardBtn = document.getElementById('btn-step-forward');
    if (stepForwardBtn) {
      stepForwardBtn.addEventListener('click', () => {
        this.stepForward();
      });
    }

    // 跳到结束
    const endBtn = document.getElementById('btn-end');
    if (endBtn) {
      endBtn.addEventListener('click', () => {
        this.jumpToEnd();
      });
    }
  }

  /**
   * 切换播放状态
   */
  private togglePlay(): void {
    this.isPlaying = !this.isPlaying;
    
    const playBtn = document.getElementById('btn-play');
    if (playBtn) {
      playBtn.innerHTML = this.isPlaying 
        ? '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/></svg>'
        : '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
    }
    
    if (this.isPlaying && this.animationScheduler) {
      this.animationScheduler.play();
      this.app.log('开始执行算法', 'info');
    } else if (this.animationScheduler) {
      this.animationScheduler.pause();
      this.app.log('暂停执行', 'warning');
    }
  }

  /**
   * 重置
   */
  private reset(): void {
    this.isPlaying = false;
    const playBtn = document.getElementById('btn-play');
    if (playBtn) playBtn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
    
    if (this.animationScheduler) {
      this.animationScheduler.reset();
    }
    
    this.app.updateStep(0, 0, '准备就绪');
    this.app.log('已重置', 'info');
  }

  /**
   * 后退一步
   */
  private stepBack(): void {
    if (this.animationScheduler) {
      this.animationScheduler.stepBack();
      this.app.log('后退一步', 'info');
    }
  }

  /**
   * 前进一步
   */
  private stepForward(): void {
    if (this.animationScheduler) {
      this.animationScheduler.stepForward();
      this.app.log('前进一步', 'info');
    }
  }

  /**
   * 跳到结束
   */
  private jumpToEnd(): void {
    if (this.animationScheduler) {
      this.animationScheduler.jumpToEnd();
      this.app.log('跳转到结束', 'info');
    }
  }

  /**
   * 绑定速度控制
   */
  private bindSpeedControl(): void {
    const slider = document.getElementById('speed-slider') as HTMLInputElement;
    const valueEl = document.getElementById('speed-value');
    
    if (slider && valueEl) {
      slider.addEventListener('input', () => {
        this.currentSpeed = parseFloat(slider.value);
        valueEl.textContent = `${this.currentSpeed}x`;
        
        if (this.animationScheduler) {
          this.animationScheduler.setSpeed(this.currentSpeed);
        }
      });
    }
  }

  /**
   * 绑定算法选择器
   */
  private bindTemplateSelector(): void {
    const algorithmBtns = document.querySelectorAll('.algorithm-btn');
    
    algorithmBtns.forEach(btn => {
      btn.addEventListener('click', async (e) => {
        const target = e.currentTarget as HTMLElement;
        const algorithmId = target.dataset.algorithm;
        
        if (algorithmId) {
          // 更新激活状态
          algorithmBtns.forEach(b => b.classList.remove('active'));
          target.classList.add('active');
          
          // 同步更新小屏算法列表的激活状态
          const algorithmBtnsSm = document.querySelectorAll('.algorithm-btn-sm');
          algorithmBtnsSm.forEach(b => {
            const btnEl = b as HTMLElement;
            if (btnEl.dataset.algorithm === algorithmId) {
              btnEl.classList.add('active');
            } else {
              btnEl.classList.remove('active');
            }
          });
          
          const algoName = target.querySelector('.algo-name')?.textContent || algorithmId;
          this.app.log(`已选择: ${algoName}`, 'success');
          
          // 重置并重新生成动画步骤
          await this.resetAndGenerateSteps(algorithmId);
        }
      });
    });
  }

  /**
   * 重置并生成新的动画步骤
   */
  private async resetAndGenerateSteps(templateId: string): Promise<void> {
    if (!this.animationScheduler) return;
    
    console.log('📦 切换模板:', templateId);
    
    // 先停止当前播放
    this.animationScheduler.pause();
    
    // 清空观察窗
    const varList = document.getElementById('variables-list');
    if (varList) varList.innerHTML = '<div class="history-placeholder">暂无执行历史，点击播放开始</div>';
    
    // 重置播放状态
    this.isPlaying = false;
    const playBtn = document.getElementById('btn-play');
    if (playBtn) playBtn.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>';
    
    // 根据模板类型切换可视化器并生成步骤
    const sortArray = [5, 3, 8, 1, 9, 2, 7, 4, 6];
    const searchArray = [1, 2, 3, 4, 5, 6, 7, 8, 9];
    const treeArray = [5, 3, 7, 1, 4, 6, 8];
    let steps;
    let algorithmName = '';
    
    switch (templateId) {
      case 'bubble-sort':
        algorithmName = '冒泡排序';
        await this.app.switchVisualizer('array', sortArray, 'cylinder');
        steps = this.animationScheduler.generateBubbleSortSteps(sortArray);
        break;
      case 'selection-sort':
        algorithmName = '选择排序';
        await this.app.switchVisualizer('array', sortArray, 'cylinder');
        steps = this.generateSelectionSortSteps(sortArray);
        break;
      case 'insertion-sort':
        algorithmName = '插入排序';
        await this.app.switchVisualizer('array', sortArray, 'cylinder');
        steps = this.generateInsertionSortSteps(sortArray);
        break;
      case 'quick-sort':
        algorithmName = '快速排序';
        await this.app.switchVisualizer('array', sortArray, 'hexagon');
        steps = this.generateQuickSortSteps(sortArray);
        break;
      case 'merge-sort':
        algorithmName = '归并排序';
        await this.app.switchVisualizer('array', sortArray, 'hexagon');
        steps = this.generateMergeSortSteps(sortArray);
        break;
      case 'heap-sort':
        algorithmName = '堆排序';
        await this.app.switchVisualizer('array', sortArray, 'pyramid');
        steps = this.generateHeapSortSteps(sortArray);
        break;
      case 'binary-search':
        algorithmName = '二分查找';
        await this.app.switchVisualizer('array', searchArray, 'cube');
        steps = this.generateBinarySearchSteps(searchArray, 7);
        break;
      case 'linear-search':
        algorithmName = '线性查找';
        await this.app.switchVisualizer('array', sortArray, 'cube');
        steps = this.generateLinearSearchSteps(sortArray, 7);
        break;
      case 'linked-list':
        algorithmName = '链表操作';
        await this.app.switchVisualizer('linked-list', [1, 2, 3, 4, 5]);
        steps = this.generateLinkedListSteps();
        break;
      case 'stack':
        algorithmName = '栈操作';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5], 'dataBlock');
        steps = this.generateStackSteps();
        break;
      case 'queue':
        algorithmName = '队列操作';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5], 'dataBlock');
        steps = this.generateQueueSteps();
        break;
      case 'binary-tree':
        algorithmName = '二叉树遍历';
        await this.app.switchVisualizer('binary-tree', treeArray);
        steps = this.generateBinaryTreeSteps();
        break;
      case 'bfs':
        algorithmName = '广度优先搜索';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5, 6, 7], 'node');
        steps = this.generateBFSSteps();
        break;
      case 'dfs':
        algorithmName = '深度优先搜索';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5, 6, 7], 'node');
        steps = this.generateDFSSteps();
        break;
      // === 新增排序算法 ===
      case 'shell-sort':
        algorithmName = '希尔排序';
        await this.app.switchVisualizer('array', sortArray, 'cylinder');
        steps = this.generateShellSortSteps(sortArray);
        break;
      case 'counting-sort':
        algorithmName = '计数排序';
        await this.app.switchVisualizer('array', [4, 2, 2, 8, 3, 3, 1], 'dataBlock');
        steps = this.generateCountingSortSteps([4, 2, 2, 8, 3, 3, 1]);
        break;
      case 'radix-sort':
        algorithmName = '基数排序';
        await this.app.switchVisualizer('array', [170, 45, 75, 90, 802, 24, 2, 66], 'hexagon');
        steps = this.generateRadixSortSteps([170, 45, 75, 90, 802, 24, 2, 66]);
        break;
      // === 新增搜索算法 ===
      case 'jump-search':
        algorithmName = '跳跃查找';
        await this.app.switchVisualizer('array', searchArray, 'cube');
        steps = this.generateJumpSearchSteps(searchArray, 7);
        break;
      // === 新增数据结构 ===
      case 'hash-table':
        algorithmName = '哈希表';
        await this.app.switchVisualizer('array', [3, 7, 1, 9, 5], 'dataBlock');
        steps = this.generateHashTableSteps();
        break;
      // === 新增图算法 ===
      case 'dijkstra':
        algorithmName = '最短路径(Dijkstra)';
        await this.app.switchVisualizer('array', [0, 1, 2, 3, 4, 5], 'node');
        steps = this.generateDijkstraSteps();
        break;
      case 'prim':
        algorithmName = '最小生成树(Prim)';
        await this.app.switchVisualizer('array', [0, 1, 2, 3, 4], 'node');
        steps = this.generatePrimSteps();
        break;
      case 'topological':
        algorithmName = '拓扑排序';
        await this.app.switchVisualizer('array', [0, 1, 2, 3, 4, 5], 'node');
        steps = this.generateTopologicalSteps();
        break;
      // === 动态规划 ===
      case 'fibonacci':
        algorithmName = '斐波那契数列';
        await this.app.switchVisualizer('array', [0, 1, 1, 2, 3, 5, 8, 13], 'crystal');
        steps = this.generateFibonacciSteps();
        break;
      case 'knapsack':
        algorithmName = '背包问题';
        await this.app.switchVisualizer('array', [2, 3, 4, 5], 'dataBlock');
        steps = this.generateKnapsackSteps();
        break;
      case 'lcs':
        algorithmName = '最长公共子序列';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5, 6], 'cube');
        steps = this.generateLCSSteps();
        break;
      // === 经典算法 ===
      case 'hanoi':
        algorithmName = '汉诺塔';
        await this.app.switchVisualizer('array', [3, 2, 1], 'pyramid');
        steps = this.generateHanoiSteps(3);
        break;
      case 'n-queens':
        algorithmName = 'N皇后问题';
        await this.app.switchVisualizer('array', [0, 0, 0, 0], 'crystal');
        steps = this.generateNQueensSteps(4);
        break;
      case 'maze':
        algorithmName = '迷宫求解';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5, 6, 7, 8, 9], 'node');
        steps = this.generateMazeSteps();
        break;
      case 'sudoku':
        algorithmName = '数独求解';
        await this.app.switchVisualizer('array', [5, 3, 0, 0, 7, 0, 0, 0, 0], 'cube');
        steps = this.generateSudokuSteps();
        break;
      case 'permutation':
        algorithmName = '全排列';
        await this.app.switchVisualizer('array', [1, 2, 3], 'hexagon');
        steps = this.generatePermutationSteps([1, 2, 3]);
        break;
      // === 字符串算法 ===
      case 'kmp':
        algorithmName = 'KMP字符串匹配';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5, 6, 7, 8], 'ribbon');
        steps = this.generateKMPSteps();
        break;
      case 'rabin-karp':
        algorithmName = 'Rabin-Karp算法';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5, 6, 7, 8], 'ribbon');
        steps = this.generateRabinKarpSteps();
        break;
      case 'edit-distance':
        algorithmName = '编辑距离';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5], 'crystal');
        steps = this.generateEditDistanceSteps();
        break;
      // === 数学算法 ===
      case 'gcd':
        algorithmName = '最大公约数';
        await this.app.switchVisualizer('array', [48, 18], 'gear');
        steps = this.generateGCDSteps(48, 18);
        break;
      case 'sieve':
        algorithmName = '素数筛法';
        await this.app.switchVisualizer('array', [2, 3, 4, 5, 6, 7, 8, 9, 10], 'prism');
        steps = this.generateSieveSteps(10);
        break;
      case 'power':
        algorithmName = '快速幂';
        await this.app.switchVisualizer('array', [2, 2, 2, 2, 2], 'star');
        steps = this.generatePowerSteps(2, 10);
        break;
      // === 动态规划补充 ===
      case 'coin-change':
        algorithmName = '零钱兑换';
        await this.app.switchVisualizer('array', [1, 2, 5], 'crystal');
        steps = this.generateCoinChangeSteps([1, 2, 5], 11);
        break;
      case 'lis':
        algorithmName = '最长递增子序列';
        await this.app.switchVisualizer('array', [10, 9, 2, 5, 3, 7, 101, 18], 'wave');
        steps = this.generateLISSteps([10, 9, 2, 5, 3, 7, 101, 18]);
        break;
      // === 贪心算法 ===
      case 'activity-selection':
        algorithmName = '活动选择';
        await this.app.switchVisualizer('array', [1, 2, 3, 4, 5, 6], 'wave');
        steps = this.generateActivitySelectionSteps();
        break;
      case 'huffman':
        algorithmName = '霍夫曼编码';
        await this.app.switchVisualizer('array', [5, 9, 12, 13, 16, 45], 'tower');
        steps = this.generateHuffmanSteps([5, 9, 12, 13, 16, 45]);
        break;
      // === 数据结构补充 ===
      case 'union-find':
        algorithmName = '并查集';
        await this.app.switchVisualizer('array', [0, 1, 2, 3, 4, 5, 6, 7], 'vortex');
        steps = this.generateUnionFindSteps();
        break;
      // === 图算法补充 ===
      case 'kruskal':
        algorithmName = 'Kruskal最小生成树';
        await this.app.switchVisualizer('array', [0, 1, 2, 3, 4], 'helix');
        steps = this.generateKruskalSteps();
        break;
      // === 经典算法补充 ===
      case 'closest-pair':
        algorithmName = '最近点对';
        await this.app.switchVisualizer('array', [2, 3, 5, 1, 8, 4, 7, 6], 'vortex');
        steps = this.generateClosestPairSteps();
        break;
      default:
        algorithmName = '冒泡排序(默认)';
        await this.app.switchVisualizer('array', sortArray, 'cylinder');
        steps = this.animationScheduler.generateBubbleSortSteps(sortArray);
    }
    
    console.log(`✅ ${algorithmName} 生成 ${steps.length} 步`);
    this.app.log(`已生成${algorithmName}动画 (${steps.length} 步)`, 'info');
    
    this.animationScheduler.setSteps(steps);
    
    // 更新代码面板
    this.updateCodePanel(templateId);
    
    // 更新算法介绍面板
    this.updateAlgoIntro(templateId);
  }
  
  /**
   * 更新代码面板
   */
  private updateCodePanel(algorithmId: string, lang?: string): void {
    const codeEl = document.getElementById('algorithm-code');
    const codeElSm = document.getElementById('algorithm-code-sm');  // 小屏专用
    
    this.currentAlgorithmId = algorithmId;
    const useLang = lang || this.currentCodeLang;
    
    const config = algorithms[algorithmId];
    let codeHtml = '';
    
    if (config && config.code) {
      let codeStr: string;
      if (typeof config.code === 'string') {
        // 单语言代码，默认为JS
        codeStr = config.code;
      } else {
        // 多语言代码
        codeStr = config.code[useLang as keyof typeof config.code] || config.code.javascript;
      }
      const highlightedCode = formatCodeWithHighlight(codeStr);
      codeHtml = `<code class="language-${useLang}">${highlightedCode}</code>`;
    } else {
      codeHtml = `<code class="language-javascript"><span class="code-comment">// ${algorithmId} 算法代码待添加</span></code>`;
    }
    
    // 同时更新底部面板和小屏专用面板
    if (codeEl) codeEl.innerHTML = codeHtml;
    if (codeElSm) codeElSm.innerHTML = codeHtml;
  }

  /**
   * 绑定语言切换标签
   */
  private bindLangTabs(): void {
    const langTabs = document.querySelectorAll('.lang-tab');
    langTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const lang = target.dataset.lang || 'javascript';
        
        // 更新激活状态
        langTabs.forEach(t => t.classList.remove('active'));
        target.classList.add('active');
        
        // 同步更新小屏语言TAB状态
        const langTabsSm = document.querySelectorAll('.lang-tab-sm');
        langTabsSm.forEach(t => {
          const tabEl = t as HTMLElement;
          if (tabEl.dataset.lang === lang) {
            tabEl.classList.add('active');
          } else {
            tabEl.classList.remove('active');
          }
        });
        
        // 切换语言
        this.currentCodeLang = lang;
        if (this.currentAlgorithmId) {
          this.updateCodePanel(this.currentAlgorithmId, lang);
        }
      });
    });
  }
  
  /**
   * 绑定右侧面板TAB切换（小屏设备专用）
   */
  private bindRightPanelTabs(): void {
    const tabs = document.querySelectorAll('.right-panel-tab');
    const views = document.querySelectorAll('.right-panel-view');
    
    tabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabName = target.dataset.tab;
        
        // 更新TAB激活状态
        tabs.forEach(t => t.classList.remove('active'));
        target.classList.add('active');
        
        // 切换内容视图
        views.forEach(view => {
          const viewElement = view as HTMLElement;
          if (viewElement.dataset.view === tabName) {
            viewElement.classList.add('active');
          } else {
            viewElement.classList.remove('active');
          }
        });
      });
    });
    
    // 绑定小屏专用语言切换TAB
    const langTabsSm = document.querySelectorAll('.lang-tab-sm');
    langTabsSm.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const lang = target.dataset.lang || 'javascript';
        
        // 更新激活状态
        langTabsSm.forEach(t => t.classList.remove('active'));
        target.classList.add('active');
        
        // 切换语言（同步底部面板的TAB状态）
        const bottomLangTabs = document.querySelectorAll('.bottom-panel .lang-tab');
        bottomLangTabs.forEach(t => {
          const tabEl = t as HTMLElement;
          if (tabEl.dataset.lang === lang) {
            tabEl.classList.add('active');
          } else {
            tabEl.classList.remove('active');
          }
        });
        
        // 更新代码面板
        this.currentCodeLang = lang;
        if (this.currentAlgorithmId) {
          this.updateCodePanel(this.currentAlgorithmId, lang);
        }
      });
    });
    
    // 绑定小屏专用算法列表点击事件
    const algorithmBtnsSm = document.querySelectorAll('.algorithm-btn-sm');
    algorithmBtnsSm.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const algorithmId = target.dataset.algorithm;
        if (!algorithmId) return;
        
        // 更新小屏算法列表激活状态
        algorithmBtnsSm.forEach(b => b.classList.remove('active'));
        target.classList.add('active');
        
        // 同步更新左侧面板的算法按钮状态
        const leftPanelBtns = document.querySelectorAll('#left-panel .algorithm-btn');
        leftPanelBtns.forEach(b => {
          const btnEl = b as HTMLElement;
          if (btnEl.dataset.algorithm === algorithmId) {
            btnEl.classList.add('active');
          } else {
            btnEl.classList.remove('active');
          }
        });
        
        // 触发算法切换
        const algoBtn = target;
        const algoName = algoBtn.textContent || algorithmId;
        this.app.log(`已选择: ${algoName}`, 'success');
        this.resetAndGenerateSteps(algorithmId);
        
        // 自动切换到运行控制TAB
        const controlTab = document.querySelector('.right-panel-tab[data-tab="control"]') as HTMLElement;
        if (controlTab) {
          controlTab.click();
        }
      });
    });
  }

  /**
   * 更新算法介绍面板
   */
  private updateAlgoIntro(algorithmId: string): void {
    const panel = document.getElementById('algo-intro-panel');
    const panelSm = document.getElementById('algo-intro-panel-sm');  // 小屏专用
    
    const config = algorithms[algorithmId];
    let html = '';
    
    if (!config || !config.description) {
      html = `
        <div class="algo-intro-placeholder">
          <div class="placeholder-icon">📊</div>
          <div class="placeholder-text">该算法介绍待添加</div>
        </div>
      `;
      if (panel) panel.innerHTML = html;
      if (panelSm) panelSm.innerHTML = html;
      return;
    }
    
    const desc = config.description;
    html = `<div class="algo-intro">`;
    
    // 简要介绍
    html += `<div class="algo-intro-brief">${desc.brief}</div>`;
    
    // 综合描述（放在最上面）
    if (desc.summary) {
      html += `
        <div class="algo-intro-section algo-summary">
          <h4>综合评价</h4>
          <p class="summary-text">${desc.summary}</p>
        </div>
      `;
    }
    
    // 复杂度
    html += `
      <div class="algo-complexity">
        <div class="complexity-item">
          <span class="complexity-label">时间</span>
          <span class="complexity-value">${desc.timeComplexity}</span>
        </div>
        <div class="complexity-item">
          <span class="complexity-label">空间</span>
          <span class="complexity-value">${desc.spaceComplexity}</span>
        </div>
      </div>
    `;
    
    // 应用场景
    if (desc.useCases && desc.useCases.length > 0) {
      html += `
        <div class="algo-intro-section">
          <h4>应用场景</h4>
          <div class="algo-tags">
            ${desc.useCases.map(u => `<span class="algo-tag">${u}</span>`).join('')}
          </div>
        </div>
      `;
    }
    
    // 优点
    if (desc.pros && desc.pros.length > 0) {
      html += `
        <div class="algo-intro-section">
          <h4>优点</h4>
          <div class="algo-tags">
            ${desc.pros.map(p => `<span class="algo-tag pro">${p}</span>`).join('')}
          </div>
        </div>
      `;
    }
    
    // 缺点
    if (desc.cons && desc.cons.length > 0) {
      html += `
        <div class="algo-intro-section">
          <h4>缺点</h4>
          <div class="algo-tags">
            ${desc.cons.map(c => `<span class="algo-tag con">${c}</span>`).join('')}
          </div>
        </div>
      `;
    }
    
    html += `</div>`;
    
    // 同时更新底部面板和小屏专用面板
    if (panel) panel.innerHTML = html;
    if (panelSm) panelSm.innerHTML = html;
  }

  /**
   * 生成链表操作步骤 - 增强动画效果
   */
  private generateLinkedListSteps(): any[] {
    const steps: any[] = [];
    const nodes = 5;
    
    steps.push({
      type: 'highlight',
      indices: [0],
      description: '链表遍历开始，从头节点出发',
      variables: { '链表长度': nodes }
    });
    
    for (let i = 0; i < nodes; i++) {
      // 访问当前节点
      if (i > 0) {
        steps.push({
          type: 'compare',
          indices: [i - 1, i],
          description: `指针移动: 节点${i} → 节点${i + 1}`,
          variables: { '当前节点': i + 1, '前一节点': i }
        });
      }
      
      steps.push({
        type: 'highlight',
        indices: [i],
        description: `访问节点 ${i + 1}，数据: node${i + 1}`,
        variables: { '节点': i + 1 }
      });
      
      // 标记已访问
      steps.push({
        type: 'mark',
        indices: [i],
        description: i === nodes - 1 ? '到达尾节点!' : `节点 ${i + 1} 已访问`
      });
    }
    
    steps.push({
      type: 'mark',
      indices: Array.from({length: nodes}, (_, i) => i),
      description: '链表遍历完成!'
    });
    return steps;
  }

  /**
   * 生成二叉树操作步骤 - 增强动画效果
   */
  private generateBinaryTreeSteps(): any[] {
    const steps: any[] = [];
    // 二叉树中序遍历演示: 1, 3, 4, 5, 6, 7, 8
    const inorderSequence = [2, 1, 3, 0, 5, 4, 6]; // 按Map的索引
    
    steps.push({
      type: 'highlight',
      indices: [0],
      description: '二叉树中序遍历开始',
      variables: { '遍历顺序': '左子树 → 根 → 右子树' }
    });
    
    for (let i = 0; i < inorderSequence.length; i++) {
      const node = inorderSequence[i];
      const prevNode = i > 0 ? inorderSequence[i - 1] : node;
      
      // 移动到下一个节点
      if (i > 0) {
        steps.push({
          type: 'compare',
          indices: [prevNode, node],
          description: `移动到节点 ${node}`,
          variables: { '步骤': i + 1, '当前节点': node }
        });
      }
      
      steps.push({
        type: 'highlight',
        indices: [node],
        description: `访问节点 ${node}`,
        variables: { '步骤': i + 1 }
      });
      
      steps.push({
        type: 'mark',
        indices: [node],
        description: i === inorderSequence.length - 1 ? '遍历完成!' : `节点 ${node} 已访问`
      });
    }
    
    steps.push({
      type: 'mark',
      indices: inorderSequence,
      description: `中序遍历完成! 顺序: ${inorderSequence.join(' → ')}`
    });
    return steps;
  }

  /**
   * 生成二分查找步骤 - 增强动画效果
   */
  private generateBinarySearchSteps(sortedArray: number[], target: number): any[] {
    const steps: any[] = [];
    let left = 0;
    let right = sortedArray.length - 1;
    let round = 1;
    
    steps.push({
      type: 'highlight',
      indices: [0],
      description: `二分查找开始，目标: ${target}`,
      variables: { '目标': target, '数组': sortedArray.join(', ') }
    });
    
    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      
      // 显示当前搜索范围
      steps.push({
        type: 'compare',
        indices: [left, right],
        description: `第 ${round} 轮: 搜索范围 [${left}, ${right}]`,
        variables: { '轮次': round, '左边界': left, '右边界': right, '中点': mid }
      });
      
      // 检查中间元素
      steps.push({
        type: 'swap',
        indices: [left, mid],
        description: `检查中点 arr[${mid}] = ${sortedArray[mid]}`,
        variables: { 'mid': mid, 'arr[mid]': sortedArray[mid], '目标': target }
      });
      
      if (sortedArray[mid] === target) {
        steps.push({
          type: 'mark',
          indices: [mid],
          description: `找到目标 ${target}，位置: ${mid}`,
          variables: { '结果': '成功', '位置': mid }
        });
        break;
      } else if (sortedArray[mid] < target) {
        steps.push({
          type: 'highlight',
          indices: [mid],
          description: `${sortedArray[mid]} < ${target}，在右半区继续搜索`,
          variables: { '下一范围': `[${mid + 1}, ${right}]` }
        });
        left = mid + 1;
      } else {
        steps.push({
          type: 'highlight',
          indices: [mid],
          description: `${sortedArray[mid]} > ${target}，在左半区继续搜索`,
          variables: { '下一范围': `[${left}, ${mid - 1}]` }
        });
        right = mid - 1;
      }
      round++;
    }
    
    if (left > right) {
      steps.push({
        type: 'highlight',
        indices: [0],
        description: `未找到目标 ${target}`,
        variables: { '结果': '未找到' }
      });
    }
    
    return steps;
  }

  /**
   * 生成线性查找步骤 - 增强动画效果
   */
  private generateLinearSearchSteps(array: number[], target: number): any[] {
    const steps: any[] = [];
    
    steps.push({
      type: 'highlight',
      indices: [0],
      description: `线性查找开始，目标: ${target}`,
      variables: { '目标': target, '数组长度': array.length }
    });
    
    for (let i = 0; i < array.length; i++) {
      // 显示比较过程
      if (i > 0) {
        steps.push({
          type: 'compare',
          indices: [i - 1, i],
          description: `移动到下一个元素`,
          variables: { '检查位置': i }
        });
      }
      
      steps.push({
        type: 'highlight',
        indices: [i],
        description: `检查 arr[${i}] = ${array[i]} ${array[i] === target ? '== ' + target + ' [OK]' : '!= ' + target}`,
        variables: { '位置': i, '值': array[i], '目标': target }
      });
      
      if (array[i] === target) {
        steps.push({
          type: 'mark',
          indices: [i],
          description: `找到目标 ${target}，位置: ${i}`,
          variables: { '结果': '成功', '位置': i }
        });
        return steps;
      }
    }
    
    steps.push({
      type: 'highlight',
      indices: [array.length - 1],
      description: `未找到目标 ${target}`,
      variables: { '结果': '未找到', '已检查': array.length }
    });
    
    return steps;
  }

  /**
   * 生成快速排序步骤
   */
  private generateQuickSortSteps(array: number[]): any[] {
    const steps: any[] = [];
    const arr = [...array];
    
    const partition = (low: number, high: number): number => {
      const pivot = arr[high];
      steps.push({
        type: 'highlight',
        indices: [high],
        description: `选择基准元素: ${pivot}`,
        variables: { pivot, low, high }
      });
      
      let i = low - 1;
      
      for (let j = low; j < high; j++) {
        steps.push({
          type: 'compare',
          indices: [j, high],
          description: `比较 arr[${j}]=${arr[j]} 与基准 ${pivot}`,
          variables: { i, j, 'arr[j]': arr[j], pivot }
        });
        
        if (arr[j] < pivot) {
          i++;
          if (i !== j) {
            [arr[i], arr[j]] = [arr[j], arr[i]];
            steps.push({
              type: 'swap',
              indices: [i, j],
              description: `交换 arr[${i}] 和 arr[${j}]`,
              variables: { i, j }
            });
          }
        }
        
        steps.push({ type: 'unmark', indices: [j, high], description: '' });
      }
      
      if (i + 1 !== high) {
        [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
        steps.push({
          type: 'swap',
          indices: [i + 1, high],
          description: `将基准放到正确位置`,
          variables: { '基准位置': i + 1 }
        });
      }
      
      steps.push({
        type: 'mark',
        indices: [i + 1],
        description: `位置 ${i + 1} 已排序`
      });
      
      return i + 1;
    };
    
    const quickSort = (low: number, high: number): void => {
      if (low < high) {
        const pi = partition(low, high);
        quickSort(low, pi - 1);
        quickSort(pi + 1, high);
      } else if (low === high) {
        steps.push({
          type: 'mark',
          indices: [low],
          description: `位置 ${low} 已排序`
        });
      }
    };
    
    quickSort(0, arr.length - 1);
    return steps;
  }

  /**
   * 生成归并排序步骤（简化版）
   */
  private generateMergeSortSteps(array: number[]): any[] {
    // 归并排序的可视化比较复杂，这里用冒泡代替演示
    return this.animationScheduler!.generateBubbleSortSteps(array);
  }

  /**
   * 生成选择排序步骤
   */
  private generateSelectionSortSteps(array: number[]): any[] {
    const arr = [...array];
    const steps: any[] = [];
    const n = arr.length;
    
    for (let i = 0; i < n - 1; i++) {
      let minIdx = i;
      steps.push({
        type: 'highlight',
        indices: [i],
        description: `开始第 ${i + 1} 轮，假设 arr[${i}]=${arr[i]} 是最小元素`,
        variables: { '轮次': i + 1, minIdx }
      });
      
      for (let j = i + 1; j < n; j++) {
        steps.push({
          type: 'compare',
          indices: [minIdx, j],
          description: `比较 arr[${minIdx}]=${arr[minIdx]} 和 arr[${j}]=${arr[j]}`,
          variables: { minIdx, j }
        });
        
        if (arr[j] < arr[minIdx]) {
          minIdx = j;
          steps.push({
            type: 'highlight',
            indices: [minIdx],
            description: `找到更小元素 arr[${minIdx}]=${arr[minIdx]}`,
            variables: { '新最小索引': minIdx }
          });
        }
      }
      
      if (minIdx !== i) {
        steps.push({
          type: 'swap',
          indices: [i, minIdx],
          description: `交换 arr[${i}] 和 arr[${minIdx}]`,
          variables: { '交换': `${arr[i]} ↔ ${arr[minIdx]}` }
        });
        [arr[i], arr[minIdx]] = [arr[minIdx], arr[i]];
      }
      
      steps.push({
        type: 'mark',
        indices: [i],
        description: `第 ${i + 1} 位已排好`
      });
    }
    
    steps.push({ type: 'mark', indices: [n - 1], description: '排序完成!' });
    return steps;
  }

  /**
   * 生成插入排序步骤
   */
  private generateInsertionSortSteps(array: number[]): any[] {
    const arr = [...array];
    const steps: any[] = [];
    const n = arr.length;
    
    steps.push({
      type: 'mark',
      indices: [0],
      description: `第一个元素 ${arr[0]} 视为已排序`
    });
    
    for (let i = 1; i < n; i++) {
      const key = arr[i];
      steps.push({
        type: 'highlight',
        indices: [i],
        description: `取出元素 arr[${i}]=${key} 准备插入`,
        variables: { 'key': key, 'i': i }
      });
      
      let j = i - 1;
      while (j >= 0 && arr[j] > key) {
        steps.push({
          type: 'compare',
          indices: [j, j + 1],
          description: `arr[${j}]=${arr[j]} > ${key}，后移`,
          variables: { j }
        });
        arr[j + 1] = arr[j];
        j--;
      }
      
      arr[j + 1] = key;
      steps.push({
        type: 'mark',
        indices: [j + 1],
        description: `将 ${key} 插入到位置 ${j + 1}`
      });
    }
    
    steps.push({ type: 'mark', indices: Array.from({ length: n }, (_, i) => i), description: '排序完成!' });
    return steps;
  }

  /**
   * 生成堆排序步骤
   */
  private generateHeapSortSteps(array: number[]): any[] {
    const arr = [...array];
    const steps: any[] = [];
    const n = arr.length;
    
    // 构建最大堆
    for (let i = Math.floor(n / 2) - 1; i >= 0; i--) {
      this.heapifySteps(arr, n, i, steps, '构建堆');
    }
    
    // 逐个提取最大值
    for (let i = n - 1; i > 0; i--) {
      steps.push({
        type: 'swap',
        indices: [0, i],
        description: `将堆顶 ${arr[0]} 与末尾 ${arr[i]} 交换`,
        variables: { '堆顶': arr[0], '末尾': arr[i] }
      });
      [arr[0], arr[i]] = [arr[i], arr[0]];
      
      steps.push({ type: 'mark', indices: [i], description: `${arr[i]} 已排序` });
      this.heapifySteps(arr, i, 0, steps, '调整堆');
    }
    
    steps.push({ type: 'mark', indices: [0], description: '堆排序完成!' });
    return steps;
  }
  
  private heapifySteps(arr: number[], n: number, i: number, steps: any[], phase: string): void {
    let largest = i;
    const left = 2 * i + 1;
    const right = 2 * i + 2;
    
    if (left < n && arr[left] > arr[largest]) {
      largest = left;
    }
    if (right < n && arr[right] > arr[largest]) {
      largest = right;
    }
    
    if (largest !== i) {
      steps.push({
        type: 'compare',
        indices: [i, largest],
        description: `${phase}: 比较节点 ${arr[i]} 和 ${arr[largest]}`,
        variables: { '父节点': arr[i], '子节点': arr[largest] }
      });
      steps.push({
        type: 'swap',
        indices: [i, largest],
        description: `交换以满足堆性质`
      });
      [arr[i], arr[largest]] = [arr[largest], arr[i]];
      this.heapifySteps(arr, n, largest, steps, phase);
    }
  }

  /**
   * 生成栈操作步骤 - 增强动画效果
   */
  private generateStackSteps(): any[] {
    const steps: any[] = [];
    const stack = [1, 2, 3, 4, 5];
    
    // 展示当前栈
    steps.push({
      type: 'highlight',
      indices: [4],
      description: '栈 (LIFO): 后进先出',
      variables: { '栈': stack.join(' → '), '栈顶': stack[4], '栈底': stack[0] }
    });
    
    steps.push({
      type: 'mark',
      indices: [4],
      description: `栈顶元素: ${stack[4]}`
    });
    
    // Pop操作演示
    for (let i = 4; i >= 2; i--) {
      if (i < 4) {
        steps.push({
          type: 'compare',
          indices: [i + 1, i],
          description: `栈顶下移到元素 ${stack[i]}`,
          variables: { '新栈顶': stack[i] }
        });
      }
      
      steps.push({
        type: 'swap',
        indices: [i, Math.max(0, i - 1)],
        description: `Pop: 弹出 ${stack[i]}`,
        variables: { '弹出元素': stack[i], '剩余栈': stack.slice(0, i).join(' → ') }
      });
    }
    
    // Push操作演示
    steps.push({
      type: 'swap',
      indices: [1, 2],
      description: `Push: 压入 6`,
      variables: { '新元素': 6 }
    });
    
    steps.push({
      type: 'mark',
      indices: [2],
      description: `新元素 6 成为栈顶`
    });
    
    steps.push({ 
      type: 'mark', 
      indices: [0, 1, 2], 
      description: '栈操作演示完成!'
    });
    return steps;
  }

  /**
   * 生成队列操作步骤 - 增强动画效果
   */
  private generateQueueSteps(): any[] {
    const steps: any[] = [];
    const queue = [1, 2, 3, 4, 5];
    
    // 展示队列
    steps.push({
      type: 'highlight',
      indices: [0],
      description: '队列 (FIFO): 先进先出',
      variables: { '队列': queue.join(' → '), '队头': queue[0], '队尾': queue[4] }
    });
    
    steps.push({
      type: 'mark',
      indices: [0],
      description: `队头元素: ${queue[0]}`
    });
    
    // Dequeue操作演示
    for (let i = 0; i < 3; i++) {
      if (i > 0) {
        steps.push({
          type: 'compare',
          indices: [i - 1, i],
          description: `队头移动到元素 ${queue[i]}`,
          variables: { '新队头': queue[i] }
        });
      }
      
      steps.push({
        type: 'swap',
        indices: [i, i + 1],
        description: `Dequeue: 出队 ${queue[i]}`,
        variables: { '出队元素': queue[i], '剩余队列': queue.slice(i + 1).join(' → ') }
      });
    }
    
    // Enqueue操作演示
    steps.push({
      type: 'swap',
      indices: [3, 4],
      description: `Enqueue: 入队 6 (加到队尾)`,
      variables: { '新元素': 6 }
    });
    
    steps.push({
      type: 'mark',
      indices: [4],
      description: `新元素 6 加入队尾`
    });
    
    steps.push({ 
      type: 'mark', 
      indices: [3, 4], 
      description: '队列操作演示完成!'
    });
    return steps;
  }

  /**
   * 生成BFS步骤 - 增强动画效果
   */
  private generateBFSSteps(): any[] {
    const steps: any[] = [];
    // 模拟图的层序遍历: 0 -> 1,2 -> 3,4,5,6
    const levels = [[0], [1, 2], [3, 4, 5, 6]];
    
    steps.push({
      type: 'highlight',
      indices: [0],
      description: 'BFS 广度优先搜索，从节点 0 开始',
      variables: { '队列': [0], '已访问': [] }
    });
    
    steps.push({
      type: 'mark',
      indices: [0],
      description: '起点 0 入队并访问'
    });
    
    let visited: number[] = [0];
    let prevNode = 0;
    
    for (let levelIdx = 1; levelIdx < levels.length; levelIdx++) {
      const level = levels[levelIdx];
      const parentLevel = levels[levelIdx - 1];
      
      steps.push({
        type: 'highlight',
        indices: [parentLevel[0]],
        description: `探索第 ${levelIdx + 1} 层节点`,
        variables: { '层级': levelIdx + 1, '节点数': level.length }
      });
      
      for (const node of level) {
        // 从父节点探索到当前节点
        steps.push({
          type: 'compare',
          indices: [prevNode, node],
          description: `发现节点 ${node}，加入队列`,
          variables: { '当前节点': node, '来自': prevNode }
        });
        
        steps.push({
          type: 'swap',
          indices: [prevNode, node],
          description: `访问节点 ${node}`,
          variables: { '队列': level.filter(n => !visited.includes(n)).join(', ') }
        });
        
        visited.push(node);
        steps.push({
          type: 'mark',
          indices: [node],
          description: `节点 ${node} 已访问`,
          variables: { '已访问': visited.join(' → ') }
        });
        
        prevNode = node;
      }
    }
    
    steps.push({ 
      type: 'mark', 
      indices: [0, 1, 2, 3, 4, 5, 6], 
      description: 'BFS 遍历完成! 顺序: 0 → 1 → 2 → 3 → 4 → 5 → 6'
    });
    return steps;
  }

  /**
   * 生成DFS步骤 - 增强动画效果
   */
  private generateDFSSteps(): any[] {
    const steps: any[] = [];
    // 模拟DFS深度遍历顺序: 0 -> 1 -> 3 -> 4 -> 2 -> 5 -> 6
    const dfsOrder = [0, 1, 3, 4, 2, 5, 6];
    const parentMap: Record<number, number> = {0: -1, 1: 0, 3: 1, 4: 1, 2: 0, 5: 2, 6: 2};
    
    steps.push({
      type: 'highlight',
      indices: [0],
      description: 'DFS 深度优先搜索，从节点 0 开始',
      variables: { '栈': [0], '已访问': [] }
    });
    
    steps.push({
      type: 'mark',
      indices: [0],
      description: '起点 0 入栈并访问'
    });
    
    let visited: number[] = [0];
    
    for (let i = 1; i < dfsOrder.length; i++) {
      const node = dfsOrder[i];
      const parent = parentMap[node];
      const prevNode = dfsOrder[i - 1];
      
      // 显示深入或回溯
      if (parent === prevNode) {
        // 深入
        steps.push({
          type: 'compare',
          indices: [prevNode, node],
          description: `深入: ${prevNode} → ${node}`,
          variables: { '操作': '深入', '从': prevNode, '到': node }
        });
      } else {
        // 回溯
        steps.push({
          type: 'compare',
          indices: [prevNode, parent],
          description: `回溯到节点 ${parent}`,
          variables: { '操作': '回溯', '从': prevNode, '到': parent }
        });
        
        steps.push({
          type: 'swap',
          indices: [parent, node],
          description: `探索新分支: ${parent} → ${node}`,
          variables: { '新节点': node }
        });
      }
      
      visited.push(node);
      steps.push({
        type: 'mark',
        indices: [node],
        description: `节点 ${node} 已访问`,
        variables: { '已访问': visited.join(' → ') }
      });
    }
    
    steps.push({ 
      type: 'mark', 
      indices: dfsOrder, 
      description: 'DFS 遍历完成! 顺序: 0 → 1 → 3 → 4 → 2 → 5 → 6'
    });
    return steps;
  }

  // ==================== 新增算法方法 ====================

  /**
   * 生成希尔排序步骤 - 增强动画效果
   */
  private generateShellSortSteps(array: number[]): any[] {
    const steps: any[] = [];
    const arr = [...array];
    let gap = Math.floor(arr.length / 2);
    let round = 1;
    
    while (gap > 0) {
      // 高亮当前间隔的所有分组
      const groupIndices = [];
      for (let i = 0; i < gap && i < arr.length; i++) {
        groupIndices.push(i);
      }
      steps.push({ 
        type: 'highlight', 
        indices: groupIndices.length > 0 ? [groupIndices[0]] : [0], 
        description: `第 ${round} 轮：间隔 gap = ${gap}`, 
        variables: { '轮次': round, '间隔': gap } 
      });
      
      for (let i = gap; i < arr.length; i++) {
        const temp = arr[i];
        let j = i;
        
        // 比较：高亮当前元素和它要比较的元素
        if (j >= gap) {
          steps.push({ 
            type: 'compare', 
            indices: [j, j - gap], 
            description: `比较 arr[${j}]=${arr[j]} 和 arr[${j-gap}]=${arr[j-gap]}`, 
            variables: { 'i': i, 'j': j, 'gap': gap, 'arr[j]': arr[j], 'arr[j-gap]': arr[j-gap] } 
          });
        }
        
        while (j >= gap && arr[j - gap] > temp) {
          // 交换动画
          steps.push({ 
            type: 'swap', 
            indices: [j, j - gap], 
            description: `${arr[j-gap]} > ${temp}，交换位置`, 
            variables: { '交换': `arr[${j}] ↔ arr[${j-gap}]`, '数组': `[${arr.join(', ')}]` }
          });
          arr[j] = arr[j - gap];
          j -= gap;
          
          // 继续比较下一个间隔位置
          if (j >= gap) {
            steps.push({ 
              type: 'compare', 
              indices: [j, j - gap], 
              description: `继续比较 arr[${j}]=${temp} 和 arr[${j-gap}]=${arr[j-gap]}`, 
              variables: { 'j': j, 'temp': temp, 'arr[j-gap]': arr[j-gap] } 
            });
          }
        }
        arr[j] = temp;
        
        // 标记插入完成
        steps.push({ 
          type: 'highlight', 
          indices: [j], 
          description: `${temp} 插入到位置 ${j}`, 
          variables: { '当前数组': `[${arr.join(', ')}]` } 
        });
      }
      
      gap = Math.floor(gap / 2);
      round++;
    }
    
    // 逐个标记已排序
    for (let i = 0; i < arr.length; i++) {
      steps.push({ 
        type: 'mark', 
        indices: [i], 
        description: i === arr.length - 1 ? '希尔排序完成!' : '' 
      });
    }
    return steps;
  }

  /**
   * 生成计数排序步骤 - 增强动画效果
   */
  private generateCountingSortSteps(array: number[]): any[] {
    const steps: any[] = [];
    const arr = [...array];
    const max = Math.max(...arr);
    const min = Math.min(...arr);
    const count: number[] = new Array(max - min + 1).fill(0);
    
    // 阶段1: 统计阶段 - 高亮每个元素并更新计数
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: `开始统计：范围 [${min}, ${max}]`, 
      variables: { '最小值': min, '最大值': max, '桶数量': max - min + 1 } 
    });
    
    for (let i = 0; i < arr.length; i++) {
      const val = arr[i];
      count[val - min]++;
      
      // 高亮当前元素
      steps.push({ 
        type: 'highlight', 
        indices: [i], 
        description: `统计 arr[${i}] = ${val}`, 
        variables: { '元素': val, '计数': count[val - min], '桶索引': val - min } 
      });
      
      // 如果有下一个元素，创建比较效果
      if (i < arr.length - 1) {
        steps.push({ 
          type: 'compare', 
          indices: [i, i + 1], 
          description: `准备处理下一个元素`, 
          variables: { '当前': arr[i], '下一个': arr[i + 1] } 
        });
      }
    }
    
    // 阶段2: 重建数组
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: '根据计数结果重建有序数组', 
      variables: { '计数数组': count.join(', ') } 
    });
    
    // 模拟重建过程
    const output: number[] = [];
    let outputIdx = 0;
    for (let i = 0; i <= max - min; i++) {
      const val = i + min;
      while (count[i] > 0) {
        output.push(val);
        
        // 显示放置动画
        if (outputIdx < arr.length) {
          steps.push({ 
            type: 'swap', 
            indices: [outputIdx, Math.min(outputIdx + 1, arr.length - 1)], 
            description: `放置 ${val} 到位置 ${outputIdx}`, 
            variables: { '值': val, '位置': outputIdx, '已排序': output.join(', ') } 
          });
        }
        
        outputIdx++;
        count[i]--;
      }
    }
    
    // 阶段3: 标记完成
    for (let i = 0; i < arr.length; i++) {
      steps.push({ 
        type: 'mark', 
        indices: [i], 
        description: i === arr.length - 1 ? '计数排序完成!' : `位置 ${i} 已确定` 
      });
    }
    
    return steps;
  }

  /**
   * 生成基数排序步骤 - 增强动画效果，真实模拟分桶和重排
   */
  private generateRadixSortSteps(array: number[]): any[] {
    const steps: any[] = [];
    const arr = [...array];
    const max = Math.max(...arr);
    let exp = 1;
    let digit = 1;
    
    // 计算最大位数
    const maxDigits = max > 0 ? Math.floor(Math.log10(max)) + 1 : 1;
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: `基数排序开始，最大值 ${max}，共 ${maxDigits} 位`, 
      variables: { '最大值': max, '位数': maxDigits, '原数组': arr.join(', ') } 
    });
    
    while (Math.floor(max / exp) > 0) {
      // 创建10个桶 (0-9)
      const buckets: number[][] = Array.from({ length: 10 }, () => []);
      
      // 阶段1: 高亮并提示当前处理的位
      steps.push({ 
        type: 'highlight', 
        indices: [0], 
        description: `===== 第 ${digit} 位排序 (${digit === 1 ? '个位' : digit === 2 ? '十位' : digit === 3 ? '百位' : `${digit}位`}) =====`, 
        variables: { '当前位': digit, '排序前': arr.join(', ') } 
      });
      
      // 阶段2: 分桶 - 逐个读取元素并分配到桶中
      for (let i = 0; i < arr.length; i++) {
        const digitVal = Math.floor(arr[i] / exp) % 10;
        buckets[digitVal].push(arr[i]);
        
        // 高亮当前元素
        steps.push({ 
          type: 'highlight', 
          indices: [i], 
          description: `${arr[i]} → 桶${digitVal}`, 
          variables: { '元素': arr[i], '当前位值': digitVal, '放入': `桶[${digitVal}]` } 
        });
        
        // 与下一个元素比较效果
        if (i < arr.length - 1) {
          const nextDigit = Math.floor(arr[i + 1] / exp) % 10;
          steps.push({ 
            type: 'compare', 
            indices: [i, i + 1], 
            description: `下一个: ${arr[i + 1]} 的第${digit}位是 ${nextDigit}`, 
            variables: { '当前': arr[i], '下一个': arr[i + 1] } 
          });
        }
      }
      
      // 显示桶状态
      const bucketStatus = buckets.map((b, idx) => b.length > 0 ? `桶${idx}:[${b.join(',')}]` : '').filter(s => s).join(' ');
      steps.push({ 
        type: 'highlight', 
        indices: [arr.length - 1], 
        description: `分桶完成`, 
        variables: { '桶状态': bucketStatus } 
      });
      
      // 阶段3: 收集 - 从桶中按顺序取出
      let idx = 0;
      const prevArr = [...arr];
      for (let b = 0; b < 10; b++) {
        for (const val of buckets[b]) {
          arr[idx] = val;
          
          // 显示元素移动到新位置
          const oldIdx = prevArr.indexOf(val);
          if (oldIdx !== idx && oldIdx >= 0 && oldIdx < arr.length) {
            steps.push({ 
              type: 'swap', 
              indices: [Math.min(oldIdx, arr.length - 1), Math.min(idx, arr.length - 1)], 
              description: `从桶${b}取出 ${val} → 位置 ${idx}`, 
              variables: { '元素': val, '来自': `桶[${b}]`, '新位置': idx } 
            });
          } else {
            steps.push({ 
              type: 'highlight', 
              indices: [Math.min(idx, arr.length - 1)], 
              description: `从桶${b}取出 ${val} → 位置 ${idx}`, 
              variables: { '元素': val, '位置': idx } 
            });
          }
          
          idx++;
        }
      }
      
      // 本轮完成
      steps.push({ 
        type: 'highlight', 
        indices: [0], 
        description: `第 ${digit} 位排序完成`, 
        variables: { '当前数组': arr.join(', ') } 
      });
      
      exp *= 10;
      digit++;
    }
    
    // 最终标记所有元素已排序
    for (let i = 0; i < arr.length; i++) {
      steps.push({ 
        type: 'mark', 
        indices: [i], 
        description: i === arr.length - 1 ? `基数排序完成! 结果: [${arr.join(', ')}]` : '' 
      });
    }
    
    return steps;
  }

  /**
   * 生成跳跃查找步骤 - 增强动画效果
   */
  private generateJumpSearchSteps(array: number[], target: number): any[] {
    const steps: any[] = [];
    const n = array.length;
    const jump = Math.floor(Math.sqrt(n));
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: `跳跃查找开始，步长 = √${n} ≈ ${jump}`, 
      variables: { '目标': target, '步长': jump, '数组长度': n } 
    });
    
    let prev = 0;
    let curr = 0;
    let jumpCount = 1;
    
    // 跳跃阶段
    while (curr < n && array[curr] < target) {
      // 高亮当前跳跃位置和上一个位置
      if (prev !== curr) {
        steps.push({ 
          type: 'compare', 
          indices: [prev, Math.min(curr, n - 1)], 
          description: `第 ${jumpCount} 次跳跃: arr[${curr}]=${array[Math.min(curr, n-1)]} < ${target}`, 
          variables: { '跳跃次数': jumpCount, '当前位置': curr, '当前值': array[Math.min(curr, n-1)] } 
        });
      }
      prev = curr;
      curr += jump;
      jumpCount++;
    }
    
    // 显示跳跃结束，准备线性查找
    const searchEnd = Math.min(curr, n - 1);
    steps.push({ 
      type: 'highlight', 
      indices: [prev], 
      description: `跳跃结束，在 [${prev}, ${searchEnd}] 区间线性查找`, 
      variables: { '搜索起点': prev, '搜索终点': searchEnd } 
    });
    
    // 线性查找阶段
    for (let i = prev; i <= searchEnd; i++) {
      // 比较相邻两个元素
      if (i < searchEnd) {
        steps.push({ 
          type: 'compare', 
          indices: [i, i + 1], 
          description: `线性检查: arr[${i}]=${array[i]} ${array[i] === target ? '== ' + target + ' [OK]' : '!= ' + target}`,
          variables: { '位置': i, '值': array[i], '目标': target } 
        });
      } else {
        steps.push({ 
          type: 'highlight', 
          indices: [i], 
          description: `检查: arr[${i}]=${array[i]}`, 
          variables: { '位置': i, '值': array[i] } 
        });
      }
      
      if (array[i] === target) {
        steps.push({ 
          type: 'mark', 
          indices: [i], 
          description: `找到目标 ${target}，位置: ${i}`, 
          variables: { '结果': '成功', '位置': i } 
        });
        return steps;
      }
    }
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: `未找到目标 ${target}`, 
      variables: { '结果': '未找到' } 
    });
    return steps;
  }

  /**
   * 生成哈希表步骤 - 增强动画效果
   */
  private generateHashTableSteps(): any[] {
    const steps: any[] = [];
    const keys = [3, 7, 1, 9, 5];
    const tableSize = 5;
    const table: (number | null)[] = [null, null, null, null, null];
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: `哈希表初始化，大小 = ${tableSize}`, 
      variables: { '待插入': keys.join(', '), '哈希函数': 'h(k) = k % ' + tableSize } 
    });
    
    for (let i = 0; i < keys.length; i++) {
      const key = keys[i];
      const hash = key % tableSize;
      
      // 显示计算哈希值
      steps.push({ 
        type: 'compare', 
        indices: [i, hash], 
        description: `插入 ${key}: h(${key}) = ${key} % ${tableSize} = ${hash}`, 
        variables: { 'key': key, '哈希值': hash } 
      });
      
      // 检查冲突
      if (table[hash] !== null) {
        steps.push({ 
          type: 'highlight', 
          indices: [hash], 
          description: `槽 ${hash} 已被占用，发生冲突!`, 
          variables: { '冲突位置': hash, '已存在': table[hash] } 
        });
      }
      
      table[hash] = key;
      
      // 放入槽位
      steps.push({ 
        type: 'swap', 
        indices: [i, hash], 
        description: `${key} → 槽[${hash}]`, 
        variables: { '操作': '插入', '哈希表': table.map((v, idx) => v !== null ? `[${idx}]:${v}` : `[${idx}]:-`).join(' ') } 
      });
      
      steps.push({ 
        type: 'mark', 
        indices: [hash], 
        description: `槽 ${hash} 已存入 ${key}` 
      });
    }
    
    // 最终状态
    for (let i = 0; i < tableSize; i++) {
      steps.push({ 
        type: 'mark', 
        indices: [i], 
        description: i === tableSize - 1 ? `哈希表演示完成!` : '' 
      });
    }
    return steps;
  }

  /**
   * 生成Dijkstra步骤 - 增强动画效果
   */
  private generateDijkstraSteps(): any[] {
    const steps: any[] = [];
    const nodes = [0, 1, 2, 3, 4, 5];
    const visitOrder = [0, 1, 3, 2, 4, 5];
    const distances = [0, 4, 8, 5, 12, 14];
    const prevNode = [-1, 0, 1, 0, 3, 2];
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: '从起点 0 开始 Dijkstra 算法',
      variables: { '起点': 0, '初始距离': 'dist[0]=0, 其他=∞' } 
    });
    
    steps.push({ 
      type: 'mark', 
      indices: [0], 
      description: '起点 0 距离确定: 0', 
      variables: { 'dist[0]': 0 } 
    });
    
    for (let i = 1; i < visitOrder.length; i++) {
      const node = visitOrder[i];
      const dist = distances[node];
      const prev = prevNode[node];
      
      // 显示从哪个节点连接过来
      steps.push({ 
        type: 'compare', 
        indices: [prev, node], 
        description: `探索节点 ${node}，从节点 ${prev} 到达`, 
        variables: { '当前节点': node, '来自': prev, '距离': dist } 
      });
      
      // 更新距离
      steps.push({ 
        type: 'swap', 
        indices: [prev, node], 
        description: `更新 dist[${node}] = ${dist}`, 
        variables: { [`dist[${node}]`]: dist, '路径': `0 → ... → ${prev} → ${node}` } 
      });
      
      steps.push({ 
        type: 'mark', 
        indices: [node], 
        description: `节点 ${node} 最短距离确定: ${dist}`
      });
    }
    
    steps.push({ 
      type: 'mark', 
      indices: nodes, 
      description: 'Dijkstra 完成! 所有最短路径已确定'
    });
    return steps;
  }

  /**
   * 生成Prim步骤 - 增强动画效果
   */
  private generatePrimSteps(): any[] {
    const steps: any[] = [];
    const nodes = [0, 1, 2, 3, 4];
    const mstOrder = [0, 1, 2, 4, 3];
    const edgeWeights = [0, 2, 3, 4, 1]; // 边权重
    const fromNode = [-1, 0, 1, 4, 2]; // 从哪个节点连接
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: 'Prim 算法: 从节点 0 构建最小生成树',
      variables: { '起点': 0, '节点数': nodes.length } 
    });
    
    steps.push({ 
      type: 'mark', 
      indices: [0], 
      description: '节点 0 加入 MST', 
      variables: { 'MST节点': [0] } 
    });
    
    let totalWeight = 0;
    for (let i = 1; i < mstOrder.length; i++) {
      const node = mstOrder[i];
      const from = fromNode[i];
      const weight = edgeWeights[i];
      totalWeight += weight;
      
      // 显示选择最小边
      steps.push({ 
        type: 'compare', 
        indices: [from, node], 
        description: `选择最小边: (${from}, ${node}) 权重 = ${weight}`, 
        variables: { '边': `${from} - ${node}`, '权重': weight } 
      });
      
      // 连接节点
      steps.push({ 
        type: 'swap', 
        indices: [from, node], 
        description: `添加边 (${from}, ${node}) 到 MST`, 
        variables: { '当前MST权重': totalWeight } 
      });
      
      steps.push({ 
        type: 'mark', 
        indices: [node], 
        description: `节点 ${node} 加入 MST`
      });
    }
    
    steps.push({ 
      type: 'mark', 
      indices: nodes, 
      description: `MST 构建完成! 总权重 = ${totalWeight}`
    });
    return steps;
  }

  /**
   * 生成拓扑排序步骤 - 增强动画效果
   */
  private generateTopologicalSteps(): any[] {
    const steps: any[] = [];
    const topoOrder = [0, 1, 2, 3, 4, 5];
    const inDegrees = [0, 1, 1, 2, 2, 1]; // 初始入度
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: '拓扑排序: 找出所有入度为0的节点',
      variables: { '入度': inDegrees.map((d, i) => `节点${i}:${d}`).join(', ') } 
    });
    
    const result: number[] = [];
    for (let i = 0; i < topoOrder.length; i++) {
      const node = topoOrder[i];
      
      // 找入度为0的节点
      if (i > 0) {
        steps.push({ 
          type: 'compare', 
          indices: [topoOrder[i-1], node], 
          description: `找到入度为 0 的节点: ${node}`, 
          variables: { '节点': node, '入度': 0 } 
        });
      }
      
      // 输出节点
      result.push(node);
      steps.push({ 
        type: 'swap', 
        indices: [i > 0 ? topoOrder[i-1] : 0, node], 
        description: `输出节点 ${node}，移除其出边`, 
        variables: { '当前结果': result.join(' → ') } 
      });
      
      steps.push({ 
        type: 'mark', 
        indices: [node], 
        description: `节点 ${node} 已输出` 
      });
    }
    
    steps.push({ 
      type: 'mark', 
      indices: topoOrder, 
      description: `拓扑排序完成! 顺序: ${result.join(' → ')}`
    });
    return steps;
  }

  /**
   * 生成斐波那契步骤
   */
  private generateFibonacciSteps(): any[] {
    const steps: any[] = [];
    const fib = [0, 1, 1, 2, 3, 5, 8, 13];
    
    steps.push({ type: 'highlight', indices: [0, 1], description: 'F(0)=0, F(1)=1', variables: { 'F(0)': 0, 'F(1)': 1 } });
    steps.push({ type: 'mark', indices: [0, 1], description: '初始值设置完成' });
    
    for (let i = 2; i < fib.length; i++) {
      steps.push({ type: 'compare', indices: [i-2, i-1], description: `F(${i}) = F(${i-2}) + F(${i-1}) = ${fib[i-2]} + ${fib[i-1]}`, variables: { [`F(${i})`]: fib[i] } });
      steps.push({ type: 'mark', indices: [i], description: `F(${i}) = ${fib[i]}` });
    }
    
    steps.push({ type: 'mark', indices: Array.from({length: fib.length}, (_, i) => i), description: '斐波那契数列完成!' });
    return steps;
  }

  /**
   * 生成背包问题步骤 - 增强动画效果
   */
  private generateKnapsackSteps(): any[] {
    const steps: any[] = [];
    const items = [{w: 2, v: 3}, {w: 3, v: 4}, {w: 4, v: 5}, {w: 5, v: 6}];
    const capacity = 8;
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: `0-1背包问题: 容量 = ${capacity}`,
      variables: { '容量': capacity, '物品数': items.length } 
    });
    
    // 显示所有物品
    for (let i = 0; i < items.length; i++) {
      steps.push({ 
        type: 'highlight', 
        indices: [i], 
        description: `物品 ${i+1}: 重量=${items[i].w}, 价值=${items[i].v}`, 
        variables: { '物品': i + 1, '重量': items[i].w, '价值': items[i].v } 
      });
    }
    
    // 动态规划过程
    let maxValue = 0;
    for (let i = 0; i < items.length; i++) {
      // 考虑当前物品
      if (i > 0) {
        steps.push({ 
          type: 'compare', 
          indices: [i - 1, i], 
          description: `考虑物品 ${i+1}: 装入 vs 不装`, 
          variables: { '物品': i + 1, '重量': items[i].w, '价值': items[i].v } 
        });
      }
      
      // 更新DP表
      maxValue = Math.max(maxValue, items[i].v);
      steps.push({ 
        type: 'swap', 
        indices: [Math.max(0, i - 1), i], 
        description: `更新 DP 表: 考虑物品 ${i+1}`, 
        variables: { '当前最大价值': maxValue } 
      });
      
      steps.push({ 
        type: 'mark', 
        indices: [i], 
        description: `物品 ${i+1} 处理完成` 
      });
    }
    
    steps.push({ 
      type: 'mark', 
      indices: [0, 1, 2, 3], 
      description: `背包问题求解完成! 最大价值 = ${maxValue}`
    });
    return steps;
  }

  /**
   * 生成LCS步骤 - 增强动画效果
   */
  private generateLCSSteps(): any[] {
    const steps: any[] = [];
    const s1 = 'ABCBDAB';
    const s2 = 'BDCAB';
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: `最长公共子序列 (LCS)`,
      variables: { '序列1': s1, '序列2': s2 } 
    });
    
    // 模拟DP过程
    const matches = [[0, 1], [1, 3], [2, 2], [4, 0], [5, 4]]; // 匹配位置
    
    for (let row = 0; row < 6; row++) {
      // 填充每一行
      if (row > 0) {
        steps.push({ 
          type: 'compare', 
          indices: [row - 1, row], 
          description: `填充 DP 表第 ${row} 行`, 
          variables: { '行': row, '字符': s1[row - 1] || '' } 
        });
      }
      
      // 检查是否匹配
      const matchInfo = matches.find(m => m[0] === row);
      if (matchInfo) {
        steps.push({ 
          type: 'swap', 
          indices: [Math.max(0, row - 1), row], 
          description: `发现匹配: s1[${row}] == s2[${matchInfo[1]}]`, 
          variables: { '匹配字符': s1[row] || s2[matchInfo[1]] } 
        });
      }
      
      steps.push({ 
        type: 'mark', 
        indices: [row], 
        description: `第 ${row} 行完成` 
      });
    }
    
    steps.push({ 
      type: 'mark', 
      indices: [0, 1, 2, 3, 4, 5], 
      description: `LCS = "BCAB", 长度 = 4`
    });
    return steps;
  }

  /**
   * 生成汉诺塔步骤
   */
  private generateHanoiSteps(n: number): any[] {
    const steps: any[] = [];
    
    steps.push({ type: 'highlight', indices: [0, 1, 2], description: `汉诺塔: ${n} 个圆盘`, variables: { '圆盘数': n } });
    
    const moves = [
      '圆盘 1: A → C',
      '圆盘 2: A → B',
      '圆盘 1: C → B',
      '圆盘 3: A → C',
      '圆盘 1: B → A',
      '圆盘 2: B → C',
      '圆盘 1: A → C'
    ];
    
    for (let i = 0; i < moves.length; i++) {
      steps.push({ type: 'swap', indices: [i % 3, (i + 1) % 3], description: moves[i], variables: { '步骤': i + 1 } });
    }
    
    steps.push({ type: 'mark', indices: [0, 1, 2], description: '汉诺塔完成!' });
    return steps;
  }

  /**
   * 生成N皇后步骤 - 增强动画效果
   */
  private generateNQueensSteps(n: number): any[] {
    const steps: any[] = [];
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: `${n} 皇后问题: 在 ${n}x${n} 棋盘上放置 ${n} 个互不攻击的皇后`,
      variables: { '棋盘大小': `${n}x${n}`, '皇后数': n }
    });
    
    const solution = [1, 3, 0, 2]; // 4皇后的一个解
    let placed = 0;
    
    for (let row = 0; row < n; row++) {
      // 尝试每一列
      for (let col = 0; col < n; col++) {
        if (col < solution[row]) {
          // 冲突，显示比较动画
          const prevRow = row > 0 ? row - 1 : 0;
          steps.push({ 
            type: 'compare', 
            indices: [prevRow, row], 
            description: `尝试 (行${row+1}, 列${col+1}) ❌ 冲突!`,
            variables: { '行': row + 1, '列': col + 1, '结果': '冲突' }
          });
        } else if (col === solution[row]) {
          // 找到安全位置
          if (row > 0) {
            steps.push({ 
              type: 'swap', 
              indices: [row - 1, row], 
              description: `尝试 (行${row+1}, 列${col+1}) 安全!`,
              variables: { '行': row + 1, '列': col + 1, '结果': '安全' }
            });
          }
          
          placed++;
          steps.push({ 
            type: 'mark', 
            indices: [row], 
            description: `在第 ${row+1} 行第 ${col+1} 列放置皇后`,
            variables: { '已放置': placed, '位置': `(${row+1}, ${col+1})` } 
          });
          break;
        }
      }
    }
    
    steps.push({ 
      type: 'mark', 
      indices: [0, 1, 2, 3], 
      description: `N皇后问题解决! 解: [${solution.map((c, r) => `(${r+1},${c+1})`).join(', ')}]`
    });
    return steps;
  }

  /**
   * 生成迷宫求解步骤 - 增强动画效果
   */
  private generateMazeSteps(): any[] {
    const steps: any[] = [];
    const path = [0, 1, 2, 5, 8, 7, 6, 3, 4]; // 迷宫路径
    
    steps.push({ 
      type: 'highlight', 
      indices: [0], 
      description: '迷宫求解: 从起点开始探索',
      variables: { '起点': 0, '终点': 4 }
    });
    
    steps.push({
      type: 'mark',
      indices: [0],
      description: '标记起点'
    });
    
    for (let i = 1; i < path.length; i++) {
      const cell = path[i];
      const prevCell = path[i - 1];
      
      // 移动到新格子
      steps.push({ 
        type: 'compare', 
        indices: [prevCell, cell], 
        description: `从格子 ${prevCell} 探索到格子 ${cell}`,
        variables: { '当前': cell, '来自': prevCell }
      });
      
      steps.push({ 
        type: 'swap', 
        indices: [prevCell, cell], 
        description: `移动到格子 ${cell}`,
        variables: { '路径': path.slice(0, i + 1).join(' → ') }
      });
      
      if (i === path.length - 1) {
        steps.push({ 
          type: 'mark', 
          indices: [cell], 
          description: '到达终点!',
          variables: { '总步数': path.length }
        });
      } else {
        steps.push({ 
          type: 'mark', 
          indices: [cell], 
          description: `标记格子 ${cell} 已访问`
        });
      }
    }
    
    steps.push({ 
      type: 'mark', 
      indices: path, 
      description: `迷宫求解完成! 路径: ${path.join(' → ')}`
    });
    return steps;
  }

  /**
   * 获取代码模板
   */
  private getCodeTemplate(templateId: string): string | null {
    const templates: Record<string, string> = {
      'bubble-sort': `// 冒泡排序
function bubbleSort(arr) {
  const n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      if (arr[j] > arr[j + 1]) {
        // 交换元素
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]];
      }
    }
  }
  return arr;
}`,
      'quick-sort': `// 快速排序
function quickSort(arr, low = 0, high = arr.length - 1) {
  if (low < high) {
    const pivot = partition(arr, low, high);
    quickSort(arr, low, pivot - 1);
    quickSort(arr, pivot + 1, high);
  }
  return arr;
}

function partition(arr, low, high) {
  const pivot = arr[high];
  let i = low - 1;
  
  for (let j = low; j < high; j++) {
    if (arr[j] < pivot) {
      i++;
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }
  [arr[i + 1], arr[high]] = [arr[high], arr[i + 1]];
  return i + 1;
}`,
      'merge-sort': `// 归并排序
function mergeSort(arr) {
  if (arr.length <= 1) return arr;
  
  const mid = Math.floor(arr.length / 2);
  const left = mergeSort(arr.slice(0, mid));
  const right = mergeSort(arr.slice(mid));
  
  return merge(left, right);
}

function merge(left, right) {
  const result = [];
  let i = 0, j = 0;
  
  while (i < left.length && j < right.length) {
    if (left[i] < right[j]) {
      result.push(left[i++]);
    } else {
      result.push(right[j++]);
    }
  }
  
  return result.concat(left.slice(i)).concat(right.slice(j));
}`,
      'binary-search': `// 二分查找
function binarySearch(arr, target) {
  let left = 0;
  let right = arr.length - 1;
  
  while (left <= right) {
    const mid = Math.floor((left + right) / 2);
    
    if (arr[mid] === target) {
      return mid; // 找到目标
    } else if (arr[mid] < target) {
      left = mid + 1;
    } else {
      right = mid - 1;
    }
  }
  
  return -1; // 未找到
}`,
      'linear-search': `// 线性查找
function linearSearch(arr, target) {
  for (let i = 0; i < arr.length; i++) {
    if (arr[i] === target) {
      return i; // 找到目标
    }
  }
  return -1; // 未找到
}`,
      'linked-list': `// 链表节点
class ListNode {
  constructor(value) {
    this.value = value;
    this.next = null;
  }
}

// 链表
class LinkedList {
  constructor() {
    this.head = null;
  }
  
  // 尾部插入
  append(value) {
    const node = new ListNode(value);
    if (!this.head) {
      this.head = node;
    } else {
      let current = this.head;
      while (current.next) {
        current = current.next;
      }
      current.next = node;
    }
  }
  
  // 遍历打印
  print() {
    let current = this.head;
    const values = [];
    while (current) {
      values.push(current.value);
      current = current.next;
    }
    console.log(values.join(' -> '));
  }
}`,
      'binary-tree': `// 二叉树节点
class TreeNode {
  constructor(value) {
    this.value = value;
    this.left = null;
    this.right = null;
  }
}

// 二叉搜索树
class BST {
  constructor() {
    this.root = null;
  }
  
  insert(value) {
    this.root = this._insert(this.root, value);
  }
  
  _insert(node, value) {
    if (!node) return new TreeNode(value);
    
    if (value < node.value) {
      node.left = this._insert(node.left, value);
    } else {
      node.right = this._insert(node.right, value);
    }
    return node;
  }
  
  // 中序遍历
  inorder(node = this.root, result = []) {
    if (node) {
      this.inorder(node.left, result);
      result.push(node.value);
      this.inorder(node.right, result);
    }
    return result;
  }
}`,
      'graph': `// 图遍历
class Graph {
  constructor() {
    this.adjacencyList = new Map();
  }
  
  addVertex(vertex) {
    if (!this.adjacencyList.has(vertex)) {
      this.adjacencyList.set(vertex, []);
    }
  }
  
  addEdge(v1, v2) {
    this.adjacencyList.get(v1).push(v2);
    this.adjacencyList.get(v2).push(v1);
  }
  
  // BFS 广度优先搜索
  bfs(start) {
    const visited = new Set();
    const queue = [start];
    const result = [];
    
    visited.add(start);
    
    while (queue.length) {
      const vertex = queue.shift();
      result.push(vertex);
      
      for (const neighbor of this.adjacencyList.get(vertex)) {
        if (!visited.has(neighbor)) {
          visited.add(neighbor);
          queue.push(neighbor);
        }
      }
    }
    return result;
  }
}`
    };

    return templates[templateId] || null;
  }

  /**
   * 绑定日志清空
   */
  private bindLogClear(): void {
    const clearBtn = document.getElementById('clear-log');
    const logList = document.getElementById('log-list');
    
    if (clearBtn && logList) {
      clearBtn.addEventListener('click', () => {
        logList.innerHTML = '<div class="log-item info">日志已清空</div>';
      });
    }
  }

  // ========== 新增算法步骤生成方法 ==========

  /**
   * 生成数独求解步骤
   */
  private generateSudokuSteps(): any[] {
    const steps: any[] = [];
    const cells = [5, 3, 0, 0, 7, 0, 0, 0, 0];
    const solution = [5, 3, 4, 6, 7, 8, 9, 1, 2];
    
    for (let i = 0; i < cells.length; i++) {
      if (cells[i] === 0) {
        steps.push({
          type: 'highlight',
          indices: [i],
          description: `尝试填写空格 [${i}]`,
          variables: { '位置': i, '尝试值': solution[i] }
        });
        steps.push({
          type: 'mark',
          indices: [i],
          description: `填入 ${solution[i]}`,
          variables: { '确定值': solution[i] }
        });
      }
    }
    steps.push({ type: 'mark', indices: [0, 1, 2, 3, 4, 5, 6, 7, 8], description: '数独求解完成!' });
    return steps;
  }

  /**
   * 生成全排列步骤
   */
  private generatePermutationSteps(arr: number[]): any[] {
    const steps: any[] = [];
    const permutations: number[][] = [];
    
    const permute = (current: number[], remaining: number[]) => {
      if (remaining.length === 0) {
        permutations.push([...current]);
        steps.push({
          type: 'mark',
          indices: current.map((_, i) => i),
          description: `找到排列: [${current.join(', ')}]`,
          variables: { '排列': current.join(',') }
        });
        return;
      }
      for (let i = 0; i < remaining.length; i++) {
        steps.push({
          type: 'highlight',
          indices: [i],
          description: `选择元素 ${remaining[i]}`,
          variables: { '当前': current.join(','), '剩余': remaining.join(',') }
        });
        permute([...current, remaining[i]], [...remaining.slice(0, i), ...remaining.slice(i + 1)]);
      }
    };
    
    permute([], arr);
    return steps;
  }

  /**
   * 生成KMP字符串匹配步骤
   */
  private generateKMPSteps(): any[] {
    const steps: any[] = [];
    const text = [1, 2, 3, 4, 5, 6, 7, 8]; // 模拟文本
    const pattern = [3, 4, 5]; // 模拟模式
    
    steps.push({
      type: 'highlight',
      indices: [0],
      description: 'KMP字符串匹配: 在文本中查找模式 [3,4,5]',
      variables: { '文本长度': text.length, '模式长度': pattern.length }
    });
    
    for (let i = 0; i <= text.length - pattern.length; i++) {
      let match = true;
      for (let j = 0; j < pattern.length; j++) {
        // compare必须传入两个索引才能触发动画
        const idx1 = i + j;
        const idx2 = Math.min(i + j + 1, text.length - 1);
        steps.push({
          type: 'compare',
          indices: [idx1, idx2],
          description: `比较位置 ${i + j}: 文本[${text[i + j]}] vs 模式[${pattern[j]}]`,
          variables: { '文本位置': i + j, '模式位置': j, '文本字符': text[i + j], '模式字符': pattern[j] }
        });
        if (text[i + j] !== pattern[j]) {
          match = false;
          steps.push({ 
            type: 'highlight', 
            indices: [i + j], 
            description: `位置 ${i + j} 不匹配, 移动窗口`,
            variables: { '结果': '不匹配' }
          });
          break;
        }
        steps.push({ 
          type: 'mark', 
          indices: [i + j], 
          description: `位置 ${i + j} 匹配成功!`,
          variables: { '结果': '匹配' }
        });
      }
      if (match) {
        steps.push({
          type: 'mark',
          indices: [i, i + 1, i + 2],
          description: `找到完整匹配! 位置: ${i} - ${i + pattern.length - 1}`,
          variables: { '匹配位置': `${i}-${i + pattern.length - 1}` }
        });
        break;
      }
    }
    return steps;
  }

  /**
   * 生成Rabin-Karp步骤
   */
  private generateRabinKarpSteps(): any[] {
    const steps: any[] = [];
    const text = [1, 2, 3, 4, 5, 6, 7, 8];
    const patternHash = 3 + 4 + 5; // 模式[3,4,5]的哈希值
    
    steps.push({
      type: 'highlight',
      indices: [0],
      description: 'Rabin-Karp算法: 使用哈希值快速匹配字符串',
      variables: { '模式哈希': patternHash, '窗口大小': 3 }
    });
    
    for (let i = 0; i < text.length - 2; i++) {
      const windowHash = text[i] + text[i + 1] + text[i + 2];
      
      // 计算窗口哈希 - 使用compare触发动画
      steps.push({
        type: 'compare',
        indices: [i, i + 2],
        description: `计算窗口[${i}-${i + 2}]哈希值: ${windowHash}`,
        variables: { '窗口': `[${i}-${i + 2}]`, '窗口哈希': windowHash, '模式哈希': patternHash }
      });
      
      if (windowHash === patternHash) {
        // 哈希匹配，验证字符
        steps.push({
          type: 'swap',
          indices: [i, i + 1],
          description: `哈希匹配! 验证字符内容...`,
          variables: { '状态': '哈希匹配' }
        });
        steps.push({
          type: 'mark',
          indices: [i, i + 1, i + 2],
          description: `找到匹配! 位置: ${i}-${i + 2}`,
          variables: { '匹配位置': `${i}-${i + 2}` }
        });
      } else {
        steps.push({
          type: 'highlight',
          indices: [i, i + 1, i + 2],
          description: `哈希不匹配(${windowHash} ≠ ${patternHash}), 滑动窗口`,
          variables: { '状态': '不匹配' }
        });
      }
    }
    return steps;
  }

  /**
   * 生成编辑距离步骤
   */
  private generateEditDistanceSteps(): any[] {
    const steps: any[] = [];
    // 模拟编辑距离计算过程
    for (let i = 0; i < 5; i++) {
      steps.push({
        type: 'highlight',
        indices: [i],
        description: `计算第 ${i + 1} 个字符的编辑距离`,
        variables: { '位置': i, '当前距离': i }
      });
      steps.push({ type: 'mark', indices: [i], description: `距离: ${i}` });
    }
    return steps;
  }

  /**
   * 生成最大公约数步骤 (欧几里得算法)
   */
  private generateGCDSteps(a: number, b: number): any[] {
    const steps: any[] = [];
    let x = a, y = b;
    
    while (y !== 0) {
      steps.push({
        type: 'highlight',
        indices: [0, 1],
        description: `计算 ${x} mod ${y}`,
        variables: { a: x, b: y, '余数': x % y }
      });
      const temp = y;
      y = x % y;
      x = temp;
      steps.push({
        type: 'compare',
        indices: [0, 1],
        description: `更新: a=${x}, b=${y}`,
        variables: { a: x, b: y }
      });
    }
    steps.push({ type: 'mark', indices: [0], description: `GCD = ${x}` });
    return steps;
  }

  /**
   * 生成素数筛法步骤
   */
  private generateSieveSteps(n: number): any[] {
    const steps: any[] = [];
    const isPrime = new Array(n + 1).fill(true);
    isPrime[0] = isPrime[1] = false;
    
    for (let i = 2; i <= Math.sqrt(n); i++) {
      if (isPrime[i]) {
        steps.push({
          type: 'mark',
          indices: [i - 2],
          description: `${i} 是素数`,
          variables: { '当前素数': i }
        });
        for (let j = i * i; j <= n; j += i) {
          if (j - 2 < 9) {
            steps.push({
              type: 'highlight',
              indices: [j - 2],
              description: `标记 ${j} 为合数 (${i}的倍数)`,
              variables: { '合数': j, '因子': i }
            });
            isPrime[j] = false;
          }
        }
      }
    }
    return steps;
  }

  /**
   * 生成快速幂步骤
   */
  private generatePowerSteps(base: number, exp: number): any[] {
    const steps: any[] = [];
    let result = 1;
    let b = base;
    let e = exp;
    let idx = 0;
    
    while (e > 0) {
      if (e % 2 === 1) {
        result *= b;
        steps.push({
          type: 'highlight',
          indices: [idx % 5],
          description: `指数为奇数, 结果 *= ${b}`,
          variables: { '结果': result, '底数': b, '指数': e }
        });
      }
      e = Math.floor(e / 2);
      b *= b;
      steps.push({
        type: 'compare',
        indices: [idx % 5],
        description: `底数平方: ${b}, 指数减半: ${e}`,
        variables: { '底数': b, '指数': e }
      });
      idx++;
    }
    steps.push({ type: 'mark', indices: [0, 1, 2, 3, 4], description: `结果: ${base}^${exp} = ${result}` });
    return steps;
  }

  /**
   * 生成零钱兑换步骤
   */
  private generateCoinChangeSteps(coins: number[], amount: number): any[] {
    const steps: any[] = [];
    const dp = new Array(amount + 1).fill(Infinity);
    dp[0] = 0;
    
    for (let i = 1; i <= Math.min(amount, 10); i++) {
      for (let j = 0; j < coins.length; j++) {
        if (coins[j] <= i) {
          steps.push({
            type: 'highlight',
            indices: [j],
            description: `考虑硬币 ${coins[j]} 凑成 ${i}`,
            variables: { '金额': i, '硬币': coins[j], 'dp[i]': dp[i] }
          });
          if (dp[i - coins[j]] + 1 < dp[i]) {
            dp[i] = dp[i - coins[j]] + 1;
            steps.push({
              type: 'mark',
              indices: [j],
              description: `更新 dp[${i}] = ${dp[i]}`,
              variables: { 'dp[i]': dp[i] }
            });
          }
        }
      }
    }
    return steps;
  }

  /**
   * 生成最长递增子序列步骤
   */
  private generateLISSteps(nums: number[]): any[] {
    const steps: any[] = [];
    const dp = new Array(nums.length).fill(1);
    
    for (let i = 1; i < nums.length; i++) {
      steps.push({
        type: 'highlight',
        indices: [i],
        description: `考察元素 nums[${i}]=${nums[i]}`,
        variables: { '当前元素': nums[i], 'dp[i]': dp[i] }
      });
      
      for (let j = 0; j < i; j++) {
        if (nums[j] < nums[i]) {
          steps.push({
            type: 'compare',
            indices: [j, i],
            description: `比较 nums[${j}]=${nums[j]} < nums[${i}]=${nums[i]}`,
            variables: { 'nums[j]': nums[j], 'nums[i]': nums[i] }
          });
          if (dp[j] + 1 > dp[i]) {
            dp[i] = dp[j] + 1;
            steps.push({
              type: 'mark',
              indices: [i],
              description: `更新 dp[${i}] = ${dp[i]}`,
              variables: { 'dp[i]': dp[i] }
            });
          }
        }
      }
    }
    
    const maxLen = Math.max(...dp);
    steps.push({ type: 'mark', indices: [dp.indexOf(maxLen)], description: `LIS 长度: ${maxLen}` });
    return steps;
  }

  /**
   * 生成活动选择算法步骤
   */
  private generateActivitySelectionSteps(): any[] {
    const steps: any[] = [];
    const activities = [
      { start: 1, finish: 4 },
      { start: 3, finish: 5 },
      { start: 0, finish: 6 },
      { start: 5, finish: 7 },
      { start: 3, finish: 9 },
      { start: 5, finish: 9 }
    ];
    
    steps.push({
      type: 'highlight',
      indices: [0, 1, 2, 3, 4, 5],
      description: '待选活动列表，按结束时间排序',
      variables: { '活动数': activities.length }
    });
    
    const selected = [0];
    let lastFinish = activities[0].finish;
    
    steps.push({
      type: 'mark',
      indices: [0],
      description: `选择活动 0: [${activities[0].start}, ${activities[0].finish}]`,
      variables: { '当前结束时间': lastFinish }
    });
    
    for (let i = 1; i < activities.length; i++) {
      steps.push({
        type: 'compare',
        indices: [selected[selected.length - 1], i],
        description: `检查活动 ${i}: 开始=${activities[i].start} vs 上一结束=${lastFinish}`,
        variables: { 'start': activities[i].start, 'lastFinish': lastFinish }
      });
      
      if (activities[i].start >= lastFinish) {
        selected.push(i);
        lastFinish = activities[i].finish;
        steps.push({
          type: 'mark',
          indices: [i],
          description: `选中活动 ${i}: [${activities[i].start}, ${activities[i].finish}]`,
          variables: { '已选': selected.length, '新结束时间': lastFinish }
        });
      } else {
        steps.push({
          type: 'highlight',
          indices: [i],
          description: `跳过活动 ${i}: 与已选活动冲突`,
          variables: { '冲突': true }
        });
      }
    }
    
    steps.push({
      type: 'mark',
      indices: selected,
      description: `活动选择完成! 共选择 ${selected.length} 个活动`,
      variables: { '最大活动数': selected.length }
    });
    
    return steps;
  }

  /**
   * 生成霍夫曼编码算法步骤
   */
  private generateHuffmanSteps(freqs: number[]): any[] {
    const steps: any[] = [];
    const nodes = freqs.map((f, i) => ({ freq: f, char: String.fromCharCode(65 + i), index: i }));
    
    steps.push({
      type: 'highlight',
      indices: nodes.map((_, i) => i),
      description: `初始化节点: ${nodes.map(n => `${n.char}:${n.freq}`).join(', ')}`,
      variables: { '节点数': nodes.length }
    });
    
    const sortedNodes = [...nodes].sort((a, b) => a.freq - b.freq);
    let step = 1;
    
    while (sortedNodes.length > 1) {
      const left = sortedNodes.shift()!;
      const right = sortedNodes.shift()!;
      
      steps.push({
        type: 'compare',
        indices: [left.index, right.index],
        description: `第${step}次合并: ${left.char}(${left.freq}) + ${right.char}(${right.freq})`,
        variables: { '左频率': left.freq, '右频率': right.freq }
      });
      
      const merged = {
        freq: left.freq + right.freq,
        char: `[${left.char}${right.char}]`,
        index: left.index
      };
      
      steps.push({
        type: 'swap',
        indices: [left.index, right.index],
        description: `生成新节点: ${merged.char} 频率=${merged.freq}`,
        variables: { '新频率': merged.freq }
      });
      
      sortedNodes.push(merged);
      sortedNodes.sort((a, b) => a.freq - b.freq);
      step++;
    }
    
    steps.push({
      type: 'mark',
      indices: [0],
      description: `霍夫曼树构建完成! 根节点频率: ${sortedNodes[0].freq}`,
      variables: { '总频率': sortedNodes[0].freq }
    });
    
    return steps;
  }

  /**
   * 生成并查集算法步骤
   */
  private generateUnionFindSteps(): any[] {
    const steps: any[] = [];
    const parent = [0, 1, 2, 3, 4, 5, 6, 7];
    const rank = [0, 0, 0, 0, 0, 0, 0, 0];
    
    steps.push({
      type: 'highlight',
      indices: [0, 1, 2, 3, 4, 5, 6, 7],
      description: '初始化: 每个元素自成一个集合',
      variables: { 'parent': parent.join(',') }
    });
    
    // 模拟合并操作
    const unions = [[0, 1], [2, 3], [4, 5], [6, 7], [0, 2], [4, 6], [0, 4]];
    
    for (const [x, y] of unions) {
      steps.push({
        type: 'compare',
        indices: [x, y],
        description: `Union(${x}, ${y}): 尝试合并集合`,
        variables: { 'x': x, 'y': y }
      });
      
      // 简化的合并逻辑
      const px = parent[x];
      const py = parent[y];
      
      if (px !== py) {
        parent[py] = px;
        steps.push({
          type: 'swap',
          indices: [px, py],
          description: `合并成功: ${py} -> ${px}`,
          variables: { 'parent': parent.join(',') }
        });
      }
    }
    
    steps.push({
      type: 'mark',
      indices: [0, 1, 2, 3, 4, 5, 6, 7],
      description: '并查集操作完成! 所有元素已连通',
      variables: { '最终parent': parent.join(',') }
    });
    
    return steps;
  }

  /**
   * 生成Kruskal最小生成树算法步骤
   */
  private generateKruskalSteps(): any[] {
    const steps: any[] = [];
    const edges = [
      { u: 0, v: 1, w: 2 },
      { u: 0, v: 3, w: 6 },
      { u: 1, v: 2, w: 3 },
      { u: 1, v: 3, w: 8 },
      { u: 1, v: 4, w: 5 },
      { u: 2, v: 4, w: 7 },
      { u: 3, v: 4, w: 9 }
    ].sort((a, b) => a.w - b.w);
    
    steps.push({
      type: 'highlight',
      indices: [0, 1, 2, 3, 4],
      description: `边按权重排序: ${edges.map(e => `(${e.u}-${e.v}:${e.w})`).join(' ')}`,
      variables: { '边数': edges.length }
    });
    
    const parent = [0, 1, 2, 3, 4];
    const find = (x: number): number => parent[x] === x ? x : parent[x] = find(parent[x]);
    
    const mst: typeof edges = [];
    
    for (const edge of edges) {
      const { u, v, w } = edge;
      steps.push({
        type: 'compare',
        indices: [u, v],
        description: `检查边 (${u}, ${v}) 权重=${w}`,
        variables: { 'u': u, 'v': v, 'weight': w }
      });
      
      const pu = find(u);
      const pv = find(v);
      
      if (pu !== pv) {
        parent[pu] = pv;
        mst.push(edge);
        steps.push({
          type: 'mark',
          indices: [u, v],
          description: `添加边 (${u}, ${v}) 到MST`,
          variables: { 'MST边数': mst.length, '总权重': mst.reduce((s, e) => s + e.w, 0) }
        });
      } else {
        steps.push({
          type: 'highlight',
          indices: [u],
          description: `跳过边 (${u}, ${v}): 会形成环`,
          variables: { '跳过': true }
        });
      }
      
      if (mst.length === 4) break;
    }
    
    const totalWeight = mst.reduce((s, e) => s + e.w, 0);
    steps.push({
      type: 'mark',
      indices: [0, 1, 2, 3, 4],
      description: `Kruskal完成! MST总权重: ${totalWeight}`,
      variables: { 'MST边数': mst.length, '总权重': totalWeight }
    });
    
    return steps;
  }

  /**
   * 生成最近点对算法步骤
   */
  private generateClosestPairSteps(): any[] {
    const steps: any[] = [];
    const points = [
      [2, 3], [12, 30], [40, 50], [5, 1], [12, 10], [3, 4]
    ];
    
    steps.push({
      type: 'highlight',
      indices: [0, 1, 2, 3, 4, 5],
      description: `初始化 ${points.length} 个点`,
      variables: { '点数': points.length }
    });
    
    // 按x坐标排序
    const sorted = points.map((p, i) => ({ x: p[0], y: p[1], idx: i }))
      .sort((a, b) => a.x - b.x);
    
    steps.push({
      type: 'highlight',
      indices: sorted.map(p => p.idx),
      description: '按X坐标排序',
      variables: { '排序后': sorted.map(p => `(${p.x},${p.y})`).join(' ') }
    });
    
    // 分治演示
    const mid = Math.floor(sorted.length / 2);
    steps.push({
      type: 'compare',
      indices: [sorted[mid - 1].idx, sorted[mid].idx],
      description: `分割: 左半部分 [0, ${mid - 1}], 右半部分 [${mid}, ${sorted.length - 1}]`,
      variables: { '分割点': mid }
    });
    
    // 模拟找到最近点对
    let minDist = Infinity;
    let pair = [0, 1];
    
    for (let i = 0; i < points.length; i++) {
      for (let j = i + 1; j < points.length; j++) {
        const dist = Math.sqrt(
          Math.pow(points[i][0] - points[j][0], 2) +
          Math.pow(points[i][1] - points[j][1], 2)
        );
        
        if (i < 3 && j < 4) {
          steps.push({
            type: 'compare',
            indices: [i, j],
            description: `计算距离: 点${i} 与 点${j}`,
            variables: { '距离': dist.toFixed(2) }
          });
        }
        
        if (dist < minDist) {
          minDist = dist;
          pair = [i, j];
        }
      }
    }
    
    steps.push({
      type: 'mark',
      indices: pair,
      description: `最近点对: 点${pair[0]} 和 点${pair[1]}, 距离: ${minDist.toFixed(2)}`,
      variables: { '最小距离': minDist.toFixed(2) }
    });
    
    return steps;
  }

  // ========================================
  // 实操大厅功能
  // ========================================

  /**
   * 切换模块页面
   */
  private switchModule(module: string): void {
    this.currentModule = module;
    
    // 隐藏所有页面
    const pages = document.querySelectorAll('.page-container');
    pages.forEach(page => page.classList.remove('active'));
    
    // 显示目标页面
    const targetPage = document.getElementById(`page-${module}`);
    if (targetPage) {
      targetPage.classList.add('active');
    }
    
    // 切换到非积木模块时，隐藏舞台信息
    if (module !== 'blocks') {
      const blocksStatusEl = document.getElementById('status-blocks');
      if (blocksStatusEl) {
        blocksStatusEl.style.display = 'none';
      }
    }
    
    // 「当前: 数组」只在算法可视化模块显示
    const structureEl = document.getElementById('status-structure');
    if (structureEl) {
      structureEl.style.display = module === 'algorithms' ? 'inline' : 'none';
    }
    
    // 如果切换到实操大厅，初始化TAB指示器和筛选指示器
    if (module === 'practice') {
      setTimeout(() => {
        const activeTab = document.querySelector('.practice-tab.active') as HTMLElement;
        if (activeTab) {
          this.updateTabIndicator(activeTab);
        }
        // 初始化筛选指示器
        const filterGroups = document.querySelectorAll('.filter-group');
        filterGroups.forEach(group => {
          const groupEl = group as HTMLElement;
          const activeBtn = groupEl.querySelector('.filter-btn.active') as HTMLElement;
          if (activeBtn) {
            this.updateFilterIndicator(groupEl, activeBtn);
          }
        });
      }, 100);
    }
    
    // 如果切换到设置页面，初始化TAB指示器
    if (module === 'settings') {
      setTimeout(() => {
        settingsModule.refresh();
      }, 100);
    }
    
    // 如果切换到关于页面，初始化TAB指示器
    if (module === 'about') {
      setTimeout(() => {
        aboutModule.refresh();
      }, 100);
    }
    
    // 如果切换到使用说明页面，初始化TAB指示器
    if (module === 'guide') {
      setTimeout(() => {
        guideModule.refresh();
      }, 100);
    }
    
    // 如果切换到积木编程页面，刷新 Blockly 工作区
    if (module === 'blocks') {
      setTimeout(() => {
        blocksModule.refresh();
      }, 100);
    }
    
    console.log(`📄 切换到模块: ${module}`);
  }

  /**
   * 绑定实操大厅事件
   */
  private bindPracticeEvents(): void {
    // 动态渲染题目卡片
    this.renderProblemCards();

    // 初始CodeMirror编辑器
    this.initCodeMirror();

    // 运行代码按钮
    const runBtn = document.getElementById('btn-run-code');
    if (runBtn) {
      runBtn.addEventListener('click', () => {
        this.runCode();
      });
    }

    // 语言选择
    const langSelect = document.getElementById('code-language') as HTMLSelectElement;
    if (langSelect) {
      langSelect.addEventListener('change', () => {
        this.updateCodeTemplate(langSelect.value);
        // 更新CodeMirror语言模式
        this.updateCodeMirrorMode(langSelect.value);
      });
    }

    // 题目卡片点击 - 跳转到做题页面
    const problemCards = document.querySelectorAll('.problem-card');
    problemCards.forEach(card => {
      card.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const problemId = target.dataset.problem;
        const difficulty = target.dataset.difficulty;
        const title = target.querySelector('.card-title')?.textContent || '';
        const cardId = target.querySelector('.card-id')?.textContent || '';
        
        if (problemId) {
          this.openCodingPage(problemId, title, cardId, difficulty || 'easy');
        }
      });
    });

    // 返回按钮 - 返回题库首页
    const backBtn = document.getElementById('btn-back-home');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        this.showPracticeHome();
      });
    }

    // 筛选按钮
    this.initFilterIndicators(); // 初始化筛选指示器
    const filterBtns = document.querySelectorAll('.filter-btn');
    filterBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const filterGroup = target.parentElement as HTMLElement;
        
        // 更新同组筛选按钮状态
        filterGroup?.querySelectorAll('.filter-btn').forEach(b => b.classList.remove('active'));
        target.classList.add('active');
        
        // 更新指示器位置
        this.updateFilterIndicator(filterGroup, target);
        
        // 执行筛选
        this.filterProblems();
      });
    });

    // 查看答案按钮
    const answerBtn = document.getElementById('btn-show-answer');
    if (answerBtn) {
      answerBtn.addEventListener('click', () => {
        this.showAnswer();
      });
    }

    // 二级TAB切换
    const practiceTabs = document.querySelectorAll('.practice-tab');
    this.initTabIndicator(); // 初始化滑动指示器
    practiceTabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabName = target.dataset.tab;
        if (tabName) {
          this.switchPracticeTab(tabName);
        }
      });
    });

        // 初始化收藏按钮
    this.initFavoriteButtons();
    
        // 绑定小屏代码编辑器垂直TAB切换
    this.bindCodingVerticalTabs();
    
        // 初始化输出内容自动同步
    this.initOutputSync();
    
    // 监听虚拟键盘变化，修复布局问题
    this.bindViewportResize();
  }

    /**
   * 绑定小屏代码编辑器垂直TAB切换
   */
  private bindCodingVerticalTabs(): void {
    const vtabs = document.querySelectorAll('.coding-vtab');
    const views = document.querySelectorAll('.coding-tab-view');
    const editor = document.querySelector('.coding-editor');
    const tabViews = document.querySelector('.coding-tab-views');
    
    vtabs.forEach(tab => {
      tab.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const tabName = target.dataset.tab;
        
        // 更新TAB激活状态
        vtabs.forEach(t => t.classList.remove('active'));
        target.classList.add('active');
        
        // 代码TAB：显示编辑器，隐藏TAB视图
        // 其他TAB：隐藏编辑器，显示TAB视图
        if (tabName === 'code') {
          editor?.classList.remove('hidden');
          tabViews?.classList.add('hidden');
        } else {
          editor?.classList.add('hidden');
          tabViews?.classList.remove('hidden');
          
          // 切换内容视图
          views.forEach(view => {
            const viewElement = view as HTMLElement;
            if (viewElement.dataset.view === tabName) {
              viewElement.classList.add('active');
            } else {
              viewElement.classList.remove('active');
            }
          });
        }
      });
    });
  }
  
  /**
   * 切换到小屏指定的TAB视图
   */
  private switchCodingTab(tabName: string): void {
    const vtabs = document.querySelectorAll('.coding-vtab');
    const views = document.querySelectorAll('.coding-tab-view');
    const editor = document.querySelector('.coding-editor');
    const tabViews = document.querySelector('.coding-tab-views');
    
    // 更新TAB激活状态
    vtabs.forEach(tab => {
      const tabEl = tab as HTMLElement;
      if (tabEl.dataset.tab === tabName) {
        tabEl.classList.add('active');
      } else {
        tabEl.classList.remove('active');
      }
    });
    
    // 代码TAB：显示编辑器，隐藏TAB视图
    if (tabName === 'code') {
      editor?.classList.remove('hidden');
      tabViews?.classList.add('hidden');
    } else {
      editor?.classList.add('hidden');
      tabViews?.classList.remove('hidden');
      
      views.forEach(view => {
        const viewEl = view as HTMLElement;
                if (viewEl.dataset.view === tabName) {
          viewEl.classList.add('active');
        } else {
          viewEl.classList.remove('active');
        }
      });
        }
  }
  
  /**
   * 初始化小屏垂直TAB状态（默认显示代码TAB）
   */
  private initCodingTabState(): void {
    const vtabs = document.querySelectorAll('.coding-vtab');
    const views = document.querySelectorAll('.coding-tab-view');
    const editor = document.querySelector('.coding-editor');
    const tabViews = document.querySelector('.coding-tab-views');
    
    // 设置代码TAB为激活状态
    vtabs.forEach(tab => {
      const tabEl = tab as HTMLElement;
      if (tabEl.dataset.tab === 'code') {
        tabEl.classList.add('active');
      } else {
        tabEl.classList.remove('active');
      }
    });
    
    // 显示编辑器，隐藏TAB视图
    editor?.classList.remove('hidden');
    tabViews?.classList.add('hidden');
    
    // 重置所有视图状态
    views.forEach(view => {
      const viewEl = view as HTMLElement;
      if (viewEl.dataset.view === 'code') {
        viewEl.classList.add('active');
      } else {
        viewEl.classList.remove('active');
      }
    });
  }
  
    /**
   * 同步运行结果到小屏视图
   */
  private syncOutputToSmallScreen(): void {
    const outputEl = document.getElementById('code-output');
    const outputSmEl = document.getElementById('code-output-sm');
    if (outputEl && outputSmEl) {
      outputSmEl.innerHTML = outputEl.innerHTML;
    }
  }
  
  /**
   * 初始化输出内容自动同步（使用MutationObserver）
   */
  private initOutputSync(): void {
    const outputEl = document.getElementById('code-output');
    const outputSmEl = document.getElementById('code-output-sm');
    
    if (outputEl && outputSmEl) {
      const observer = new MutationObserver(() => {
        outputSmEl.innerHTML = outputEl.innerHTML;
      });
      
      observer.observe(outputEl, {
        childList: true,
        subtree: true,
        characterData: true
      });
        }
  }

  /**
   * 监听视口变化（虚拟键盘弹出/收起），修复布局问题
   */
  private bindViewportResize(): void {
    // 使用 visualViewport API 监听键盘变化
    if (window.visualViewport) {
      let lastHeight = window.visualViewport.height;
      
      window.visualViewport.addEventListener('resize', () => {
        const currentHeight = window.visualViewport!.height;
        
        // 键盘收起时（高度增大）刷新布局
        if (currentHeight > lastHeight) {
          // 延迟执行确保键盘完全收起
          setTimeout(() => {
            // 刷新CodeMirror编辑器
            if (this.codeMirrorEditor) {
              this.codeMirrorEditor.refresh();
            }
            
            // 重置滚动位置，修复标题栏偏移问题
            this.resetCodingLayoutScroll();
          }, 100);
        }
        
        lastHeight = currentHeight;
      });
    }
  }
  
  /**
   * 重置做题页面布局滚动位置
   */
  private resetCodingLayoutScroll(): void {
    // 重置做题页面容器的滚动位置
    const codingLayout = document.querySelector('.coding-layout');
    const codingView = document.getElementById('practice-coding');
    const codingMain = document.querySelector('.coding-main');
    const codingEditor = document.querySelector('.coding-editor');
    
    // 重置所有可能的滚动容器
    [codingLayout, codingView, codingMain, codingEditor].forEach(el => {
      if (el) {
        (el as HTMLElement).scrollTop = 0;
      }
    });
    
    // 刷新页面布局
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
  }

  /**
   * 动态渲染题目卡片列表
   */
  private renderProblemCards(): void {
    const container = document.getElementById('problem-cards');
    if (!container) return;

    // 清空现有内容
    container.innerHTML = '';

    // 分类名称映射
    const categoryNames: Record<string, string> = {
      'array': '数组',
      'string': '字符串',
      'linked-list': '链表',
      'tree': '树',
      'dp': '动态规划'
    };

    // 难度名称映射
    const difficultyNames: Record<string, string> = {
      'easy': '简单',
      'medium': '中等',
      'hard': '困难'
    };

    // 获取所有题目 ID
    const problemIds = Object.keys(PROBLEM_INFO);

    // 生成卡片 HTML
    problemIds.forEach((id, index) => {
      const info = PROBLEM_INFO[id];
      if (!info) return;

      const difficultyText = difficultyNames[info.difficulty] || info.difficulty;
      // 从数据读取，提供默认值
      const tags = info.tags || [categoryNames[info.category] || info.category];
      const acceptance = info.acceptanceRate || '50.0%';

      const cardEl = document.createElement('div');
      cardEl.className = 'problem-card';
      cardEl.dataset.problem = id;
      cardEl.dataset.difficulty = info.difficulty;
      cardEl.dataset.category = info.category;

      cardEl.innerHTML = `
        <div class="card-header">
          <span class="card-id">#${index + 1}</span>
          <div class="card-header-right">
            <span class="card-difficulty ${info.difficulty}">${difficultyText}</span>
            <button class="favorite-btn" data-problem="${id}" title="收藏">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </button>
          </div>
        </div>
        <h3 class="card-title">${info.name}</h3>
        <p class="card-desc">${this.getShortDescription(id, info.description)}</p>
        <div class="card-tags">
          ${tags.map(tag => `<span class="card-tag">${tag}</span>`).join('')}
        </div>
        <div class="card-footer">
          <span class="card-acceptance">通过率 ${acceptance}</span>
          <button class="card-btn">开始挑战</button>
        </div>
      `;

      container.appendChild(cardEl);
    });

    console.log(`📝 已动态渲染 ${problemIds.length} 个题目卡片`);
  }

  /**
   * 获取简短描述（去除HTML标签）
   */
  private getShortDescription(id: string, description?: string): string {
    // 预定义的简短描述
    const shortDescs: Record<string, string> = {
      'two-sum': '给定一个整数数组和一个目标值，找出数组中和为目标值的两个数。',
      'reverse-string': '编写一个函数，其作用是将输入的字符串反转过来。',
      'palindrome': '判断一个整数是否是回文数。',
      'valid-parentheses': '给定一个只包括括号的字符串，判断字符串是否有效。',
      'merge-sorted': '将两个升序排列的数组合并为一个新的升序数组。',
      'reverse-linked-list': '给你单链表的头节点，请你反转链表。',
      'max-subarray': '找出一个具有最大和的连续子数组，返回其最大和。',
      'climbing-stairs': '每次可以爬 1 或 2 个台阶，有多少种不同的方法可以爬到楼顶。',
      'binary-search': '给定一个有序数组和目标值，使用二分查找找到目标值的索引。',
      'invert-tree': '给你一棵二叉树，翻转这棵二叉树。',
      'longest-substring': '找出不含有重复字符的最长子串的长度。',
      'lru-cache': '设计并实现一个满足 LRU 缓存约束的数据结构。',
      'move-zeroes': '编写一个函数将数组中所有 0 移动到末尾，保持非零元素相对顺序。',
      'valid-anagram': '给定两个字符串，判断它们是否互为字母异位词。',
      'middle-of-linked-list': '返回链表的中间节点，如果有两个则返回第二个。',
      'max-depth-binary-tree': '给定一个二叉树，找出其最大深度。',
      'best-time-to-buy-stock': '给定股票价格数组，计算买卖一次能获取的最大利润。',
      'search-rotated-array': '在旋转过的有序数组中搜索目标值。',
      'three-sum': '找出数组中所有和为 0 的三元组。',
      'level-order-traversal': '返回二叉树节点值的层序遍历结果。',
      'single-number': '找出数组中只出现一次的元素，其他元素都出现两次。',
      'remove-duplicates': '原地删除有序数组中的重复项，返回新长度。',
      'contains-duplicate': '判断数组中是否存在重复元素。',
      'product-except-self': '返回数组中除自身以外其余各元素的乘积。',
      'min-stack': '设计一个支持常数时间获取最小元素的栈。',
      'coin-change': '计算凑成目标金额所需的最少硬币个数。',
      'longest-palindromic': '找到字符串中最长的回文子串。',
      'merge-two-lists': '将两个升序链表合并为一个新的升序链表。',
      'symmetric-tree': '检查二叉树是否轴对称。',
      'path-sum': '判断是否存在根到叶子的路径和等于目标值。'
    };

    if (shortDescs[id]) {
      return shortDescs[id];
    }

    // 如果没有预定义，从 description 提取并去除 HTML
    if (description) {
      return description.replace(/<[^>]*>/g, '').slice(0, 50) + '...';
    }

    return '';
  }

    /**
   * 打开做题页面
   */
  private openCodingPage(problemId: string, title: string, cardId: string, difficulty: string): void {
    // 记录当前激活的TAB，以便返回
    const activeTab = document.querySelector('.practice-tab.active') as HTMLElement;
    this.previousTab = activeTab?.dataset.tab || 'home';
    
    // 隐藏所有视图，显示做题页面
    document.querySelectorAll('.practice-view').forEach(v => v.classList.remove('active'));
    const codingView = document.getElementById('practice-coding');
    const tabsEl = document.querySelector('.practice-tabs') as HTMLElement;
    
    if (codingView) codingView.classList.add('active');
    if (tabsEl) tabsEl.style.display = 'none';  // 隐藏二级TAB
    
    // 初始化小屏垂直TAB状态：默认显示代码TAB
    this.initCodingTabState();
    
    // 页面显示后刷新CodeMirror并应用设置（多次刷新确保布局正确）
    const refreshEditor = () => {
      if (this.codeMirrorEditor) {
        this.applyEditorSettings();
        this.codeMirrorEditor.refresh();
      }
    };
    
    // 分多个时间点刷新，确保布局完全正确
    setTimeout(refreshEditor, 50);
    setTimeout(refreshEditor, 150);
    setTimeout(refreshEditor, 300);
    
    // 使用requestAnimationFrame确保DOM已更新
    requestAnimationFrame(() => {
      requestAnimationFrame(refreshEditor);
    });
    
    // 更新题目信息
    const nameEl = document.getElementById('current-problem-name');
    const diffEl = document.getElementById('current-problem-difficulty');
    const numEl = codingView?.querySelector('.problem-number');
    
    if (nameEl) nameEl.textContent = title;
    if (numEl) numEl.textContent = cardId;
    if (diffEl) {
      diffEl.textContent = difficulty === 'easy' ? '简单' : difficulty === 'medium' ? '中等' : '困难';
      diffEl.className = `problem-difficulty-badge ${difficulty}`;
    }
    
    // 加载题目内容
    this.loadProblem(problemId);
    this.currentProblemId = problemId;  // 保存当前题目ID
    
    this.app.log(`开始挑战: ${title}`, 'info');
    console.log(`📝 打开题目: ${problemId}`);
  }

  /**
   * 返回上一个页面（题库首页或我的记录）
   */
  private showPracticeHome(): void {
    const codingView = document.getElementById('practice-coding');
    const tabsEl = document.querySelector('.practice-tabs') as HTMLElement;
    
    if (codingView) codingView.classList.remove('active');
    
    // 如果是刷题模式，退出刷题并返回刷题首页
    if (this.quizSessionActive) {
      this.quizSessionActive = false;
      this.hideQuizControlBar();
      
      // 隐藏小屏进度TAB按钮
      const progressTab = document.getElementById('coding-vtab-progress');
      if (progressTab) progressTab.style.display = 'none';
    }
    
    // 重置编辑器状态
    this.resetCodingEditor();
    
    if (tabsEl) tabsEl.style.display = 'flex';
    
    // 根据进入前的TAB返回对应页面
    const targetViewId = this.previousTab === 'history' ? 'practice-history' : 
                         this.previousTab === 'favorites' ? 'practice-favorites' : 
                         this.previousTab === 'quiz' ? 'practice-quiz' : 'practice-home';
    const targetView = document.getElementById(targetViewId);
    if (targetView) targetView.classList.add('active');
    
    // 更新TAB状态
    document.querySelectorAll('.practice-tab').forEach(t => t.classList.remove('active'));
    const tabToActivate = this.previousTab === 'quiz' ? 'quiz' : this.previousTab;
    const activeTabEl = document.querySelector(`.practice-tab[data-tab="${tabToActivate}"]`);
    activeTabEl?.classList.add('active');
    
    // 更新滑动指示器
    setTimeout(() => {
      this.updateTabIndicator(activeTabEl as HTMLElement);
    }, 50);
    
    // 如果返回我的记录，刷新数据
    if (this.previousTab === 'history') {
      this.updateHistoryPage();
    }
    // 如果返回我的收藏，刷新数据
    if (this.previousTab === 'favorites') {
      this.updateFavoritesPage();
    }
    // 如果返回刷题页，刷新数据
    if (this.previousTab === 'quiz') {
      this.updateQuizPage();
    }
    
    console.log(`🏠 返回: ${this.previousTab}`);
  }

  /**
   * 重置代码编辑器状态
   */
  private resetCodingEditor(): void {
    // 清空当前题目ID
    this.currentProblemId = '';
    
    // 清空运行结果
    const outputEl = document.getElementById('code-output');
    if (outputEl) {
      outputEl.innerHTML = '<div class="output-placeholder">点击"运行代码"查看执行结果</div>';
    }
    
    // 重置题目描述
    const contentEl = document.getElementById('problem-content');
    if (contentEl) {
      contentEl.innerHTML = `
        <div class="problem-description">
          <p>选择一道题目开始挑战</p>
        </div>
      `;
    }
    
    // 重置题目标题
    const nameEl = document.getElementById('current-problem-name');
    const diffEl = document.getElementById('current-problem-difficulty');
    const numEl = document.querySelector('.problem-number');
    if (nameEl) nameEl.textContent = '';
    if (numEl) numEl.textContent = '';
    if (diffEl) {
      diffEl.textContent = '';
      diffEl.className = 'problem-difficulty-badge';
    }
    
    // 清空编辑器代码
    this.setEditorCode('// 选择一道题目开始编写代码\n');
    
    console.log('🗑️ 编辑器已重置');
  }

  /**
   * 当前收藏筛选条件
   */
  private favFilter: string = 'all';
  private favSort: string = 'time-desc';

  /**
   * 更新我的收藏页数据
   */
  private updateFavoritesPage(): void {
    const allFavorites = this.getFavoritedProblems();
    const favoritesData = this.getFavoritesData();
    
    // 统计各难度数量
    let easyCount = 0, mediumCount = 0, hardCount = 0;
    allFavorites.forEach(id => {
      const info = PROBLEM_INFO[id];
      if (info?.difficulty === 'easy') easyCount++;
      else if (info?.difficulty === 'medium') mediumCount++;
      else if (info?.difficulty === 'hard') hardCount++;
    });

    // 更新统计卡片
    const totalEl = document.getElementById('favorites-total');
    const easyEl = document.getElementById('fav-easy-count');
    const mediumEl = document.getElementById('fav-medium-count');
    const hardEl = document.getElementById('fav-hard-count');
    const statsEl = document.getElementById('favorites-stats');
    const filtersEl = document.getElementById('favorites-filters');
    
    if (totalEl) totalEl.textContent = allFavorites.length.toString();
    if (easyEl) easyEl.textContent = easyCount.toString();
    if (mediumEl) mediumEl.textContent = mediumCount.toString();
    if (hardEl) hardEl.textContent = hardCount.toString();

    // 无收藏时隐藏统计和筛选
    if (statsEl) statsEl.style.display = allFavorites.length > 0 ? 'grid' : 'none';
    if (filtersEl) filtersEl.style.display = allFavorites.length > 0 ? 'flex' : 'none';

    // 筛选
    let favorites = allFavorites.filter(id => {
      if (this.favFilter === 'all') return true;
      const info = PROBLEM_INFO[id];
      return info?.difficulty === this.favFilter;
    });

    // 排序
    if (this.favSort === 'time-asc') {
      favorites = favorites.reverse();
    } else if (this.favSort === 'difficulty') {
      const order = { 'easy': 1, 'medium': 2, 'hard': 3 };
      favorites.sort((a, b) => {
        const da = PROBLEM_INFO[a]?.difficulty || 'easy';
        const db = PROBLEM_INFO[b]?.difficulty || 'easy';
        return order[da] - order[db];
      });
    }

    // 更新收藏列表
    const listEl = document.getElementById('favorites-list');
    const emptyEl = document.getElementById('favorites-empty');

    if (!listEl) return;

    // 清空旧内容
    const oldItems = listEl.querySelectorAll('.timeline-group, .favorite-item');
    oldItems.forEach(item => item.remove());

    if (allFavorites.length === 0) {
      if (emptyEl) emptyEl.style.display = 'flex';
      return;
    }
    if (emptyEl) emptyEl.style.display = 'none';

    // 按时间分组
    const now = Date.now();
    const dayMs = 24 * 60 * 60 * 1000;
    const groups: { [key: string]: { title: string, items: string[] } } = {
      today: { title: '今天', items: [] },
      yesterday: { title: '昨天', items: [] },
      thisWeek: { title: '本周', items: [] },
      earlier: { title: '更早', items: [] }
    };

    favorites.forEach(id => {
      const timestamp = favoritesData[id]?.timestamp || now;
      const diff = now - timestamp;
      
      if (diff < dayMs) {
        groups.today.items.push(id);
      } else if (diff < 2 * dayMs) {
        groups.yesterday.items.push(id);
      } else if (diff < 7 * dayMs) {
        groups.thisWeek.items.push(id);
      } else {
        groups.earlier.items.push(id);
      }
    });

    // 渲染时间轴分组
    Object.entries(groups).forEach(([key, group]) => {
      if (group.items.length === 0) return;

      const groupEl = document.createElement('div');
      groupEl.className = 'timeline-group';
      
      // 时间轴标题
      groupEl.innerHTML = `
        <div class="timeline-header">
          <div class="timeline-dot"></div>
          <span class="timeline-title">${group.title}</span>
          <span class="timeline-count">${group.items.length} 题</span>
        </div>
        <div class="favorites-grid"></div>
      `;

      const gridEl = groupEl.querySelector('.favorites-grid')!;

      group.items.forEach(problemId => {
        const info = PROBLEM_INFO[problemId];
        if (!info) return;

        const difficultyText = info.difficulty === 'easy' ? '简单' : 
                               info.difficulty === 'medium' ? '中等' : '困难';
        const timestamp = favoritesData[problemId]?.timestamp;
        const timeStr = timestamp ? this.formatFavTime(timestamp) : '';

        const itemEl = document.createElement('div');
        itemEl.className = 'favorite-item';
        itemEl.dataset.problem = problemId;
        itemEl.innerHTML = `
          <div class="favorite-item-left">
            <div class="favorite-star">
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
              </svg>
            </div>
            <div class="favorite-info">
              <h4>${info.name}</h4>
              <span class="favorite-time">${timeStr}</span>
            </div>
          </div>
          <div class="favorite-item-right">
            <span class="favorite-badge ${info.difficulty}">${difficultyText}</span>
            <button class="unfavorite-btn" data-problem="${problemId}" title="取消收藏">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
            <svg class="goto-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
          </div>
        `;

        // 点击跳转到题目
        itemEl.addEventListener('click', (e) => {
          if ((e.target as HTMLElement).closest('.unfavorite-btn')) return;
          const cardIndex = Object.keys(PROBLEM_INFO).indexOf(problemId) + 1;
          this.openCodingPage(problemId, info.name, `#${cardIndex}`, info.difficulty);
        });

        // 取消收藏按钮
        const unfavoriteBtn = itemEl.querySelector('.unfavorite-btn');
        if (unfavoriteBtn) {
          unfavoriteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleFavorite(problemId);
            this.updateFavoritesPage();
            const card = document.querySelector(`.problem-card[data-problem="${problemId}"]`);
            if (card) {
              card.classList.remove('favorited');
              const btn = card.querySelector('.favorite-btn');
              if (btn) btn.classList.remove('active');
            }
          });
        }

        gridEl.appendChild(itemEl);
      });

      listEl.appendChild(groupEl);
    });

    // 绑定筛选和排序事件
    this.bindFavoritesFilters();
  }

  /**
   * 格式化收藏时间
   */
  private formatFavTime(timestamp: number): string {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes} 分钟前`;
    if (hours < 24) return `${hours} 小时前`;
    if (days < 7) return `${days} 天前`;
    
    const date = new Date(timestamp);
    return `${date.getMonth() + 1}月${date.getDate()}日`;
  }

  /**
   * 绑定收藏页筛选和排序事件
   */
  private bindFavoritesFilters(): void {
    // 筛选按钮
    const filterBtns = document.querySelectorAll('.fav-filter-btn');
    filterBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        filterBtns.forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        this.favFilter = (btn as HTMLElement).dataset.filter || 'all';
        this.updateFavoritesPage();
      });
    });

    // 排序选择
    const sortSelect = document.getElementById('fav-sort') as HTMLSelectElement;
    if (sortSelect) {
      sortSelect.value = this.favSort;
      sortSelect.addEventListener('change', () => {
        this.favSort = sortSelect.value;
        this.updateFavoritesPage();
      });
    }
  }

  /**
   * 切换实操大厅二级TAB
   */
  private switchPracticeTab(tabName: string): void {
    // 更新TAB按钮状态
    document.querySelectorAll('.practice-tab').forEach(t => t.classList.remove('active'));
    const activeTab = document.querySelector(`.practice-tab[data-tab="${tabName}"]`);
    activeTab?.classList.add('active');
    
    // 更新滑动指示器位置
    this.updateTabIndicator(activeTab as HTMLElement);
    
    // 隐藏所有视图
    document.querySelectorAll('.practice-view').forEach(v => v.classList.remove('active'));
    
    // 显示对应视图
    const viewId = tabName === 'home' ? 'practice-home' : 
                   tabName === 'favorites' ? 'practice-favorites' :
                   tabName === 'history' ? 'practice-history' : 
                   tabName === 'quiz' ? 'practice-quiz' : 
                   tabName === 'analysis' ? 'practice-analysis' : 'practice-home';
    document.getElementById(viewId)?.classList.add('active');
    
    // 如果切换到分析页，刷新数据
    if (tabName === 'analysis') {
      analysisModule.refresh();
    }
    
    // 如果切换到我的收藏页，刷新数据
    if (tabName === 'favorites') {
      this.updateFavoritesPage();
    }
    
    // 如果切换到我的记录页，刷新数据
    if (tabName === 'history') {
      this.updateHistoryPage();
    }
    
    // 如果切换到刷题页，刷新数据
    if (tabName === 'quiz') {
      this.updateQuizPage();
    }
    
    console.log(`📝 切换到: ${tabName}`);
  }

  /**
   * 初始化TAB滑动指示器
   */
  private initTabIndicator(): void {
    const tabsContainer = document.querySelector('.practice-tabs');
    if (!tabsContainer) return;
    
    // 创建指示器元素
    let indicator = tabsContainer.querySelector('.tab-indicator') as HTMLElement;
    if (!indicator) {
      indicator = document.createElement('div');
      indicator.className = 'tab-indicator';
      tabsContainer.insertBefore(indicator, tabsContainer.firstChild);
    }
    
    // 多次尝试初始化位置，确保DOM已完全渲染
    const initPosition = () => {
      const activeTab = tabsContainer.querySelector('.practice-tab.active') as HTMLElement;
      if (activeTab && activeTab.getBoundingClientRect().width > 0) {
        this.updateTabIndicator(activeTab);
      }
    };
    
    // 立即尝试
    initPosition();
    // 延迟再次尝试
    setTimeout(initPosition, 100);
    setTimeout(initPosition, 300);
  }

  /**
   * 更新TAB滑动指示器位置
   */
  private updateTabIndicator(activeTab: HTMLElement | null): void {
    if (!activeTab) return;
    
    const indicator = document.querySelector('.tab-indicator') as HTMLElement;
    if (!indicator) return;
    
    const tabsContainer = document.querySelector('.practice-tabs') as HTMLElement;
    if (!tabsContainer) return;
    
    // 计算位置（相对于容器）
    const containerRect = tabsContainer.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    
    const left = tabRect.left - containerRect.left;
    const width = tabRect.width;
    
    indicator.style.left = `${left}px`;
    indicator.style.width = `${width}px`;
  }

  /**
   * 初始化筛选指示器
   */
  private initFilterIndicators(): void {
    const filterGroups = document.querySelectorAll('.filter-group');
    filterGroups.forEach(group => {
      const groupEl = group as HTMLElement;
      
      // 创建指示器元素
      let indicator = groupEl.querySelector('.filter-indicator') as HTMLElement;
      if (!indicator) {
        indicator = document.createElement('div');
        indicator.className = 'filter-indicator';
        groupEl.insertBefore(indicator, groupEl.firstChild);
      }
      
      // 初始化位置
      setTimeout(() => {
        const activeBtn = groupEl.querySelector('.filter-btn.active') as HTMLElement;
        if (activeBtn && activeBtn.getBoundingClientRect().width > 0) {
          this.updateFilterIndicator(groupEl, activeBtn);
        }
      }, 100);
    });
  }

  /**
   * 更新筛选指示器位置
   */
  private updateFilterIndicator(filterGroup: HTMLElement, activeBtn: HTMLElement): void {
    if (!filterGroup || !activeBtn) return;
    
    const indicator = filterGroup.querySelector('.filter-indicator') as HTMLElement;
    if (!indicator) return;
    
    // 计算位置（相对于容器）
    const groupRect = filterGroup.getBoundingClientRect();
    const btnRect = activeBtn.getBoundingClientRect();
    
    const left = btnRect.left - groupRect.left;
    const width = btnRect.width;
    
    indicator.style.left = `${left}px`;
    indicator.style.width = `${width}px`;
  }

  /**
   * 初始化分析模块
   */
  private initAnalysisModule(): void {
    // 将题目信息传递给分析模块
    analysisModule.init(PROBLEM_INFO);
    console.log('📊 分析模块已初始化');
  }

  /**
   * 初始化设置模块
   */
  private initSettingsModule(): void {
    settingsModule.init();
    // 应用渲染器设置
    settingsModule.applyRendererSettings();
    console.log('⚙️ 设置模块已初始化');
    
    // 初始化积木编程模块
    blocksModule.init();
    console.log('🧱 积木编程模块已初始化');
    
    // 初始化关于模块
    aboutModule.init();
    console.log('ℹ️ 关于模块已初始化');
    
    // 初始化使用说明模块
    guideModule.init();
    console.log('❓ 使用说明模块已初始化');
    
    // 设置积木器设置变更回调，实时同步到积木模块
    settingsModule.setBlocksSettingsChangeCallback((settings) => {
      blocksModule.updateSettings(settings);
    });
  }

  /**
   * 更新我的记录页数据
   */
  private updateHistoryPage(): void {
    const completed = this.getCompletedProblems();
    const total = Object.keys(PROBLEM_INFO).length;
    const completedCount = completed.length;
    const rate = total > 0 ? Math.round((completedCount / total) * 100) : 0;

    // 更新统计数据
    const completedEl = document.getElementById('history-completed');
    const rateEl = document.getElementById('history-rate');
    const progressTextEl = document.getElementById('progress-text');
    const progressFillEl = document.getElementById('progress-fill');

    if (completedEl) completedEl.textContent = completedCount.toString();
    if (rateEl) rateEl.textContent = `${rate}%`;
    if (progressTextEl) progressTextEl.textContent = `${completedCount} / ${total}`;
    if (progressFillEl) progressFillEl.style.width = `${rate}%`;

    // 更新已完成列表
    const listEl = document.getElementById('completed-list');
    const emptyEl = document.getElementById('empty-state');

    if (!listEl) return;

    // 清空旧内容（保留空状态）
    const items = listEl.querySelectorAll('.completed-item');
    items.forEach(item => item.remove());

    if (completedCount === 0) {
      if (emptyEl) emptyEl.style.display = 'flex';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';

    // 生成已完成题目列表
    completed.forEach(problemId => {
      const info = PROBLEM_INFO[problemId];
      if (!info) return;

      const difficultyText = info.difficulty === 'easy' ? '简单' : 
                             info.difficulty === 'medium' ? '中等' : '困难';

      const itemEl = document.createElement('div');
      itemEl.className = 'completed-item';
      itemEl.dataset.problem = problemId;
      itemEl.innerHTML = `
        <div class="completed-item-left">
          <div class="completed-check">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
          <div class="completed-info">
            <h4>${info.name}</h4>
            <span>已完成</span>
          </div>
        </div>
        <div class="completed-item-right">
          <svg class="goto-icon" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
          </svg>
        </div>
      `;

      // 点击跳转到题目
      itemEl.addEventListener('click', () => {
        const cardIndex = Object.keys(PROBLEM_INFO).indexOf(problemId) + 1;
        this.openCodingPage(problemId, info.name, `#${cardIndex}`, info.difficulty);
      });

      listEl.appendChild(itemEl);
    });
  }

  // ========================================
  // 刷题模块相关方法
  // ========================================

  private quizMode: string = 'random';           // 当前刷题模式
  private quizModeLabel: string = '';             // 刷题模式标签
  private quizQueue: string[] = [];              // 刷题队列
  private quizCurrentIndex: number = 0;          // 当前题目索引
  private quizProblemResults: Map<string, boolean> = new Map();  // 每道题的最终结果 (true=正确, false=错误)
  private quizCurrentProblemResult: boolean | null = null;       // 当前题目的临时运行结果（未保存）
  private quizSessionActive: boolean = false;    // 是否在刷题中
  private quizAutoNext: boolean = false;         // 答对自动跳转下一题（默认关闭）
  private quizTimerInterval: any = null;         // 限时挑战计时器
  private quizTimeRemaining: number = 0;         // 剩余时间(秒)
  private quizEventsBound: boolean = false;      // 刷题事件是否已绑定
  private quizAnsweredProblems: Set<string> = new Set();  // 已作答的题目
  private customSelectedProblems: Set<string> = new Set();  // 编排刷题已选题目
  private customFilterDifficulty: string = 'all';           // 编排刷题筛选难度
  private customSearchKeyword: string = '';                 // 编排刷题搜索关键词

  /**
   * 更新刷题页面数据
   */
  private updateQuizPage(): void {
    // 统计今日刷题数据
    const todayStats = this.getQuizTodayStats();
    
    // 更新统计卡片
    const todayCountEl = document.getElementById('quiz-today-count');
    const correctRateEl = document.getElementById('quiz-correct-rate');
    const streakEl = document.getElementById('quiz-streak');
    
    if (todayCountEl) todayCountEl.textContent = todayStats.count.toString();
    if (correctRateEl) correctRateEl.textContent = `${todayStats.rate}%`;
    if (streakEl) streakEl.textContent = todayStats.streak.toString();

    // 更新专项练习页面的题目数量
    const problems = Object.keys(PROBLEM_INFO);
    let easyCount = 0, mediumCount = 0, hardCount = 0;
    const categoryCount: { [key: string]: number } = {
      'array': 0, 'string': 0, 'linked-list': 0, 'tree': 0, 'dp': 0
    };

    problems.forEach(id => {
      const info = PROBLEM_INFO[id];
      if (info.difficulty === 'easy') easyCount++;
      else if (info.difficulty === 'medium') mediumCount++;
      else if (info.difficulty === 'hard') hardCount++;
      
      if (categoryCount[info.category] !== undefined) {
        categoryCount[info.category]++;
      }
    });

    // 更新难度统计
    const catEasyEl = document.getElementById('cat-easy-count');
    const catMediumEl = document.getElementById('cat-medium-count');
    const catHardEl = document.getElementById('cat-hard-count');
    if (catEasyEl) catEasyEl.textContent = `${easyCount} 题`;
    if (catMediumEl) catMediumEl.textContent = `${mediumCount} 题`;
    if (catHardEl) catHardEl.textContent = `${hardCount} 题`;

    // 更新分类统计
    const catArrayEl = document.getElementById('cat-array-count');
    const catStringEl = document.getElementById('cat-string-count');
    const catLinkedListEl = document.getElementById('cat-linkedlist-count');
    const catTreeEl = document.getElementById('cat-tree-count');
    const catDpEl = document.getElementById('cat-dp-count');
    if (catArrayEl) catArrayEl.textContent = `${categoryCount['array']} 题`;
    if (catStringEl) catStringEl.textContent = `${categoryCount['string']} 题`;
    if (catLinkedListEl) catLinkedListEl.textContent = `${categoryCount['linked-list']} 题`;
    if (catTreeEl) catTreeEl.textContent = `${categoryCount['tree']} 题`;
    if (catDpEl) catDpEl.textContent = `${categoryCount['dp']} 题`;

    // 绑定刷题模式卡片事件
    this.bindQuizModeEvents();
  }

  /**
   * 获取今日刷题统计
   */
  private getQuizTodayStats(): { count: number; rate: number; streak: number } {
    try {
      const today = new Date().toDateString();
      const data = localStorage.getItem('quiz_stats');
      if (data) {
        const stats = JSON.parse(data);
        if (stats.date === today) {
          const total = stats.correct + stats.wrong;
          const rate = total > 0 ? Math.round((stats.correct / total) * 100) : 0;
          return { count: total, rate, streak: stats.streak || 0 };
        }
      }
      return { count: 0, rate: 0, streak: 0 };
    } catch {
      return { count: 0, rate: 0, streak: 0 };
    }
  }

  /**
   * 保存今日刷题统计
   */
  private saveQuizStats(correct: boolean): void {
    try {
      const today = new Date().toDateString();
      let stats = { date: today, correct: 0, wrong: 0, streak: 0 };
      
      const data = localStorage.getItem('quiz_stats');
      if (data) {
        const saved = JSON.parse(data);
        if (saved.date === today) {
          stats = saved;
        }
      }

      if (correct) {
        stats.correct++;
        stats.streak++;
      } else {
        stats.wrong++;
        stats.streak = 0;
      }

      localStorage.setItem('quiz_stats', JSON.stringify(stats));
    } catch (e) {
      console.error('保存刷题统计失败', e);
    }
  }

  /**
   * 保存本轮刷题统计（提交时调用）
   */
  private saveQuizSessionStats(): void {
    try {
      const today = new Date().toDateString();
      let stats = { date: today, correct: 0, wrong: 0, streak: 0 };
      
      const data = localStorage.getItem('quiz_stats');
      if (data) {
        const saved = JSON.parse(data);
        if (saved.date === today) {
          stats = saved;
        }
      }

      // 基于每道题的最终结果计算统计数据
      const { correctCount, wrongCount } = this.getQuizResultStats();
      
      // 累加本轮session的数据
      stats.correct += correctCount;
      stats.wrong += wrongCount;
      
      // 连续正确数更新：如果本轮有错题则重置，否则累加
      if (wrongCount > 0) {
        stats.streak = 0;
      } else {
        stats.streak += correctCount;
      }

      localStorage.setItem('quiz_stats', JSON.stringify(stats));
      console.log('✅ 本轮刷题统计已保存:', stats);
    } catch (e) {
      console.error('保存刷题统计失败', e);
    }
  }

  /**
   * 获取本轮刷题结果统计（基于每道题的最终状态）
   */
  private getQuizResultStats(): { correctCount: number; wrongCount: number } {
    let correctCount = 0;
    let wrongCount = 0;
    
    this.quizProblemResults.forEach((isCorrect) => {
      if (isCorrect) {
        correctCount++;
      } else {
        wrongCount++;
      }
    });
    
    return { correctCount, wrongCount };
  }

  /**
   * 记录当前题目的运行结果（临时，未保存）
   */
  private recordQuizProblemResult(problemId: string, isCorrect: boolean): void {
    // 只更新临时结果，不立即保存到Map
    this.quizCurrentProblemResult = isCorrect;
    console.log(`📝 题目 ${problemId} 本次运行结果: ${isCorrect ? '正确' : '错误'}（未保存）, quizCurrentProblemResult已设置为: ${this.quizCurrentProblemResult}`);
  }

  /**
   * 保存当前题目的最终结果（切换题目或提交时调用）
   */
  private saveCurrentProblemResult(): void {
    const problemId = this.quizQueue[this.quizCurrentIndex];
    if (!problemId) return;
    
    // 只有当有运行结果时才保存
    if (this.quizCurrentProblemResult !== null) {
      this.quizProblemResults.set(problemId, this.quizCurrentProblemResult);
      console.log(`✅ 题目 ${problemId} 结果已保存: ${this.quizCurrentProblemResult ? '正确' : '错误'}`);
      
      // 处理错题本
      if (this.quizCurrentProblemResult) {
        this.removeWrongProblem(problemId);
      } else {
        this.addWrongProblem(problemId);
      }
    }
  }

  /**
   * 绑定刷题模式卡片事件
   */
  private bindQuizModeEvents(): void {
    // 防止重复绑定
    if (this.quizEventsBound) return;
    this.quizEventsBound = true;

    // 模式卡片点击
    document.querySelectorAll('.quiz-mode-card').forEach(card => {
      card.addEventListener('click', () => {
        const mode = (card as HTMLElement).dataset.mode;
        if (mode) this.startQuizMode(mode);
      });
    });

    // 专项练习返回按钮
    const categoryBackBtn = document.getElementById('quiz-category-back');
    if (categoryBackBtn) {
      categoryBackBtn.addEventListener('click', () => this.showQuizHome());
    }

    // 专项练习分类卡片
    document.querySelectorAll('.quiz-category-card').forEach(card => {
      card.addEventListener('click', () => {
        const category = (card as HTMLElement).dataset.category;
        if (category) this.startCategoryQuiz(category);
      });
    });

    // 刷题进行中的按钮
    const exitBtn = document.getElementById('quiz-exit');
    const skipBtn = document.getElementById('quiz-skip');
    const solveBtn = document.getElementById('quiz-solve');
    const favoriteBtn = document.getElementById('quiz-favorite');

    if (exitBtn) exitBtn.addEventListener('click', () => this.exitQuizSession());
    if (skipBtn) skipBtn.addEventListener('click', () => this.skipQuizQuestion());
    if (solveBtn) solveBtn.addEventListener('click', () => this.goSolveCurrentQuestion());
    if (favoriteBtn) favoriteBtn.addEventListener('click', () => this.toggleQuizFavorite());

    // 刷题控制栏按钮（编辑器内）
    const quizPrevBtn = document.getElementById('quiz-prev-btn');
    const quizNextBtn = document.getElementById('quiz-next-btn');
    const quizFavBtn = document.getElementById('quiz-fav-btn');
    const quizSaveBtn = document.getElementById('quiz-save-btn');
    const quizSkipBtn = document.getElementById('quiz-skip-btn');
    const quizFinishBtn = document.getElementById('quiz-finish-btn');
    const quizAutoNextToggle = document.getElementById('quiz-auto-next-toggle');

    if (quizPrevBtn) quizPrevBtn.addEventListener('click', () => this.quizPrevProblem());
    if (quizNextBtn) quizNextBtn.addEventListener('click', () => this.quizNextProblem());
    if (quizFavBtn) quizFavBtn.addEventListener('click', () => this.quizToggleFavorite());
    if (quizSaveBtn) quizSaveBtn.addEventListener('click', () => this.quizSaveAnswer());
    if (quizSkipBtn) quizSkipBtn.addEventListener('click', () => this.quizSkipProblem());
    if (quizFinishBtn) quizFinishBtn.addEventListener('click', () => this.confirmQuizSubmit());
    if (quizAutoNextToggle) quizAutoNextToggle.addEventListener('click', () => this.toggleQuizAutoNext());

    // 编排刷题页面事件
    const customBackBtn = document.getElementById('quiz-custom-back');
    const customClearBtn = document.getElementById('quiz-custom-clear');
    const customStartBtn = document.getElementById('quiz-custom-start');
    
    if (customBackBtn) customBackBtn.addEventListener('click', () => this.showQuizHome());
    if (customClearBtn) customClearBtn.addEventListener('click', () => this.clearCustomSelection());
    if (customStartBtn) customStartBtn.addEventListener('click', () => this.startCustomQuiz());

    // 编排刷题搜索框
    const customSearchInput = document.getElementById('quiz-custom-search-input') as HTMLInputElement;
    if (customSearchInput) {
      customSearchInput.addEventListener('input', (e) => {
        this.customSearchKeyword = (e.target as HTMLInputElement).value.trim();
        this.renderCustomQuizList();
      });
    }

    // 编排刷题筛选按钮
    document.querySelectorAll('.quiz-filter-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const difficulty = (btn as HTMLElement).dataset.difficulty;
        if (difficulty) {
          this.customFilterDifficulty = difficulty;
          // 更新按钮状态
          document.querySelectorAll('.quiz-filter-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          this.renderCustomQuizList();
        }
      });
    });

    // 全选按钮
    const selectAllBtn = document.getElementById('quiz-custom-select-all');
    if (selectAllBtn) {
      selectAllBtn.addEventListener('click', () => this.toggleSelectAllCustom());
    }

    // 已选题目按钮
    const selectedBtn = document.getElementById('quiz-custom-selected-btn');
    if (selectedBtn) {
      selectedBtn.addEventListener('click', () => this.showSelectedPopup());
    }

    // 已选题目弹出层关闭按钮
    const selectedPopupClose = document.getElementById('quiz-selected-popup-close');
    if (selectedPopupClose) {
      selectedPopupClose.addEventListener('click', () => this.hideSelectedPopup());
    }

    // 小屏右侧功能区事件绑定
    this.bindSidebarEvents();
  }

  /**
   * 绑定小屏右侧功能区事件
   */
  private bindSidebarEvents(): void {
    // 搜索按钮
    const searchToggle = document.getElementById('sidebar-search-toggle');
    const searchOverlay = document.getElementById('quiz-search-overlay');
    const searchClose = document.getElementById('search-overlay-close');
    const sidebarSearchInput = document.getElementById('sidebar-search-input') as HTMLInputElement;

    if (searchToggle && searchOverlay) {
      searchToggle.addEventListener('click', () => {
        searchOverlay.classList.toggle('show');
        searchToggle.classList.toggle('active');
        if (searchOverlay.classList.contains('show') && sidebarSearchInput) {
          sidebarSearchInput.focus();
        }
      });
    }

    if (searchClose && searchOverlay && searchToggle) {
      searchClose.addEventListener('click', () => {
        searchOverlay.classList.remove('show');
        searchToggle.classList.remove('active');
      });
    }

    // 小屏搜索输入
    if (sidebarSearchInput) {
      sidebarSearchInput.addEventListener('input', (e) => {
        this.customSearchKeyword = (e.target as HTMLInputElement).value.trim();
        this.renderCustomQuizList();
        // 同步到大屏搜索框
        const mainSearchInput = document.getElementById('quiz-custom-search-input') as HTMLInputElement;
        if (mainSearchInput) mainSearchInput.value = this.customSearchKeyword;
      });
    }

    // 小屏筛选按钮
    document.querySelectorAll('.sidebar-filter-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const difficulty = (btn as HTMLElement).dataset.difficulty;
        if (difficulty) {
          this.customFilterDifficulty = difficulty;
          // 更新小屏按钮状态
          document.querySelectorAll('.sidebar-filter-btn').forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          // 同步更新大屏按钮状态
          document.querySelectorAll('.quiz-filter-btn').forEach(b => {
            b.classList.remove('active');
            if ((b as HTMLElement).dataset.difficulty === difficulty) {
              b.classList.add('active');
            }
          });
          this.renderCustomQuizList();
        }
      });
    });

    // 小屏全选按钮
    const sidebarSelectAll = document.getElementById('sidebar-select-all');
    if (sidebarSelectAll) {
      sidebarSelectAll.addEventListener('click', () => this.toggleSelectAllCustom());
    }

    // 小屏清空按钮
    const sidebarClear = document.getElementById('sidebar-clear');
    if (sidebarClear) {
      sidebarClear.addEventListener('click', () => this.clearCustomSelection());
    }

    // 小屏开始刷题按钮
    const sidebarStart = document.getElementById('sidebar-start');
    if (sidebarStart) {
      sidebarStart.addEventListener('click', () => this.startCustomQuiz());
    }

    // 小屏返回按钮
    const sidebarBack = document.getElementById('sidebar-back');
    if (sidebarBack) {
      sidebarBack.addEventListener('click', () => {
        // 返回刷题模式选择页
        document.querySelectorAll('.practice-view').forEach(v => v.classList.remove('active'));
        document.getElementById('practice-quiz')?.classList.add('active');
      });
    }

    // 小屏已选题目按钮
    const sidebarSelectedBtn = document.getElementById('sidebar-selected-btn');
    if (sidebarSelectedBtn) {
      sidebarSelectedBtn.addEventListener('click', () => this.showSelectedPopup());
    }
  }

  /**
   * 开始刷题模式
   */
  private startQuizMode(mode: string): void {
    this.quizMode = mode;
    
    if (mode === 'category') {
      // 显示专项练习选择页面
      document.querySelectorAll('.practice-view').forEach(v => v.classList.remove('active'));
      document.getElementById('practice-quiz-category')?.classList.add('active');
      return;
    }

    if (mode === 'custom') {
      // 显示编排刷题选题页面
      this.showCustomQuizPage();
      return;
    }

    if (mode === 'wrong') {
      // 错题回顾模式
      const wrongProblems = this.getWrongProblems();
      if (wrongProblems.length === 0) {
        this.showAlertModal('恭喜你', '暂无错题记录，继续加油！');
        return;
      }
      this.quizQueue = wrongProblems;
    } else if (mode === 'sequential') {
      // 顺序模式
      this.quizQueue = Object.keys(PROBLEM_INFO);
    } else if (mode === 'timed') {
      // 限时挑战模式 - 随机5题，5分钟
      this.quizQueue = this.shuffleArray([...Object.keys(PROBLEM_INFO)]).slice(0, 5);
    } else {
      // 随机模式
      this.quizQueue = this.shuffleArray([...Object.keys(PROBLEM_INFO)]);
    }

    const label = mode === 'random' ? '随机刷题' : 
                  mode === 'sequential' ? '顺序刷题' : 
                  mode === 'timed' ? '限时挑战' : 
                  mode === 'wrong' ? '错题回顾' : '刷题';
    
    this.startQuizSession(label, mode === 'timed');
  }

  /**
   * 开始专项练习
   */
  private startCategoryQuiz(category: string): void {
    let filtered: string[] = [];
    
    if (['easy', 'medium', 'hard'].includes(category)) {
      // 按难度筛选
      filtered = Object.keys(PROBLEM_INFO).filter(id => 
        PROBLEM_INFO[id].difficulty === category
      );
    } else {
      // 按分类筛选
      filtered = Object.keys(PROBLEM_INFO).filter(id => 
        PROBLEM_INFO[id].category === category
      );
    }

    if (filtered.length === 0) {
      this.app.log('该分类暂无题目', 'warning');
      return;
    }

    this.quizQueue = this.shuffleArray(filtered);
    const label = category === 'easy' ? '简单' : 
                  category === 'medium' ? '中等' : 
                  category === 'hard' ? '困难' : category;
    this.startQuizSession(`专项: ${label}`);
  }

  /**
   * 显示编排刷题选题页面
   */
  private showCustomQuizPage(): void {
    // 清空之前的选择
    this.customSelectedProblems.clear();
    
    // 重置筛选和搜索状态
    this.customFilterDifficulty = 'all';
    this.customSearchKeyword = '';
    
    // 重置大屏搜索输入框
    const searchInput = document.getElementById('quiz-custom-search-input') as HTMLInputElement;
    if (searchInput) searchInput.value = '';
    
    // 重置小屏搜索输入框
    const sidebarSearchInput = document.getElementById('sidebar-search-input') as HTMLInputElement;
    if (sidebarSearchInput) sidebarSearchInput.value = '';
    
    // 关闭小屏搜索弹出层
    const searchOverlay = document.getElementById('quiz-search-overlay');
    const searchToggle = document.getElementById('sidebar-search-toggle');
    if (searchOverlay) searchOverlay.classList.remove('show');
    if (searchToggle) searchToggle.classList.remove('active');
    
    // 重置大屏筛选按钮状态
    document.querySelectorAll('.quiz-filter-btn').forEach(btn => {
      btn.classList.remove('active');
      if ((btn as HTMLElement).dataset.difficulty === 'all') {
        btn.classList.add('active');
      }
    });
    
    // 重置小屏筛选按钮状态
    document.querySelectorAll('.sidebar-filter-btn').forEach(btn => {
      btn.classList.remove('active');
      if ((btn as HTMLElement).dataset.difficulty === 'all') {
        btn.classList.add('active');
      }
    });
    
    // 显示选题页面
    document.querySelectorAll('.practice-view').forEach(v => v.classList.remove('active'));
    document.getElementById('practice-quiz-custom')?.classList.add('active');
    
    // 渲染题目列表
    this.renderCustomQuizList();
    
    // 更新已选数量
    this.updateCustomSelectedCount();
  }

  /**
   * 渲染编排刷题题目列表
   */
  private renderCustomQuizList(): void {
    const listEl = document.getElementById('quiz-custom-list');
    if (!listEl) return;
    
    const allProblems = Object.keys(PROBLEM_INFO);
    const difficultyText: { [key: string]: string } = {
      'easy': '简单',
      'medium': '中等',
      'hard': '困难'
    };
    
    // 筛选题目
    const filteredProblems = allProblems.filter(id => {
      const info = PROBLEM_INFO[id];
      
      // 难度筛选
      if (this.customFilterDifficulty !== 'all' && info.difficulty !== this.customFilterDifficulty) {
        return false;
      }
      
      // 搜索筛选
      if (this.customSearchKeyword && !info.name.toLowerCase().includes(this.customSearchKeyword.toLowerCase())) {
        return false;
      }
      
      return true;
    });
    
    // 如果没有匹配的题目
    if (filteredProblems.length === 0) {
      listEl.innerHTML = `
        <div class="quiz-custom-empty">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor" style="opacity: 0.3">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
          </svg>
          <p>没有找到匹配的题目</p>
        </div>
      `;
      return;
    }
    
    listEl.innerHTML = filteredProblems.map((id) => {
      const info = PROBLEM_INFO[id];
      const originalIndex = allProblems.indexOf(id) + 1;
      const isSelected = this.customSelectedProblems.has(id);
      return `
        <div class="quiz-custom-item ${isSelected ? 'selected' : ''}" data-problem-id="${id}">
          <div class="quiz-custom-info">
            <span class="quiz-custom-id">#${originalIndex}</span>
            <span class="quiz-custom-name">${info.name}</span>
            <span class="quiz-custom-difficulty ${info.difficulty}">${difficultyText[info.difficulty] || info.difficulty}</span>
          </div>
          <div class="quiz-custom-checkbox">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor">
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
            </svg>
          </div>
        </div>
      `;
    }).join('');
    
    // 绑定点击事件
    listEl.querySelectorAll('.quiz-custom-item').forEach(item => {
      item.addEventListener('click', () => {
        const problemId = (item as HTMLElement).dataset.problemId;
        if (problemId) this.toggleCustomQuizItem(problemId, item as HTMLElement);
      });
    });
    
    // 更新全选按钮状态
    this.updateSelectAllButtonState();
  }

  /**
   * 切换编排刷题题目选中状态
   */
  private toggleCustomQuizItem(problemId: string, itemEl: HTMLElement): void {
    if (this.customSelectedProblems.has(problemId)) {
      this.customSelectedProblems.delete(problemId);
      itemEl.classList.remove('selected');
    } else {
      this.customSelectedProblems.add(problemId);
      itemEl.classList.add('selected');
    }
    
    this.updateCustomSelectedCount();
    
    // 刷新已选题目弹出层（如果已打开）
    const popup = document.getElementById('quiz-selected-popup');
    if (popup?.classList.contains('show')) {
      this.renderSelectedList();
    }
    
    // 更新全选按钮状态
    this.updateSelectAllButtonState();
  }

  /**
   * 更新编排刷题已选数量
   */
  private updateCustomSelectedCount(): void {
    const countEl = document.getElementById('custom-selected-count');
    const startBtn = document.getElementById('quiz-custom-start') as HTMLButtonElement;
    const sidebarStartBtn = document.getElementById('sidebar-start') as HTMLButtonElement;
    const sidebarSelectedCount = document.getElementById('sidebar-selected-count');
    
    const count = this.customSelectedProblems.size;
    if (countEl) countEl.textContent = count.toString();
    if (sidebarSelectedCount) sidebarSelectedCount.textContent = count.toString();
    if (startBtn) startBtn.disabled = count === 0;
    if (sidebarStartBtn) sidebarStartBtn.disabled = count === 0;
  }

  /**
   * 全选/取消全选当前可见题目
   */
  private toggleSelectAllCustom(): void {
    const allProblems = Object.keys(PROBLEM_INFO);
    
    // 获取当前筛选后的题目
    const filteredProblems = allProblems.filter(id => {
      const info = PROBLEM_INFO[id];
      if (this.customFilterDifficulty !== 'all' && info.difficulty !== this.customFilterDifficulty) {
        return false;
      }
      if (this.customSearchKeyword && !info.name.toLowerCase().includes(this.customSearchKeyword.toLowerCase())) {
        return false;
      }
      return true;
    });
    
    // 判断当前可见题目是否全部已选中
    const allSelected = filteredProblems.every(id => this.customSelectedProblems.has(id));
    
    if (allSelected) {
      // 取消选中所有可见题目
      filteredProblems.forEach(id => this.customSelectedProblems.delete(id));
    } else {
      // 选中所有可见题目
      filteredProblems.forEach(id => this.customSelectedProblems.add(id));
    }
    
    // 重新渲染列表
    this.renderCustomQuizList();
    this.updateCustomSelectedCount();
    
    // 刷新已选题目弹出层（如果已打开）
    const popup = document.getElementById('quiz-selected-popup');
    if (popup?.classList.contains('show')) {
      this.renderSelectedList();
    }
  }

  /**
   * 更新全选按钮状态
   */
  private updateSelectAllButtonState(): void {
    const selectAllBtn = document.getElementById('quiz-custom-select-all');
    if (!selectAllBtn) return;
    
    const allProblems = Object.keys(PROBLEM_INFO);
    const filteredProblems = allProblems.filter(id => {
      const info = PROBLEM_INFO[id];
      if (this.customFilterDifficulty !== 'all' && info.difficulty !== this.customFilterDifficulty) {
        return false;
      }
      if (this.customSearchKeyword && !info.name.toLowerCase().includes(this.customSearchKeyword.toLowerCase())) {
        return false;
      }
      return true;
    });
    
    const allSelected = filteredProblems.length > 0 && filteredProblems.every(id => this.customSelectedProblems.has(id));
    selectAllBtn.textContent = allSelected ? '取消全选' : '全选';
  }

  /**
   * 显示已选题目弹出层
   */
  private showSelectedPopup(): void {
    const popup = document.getElementById('quiz-selected-popup');
    if (!popup) return;
    
    // 渲染已选题目列表
    this.renderSelectedList();
    
    // 显示弹出层
    popup.classList.add('show');
  }

  /**
   * 隐藏已选题目弹出层
   */
  private hideSelectedPopup(): void {
    const popup = document.getElementById('quiz-selected-popup');
    if (popup) popup.classList.remove('show');
  }

  /**
   * 渲染已选题目列表
   */
  private renderSelectedList(): void {
    const listEl = document.getElementById('quiz-selected-popup-list');
    if (!listEl) return;
    
    const allProblems = Object.keys(PROBLEM_INFO);
    const selectedArray = Array.from(this.customSelectedProblems);
    
    if (selectedArray.length === 0) {
      listEl.innerHTML = `
        <div class="quiz-selected-empty">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="currentColor" style="opacity: 0.3">
            <path d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2z"/>
          </svg>
          <p>还没有选择题目</p>
        </div>
      `;
      return;
    }
    
    listEl.innerHTML = selectedArray.map(id => {
      const info = PROBLEM_INFO[id];
      const originalIndex = allProblems.indexOf(id) + 1;
      return `
        <div class="quiz-selected-item" data-problem-id="${id}">
          <div class="quiz-selected-item-info">
            <span class="quiz-selected-item-id">#${originalIndex}</span>
            <span class="quiz-selected-item-name">${info.name}</span>
          </div>
          <button class="quiz-selected-item-remove" data-problem-id="${id}">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
      `;
    }).join('');
    
    // 绑定移除按钮事件
    listEl.querySelectorAll('.quiz-selected-item-remove').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const problemId = (btn as HTMLElement).dataset.problemId;
        if (problemId) this.removeSelectedProblem(problemId);
      });
    });
  }

  /**
   * 从已选列表移除题目
   */
  private removeSelectedProblem(problemId: string): void {
    this.customSelectedProblems.delete(problemId);
    
    // 更新主列表中的选中状态
    const mainItem = document.querySelector(`.quiz-custom-item[data-problem-id="${problemId}"]`);
    if (mainItem) mainItem.classList.remove('selected');
    
    // 更新已选数量
    this.updateCustomSelectedCount();
    
    // 重新渲染弹出层列表
    this.renderSelectedList();
    
    // 更新全选按钮状态
    this.updateSelectAllButtonState();
  }

  /**
   * 清空编排刷题选择
   */
  private clearCustomSelection(): void {
    this.customSelectedProblems.clear();
    
    // 移除所有选中样式
    document.querySelectorAll('.quiz-custom-item.selected').forEach(item => {
      item.classList.remove('selected');
    });
    
    this.updateCustomSelectedCount();
    
    // 刷新已选题目弹出层（如果已打开）
    const popup = document.getElementById('quiz-selected-popup');
    if (popup?.classList.contains('show')) {
      this.renderSelectedList();
    }
    
    // 更新全选按钮状态
    this.updateSelectAllButtonState();
  }

  /**
   * 开始编排刷题
   */
  private startCustomQuiz(): void {
    if (this.customSelectedProblems.size === 0) {
      this.showAlertModal('提示', '请先选择题目');
      return;
    }
    
    // 将选中的题目转换为刷题队列
    this.quizQueue = Array.from(this.customSelectedProblems);
    
    // 开始刷题会话
    this.startQuizSession('编排刷题');
  }

  /**
   * 开始刷题会话 - 直接进入编辑器
   */
  private startQuizSession(modeLabel: string, isTimed: boolean = false): void {
    this.quizCurrentIndex = 0;
    this.quizProblemResults.clear();  // 清空每道题的结果记录
    this.quizAnsweredProblems.clear();  // 清空已作答记录
    this.quizSessionActive = true;
    this.quizModeLabel = modeLabel;
    
    // 显示小屏进度TAB按钮
    const progressTab = document.getElementById('coding-vtab-progress');
    if (progressTab) progressTab.style.display = 'flex';

    // 限时挑战模式 - 启动计时器
    if (isTimed) {
      this.startQuizTimer(5 * 60); // 5分钟
    }

    // 直接加载第一题并进入编辑器
    this.loadQuizProblemToEditor();
  }

  /**
   * 加载刷题题目到编辑器
   */
  private loadQuizProblemToEditor(): void {
    if (this.quizCurrentIndex >= this.quizQueue.length) {
      // 已超出范围，回到最后一题
      this.quizCurrentIndex = this.quizQueue.length - 1;
    }

    const problemId = this.quizQueue[this.quizCurrentIndex];
    const info = PROBLEM_INFO[problemId];
    if (!info) return;

    // 重置当前题目的临时运行结果
    this.quizCurrentProblemResult = null;

    const cardIndex = Object.keys(PROBLEM_INFO).indexOf(problemId) + 1;
    
    // 设置为刷题模式
    this.previousTab = 'quiz';
    
    // 清除运行结果面板
    this.clearCodeOutput();
    
    // 打开编辑器页面
    this.openCodingPage(problemId, info.name, `#${cardIndex}`, info.difficulty);
    
    // 显示刷题控制栏
    this.showQuizControlBar();
    
    // 更新刷题控制栏信息
    this.updateQuizControlBar();
  }

  /**
   * 清除运行结果面板
   */
  private clearCodeOutput(): void {
    const outputEl = document.getElementById('code-output');
    if (outputEl) {
      outputEl.innerHTML = '<div class="output-placeholder">点击"运行代码"查看执行结果</div>';
    }
  }

  /**
   * 显示刷题控制栏
   */
  private showQuizControlBar(): void {
    const controlBar = document.getElementById('quiz-control-bar');
    const backBtnText = document.getElementById('back-btn-text');
    const progressPanel = document.getElementById('quiz-progress-panel');
    
    if (controlBar) controlBar.style.display = 'flex';
    if (backBtnText) backBtnText.textContent = '退出刷题';
    if (progressPanel) progressPanel.style.display = 'block';
    
    // 渲染答题情况面板
    this.renderQuizProgressPanel();
  }

  /**
   * 隐藏刷题控制栏
   */
  private hideQuizControlBar(): void {
    const controlBar = document.getElementById('quiz-control-bar');
    const backBtnText = document.getElementById('back-btn-text');
    const progressPanel = document.getElementById('quiz-progress-panel');
    
    if (controlBar) controlBar.style.display = 'none';
    if (backBtnText) backBtnText.textContent = '返回题库';
    if (progressPanel) progressPanel.style.display = 'none';
  }

  /**
   * 渲染答题情况面板
   */
  private renderQuizProgressPanel(): void {
    const gridEl = document.getElementById('quiz-progress-grid');
    const savedCountEl = document.getElementById('quiz-saved-count');
    const totalEl = document.getElementById('quiz-progress-total');
    
    // 小屏元素
    const gridSmEl = document.getElementById('quiz-progress-grid-sm');
    const savedCountSmEl = document.getElementById('quiz-saved-count-sm');
    const totalSmEl = document.getElementById('quiz-total-sm');
    
    // 更新统计
    const savedCount = this.quizProblemResults.size;
    if (savedCountEl) savedCountEl.textContent = savedCount.toString();
    if (totalEl) totalEl.textContent = this.quizQueue.length.toString();
    
    // 更新小屏统计
    if (savedCountSmEl) savedCountSmEl.textContent = savedCount.toString();
    if (totalSmEl) totalSmEl.textContent = this.quizQueue.length.toString();
    
    // 生成题目方块HTML
    const progressHtml = this.quizQueue.map((problemId, index) => {
      let statusClass = 'unsaved';
      
      if (this.quizProblemResults.has(problemId)) {
        statusClass = this.quizProblemResults.get(problemId) ? 'saved-correct' : 'saved-wrong';
      }
      
      if (index === this.quizCurrentIndex) {
        statusClass += ' current';
      }
      
      return `<div class="quiz-progress-item progress-item ${statusClass}" data-index="${index}">${index + 1}</div>`;
    }).join('');
    
    // 更新大屏进度面板
    if (gridEl) {
      gridEl.innerHTML = progressHtml;
      this.bindProgressItemClicks(gridEl);
    }
    
    // 更新小屏进度面板
    if (gridSmEl) {
      gridSmEl.innerHTML = progressHtml;
      this.bindProgressItemClicks(gridSmEl);
    }
  }
  
  /**
   * 绑定进度方块点击事件
   */
  private bindProgressItemClicks(container: HTMLElement): void {
    container.querySelectorAll('.quiz-progress-item').forEach(item => {
      item.addEventListener('click', () => {
        const index = parseInt((item as HTMLElement).dataset.index || '0');
        this.jumpToQuizProblem(index);
      });
    });
  }

  /**
   * 跳转到指定题目
   */
  private jumpToQuizProblem(index: number): void {
    if (index >= 0 && index < this.quizQueue.length && index !== this.quizCurrentIndex) {
      this.confirmSwitchProblem(() => {
        this.quizCurrentIndex = index;
        this.loadQuizProblemToEditor();
      });
    }
  }

  /**
   * 确认切换题目（有未保存的运行结果时提示）
   */
  private confirmSwitchProblem(onConfirm: () => void): void {
    // 如果有未保存的运行结果，弹出确认
    if (this.quizCurrentProblemResult !== null) {
      this.showConfirmModal(
        '当前题目有未保存的运行结果，确定要切换吗？',
        () => {
          // 放弃未保存的结果
          this.quizCurrentProblemResult = null;
          onConfirm();
        }
      );
    } else {
      // 没有未保存的结果，直接切换
      onConfirm();
    }
  }

  /**
   * 更新刷题控制栏信息
   */
  private updateQuizControlBar(): void {
    const currentIdxEl = document.getElementById('quiz-current-idx');
    const totalCountEl = document.getElementById('quiz-total-count');
    const prevBtn = document.getElementById('quiz-prev-btn') as HTMLButtonElement;
    const nextBtn = document.getElementById('quiz-next-btn') as HTMLButtonElement;
    const favBtn = document.getElementById('quiz-fav-btn');
    
    if (currentIdxEl) currentIdxEl.textContent = (this.quizCurrentIndex + 1).toString();
    if (totalCountEl) totalCountEl.textContent = this.quizQueue.length.toString();
    
    // 禁用上一题/下一题按钮
    if (prevBtn) prevBtn.disabled = this.quizCurrentIndex <= 0;
    if (nextBtn) nextBtn.disabled = this.quizCurrentIndex >= this.quizQueue.length - 1;
    
    // 更新收藏状态
    if (favBtn) {
      const problemId = this.quizQueue[this.quizCurrentIndex];
      const isFav = this.getFavoritedProblems().includes(problemId);
      favBtn.classList.toggle('favorited', isFav);
    }
  }

  /**
   * 刷题模式 - 上一题
   */
  private quizPrevProblem(): void {
    if (this.quizCurrentIndex > 0) {
      this.confirmSwitchProblem(() => {
        this.quizCurrentIndex--;
        this.loadQuizProblemToEditor();
      });
    }
  }

  /**
   * 刷题模式 - 下一题
   */
  private quizNextProblem(): void {
    if (this.quizCurrentIndex < this.quizQueue.length - 1) {
      this.confirmSwitchProblem(() => {
        this.quizCurrentIndex++;
        this.loadQuizProblemToEditor();
      });
    } else {
      // 已经是最后一题，提示用户
      this.app.log('已经是最后一题了', 'info');
    }
  }

  /**
   * 刷题模式 - 保存答案
   */
  private quizSaveAnswer(): void {
    console.log('💾 保存答案检查:', {
      quizSessionActive: this.quizSessionActive,
      quizCurrentProblemResult: this.quizCurrentProblemResult,
      currentProblemId: this.currentProblemId
    });
    
    // 必须先运行过代码才能保存
    if (this.quizCurrentProblemResult === null) {
      this.showToast('请先运行代码获取结果', 'warning');
      return;
    }
    
    // 保存当前题目的结果
    this.saveCurrentProblemResult();
    this.app.log('答案已保存', 'success');
    
    // 更新答题情况面板
    this.renderQuizProgressPanel();
    
    // 重置临时结果，避免重复保存
    this.quizCurrentProblemResult = null;
    
    // 如果自动跳转开启且不是最后一题，跳转下一题
    if (this.quizAutoNext && this.quizCurrentIndex < this.quizQueue.length - 1) {
      setTimeout(() => {
        this.quizCurrentIndex++;
        this.loadQuizProblemToEditor();
      }, 800);
    }
  }

  /**
   * 刷题模式 - 跳过当前题
   */
  private quizSkipProblem(): void {
    if (this.quizCurrentIndex < this.quizQueue.length - 1) {
      this.confirmSwitchProblem(() => {
        this.quizCurrentIndex++;
        this.loadQuizProblemToEditor();
      });
    } else {
      // 已经是最后一题，提示用户
      this.app.log('已经是最后一题了，无法跳过', 'warning');
    }
  }

  /**
   * 刷题模式 - 切换收藏
   */
  private quizToggleFavorite(): void {
    const problemId = this.quizQueue[this.quizCurrentIndex];
    if (!problemId) return;
    
    this.toggleFavorite(problemId);
    this.updateQuizControlBar();
  }

  /**
   * 刷题模式 - 切换自动跳转开关
   */
  private toggleQuizAutoNext(): void {
    this.quizAutoNext = !this.quizAutoNext;
    
    const toggleEl = document.getElementById('quiz-auto-next-toggle');
    const switchEl = toggleEl?.querySelector('.toggle-switch');
    
    if (switchEl) {
      switchEl.classList.toggle('active', this.quizAutoNext);
    }
    
    console.log(`🔄 自动跳转: ${this.quizAutoNext ? '开启' : '关闭'}`);
  }

  /**
   * 加载当前刷题题目
   */
  private loadQuizQuestion(): void {
    if (this.quizCurrentIndex >= this.quizQueue.length) {
      // 已超出范围，回到最后一题
      this.quizCurrentIndex = this.quizQueue.length - 1;
    }

    const problemId = this.quizQueue[this.quizCurrentIndex];
    const info = PROBLEM_INFO[problemId];
    if (!info) return;

    // 更新题号
    const currentNumEl = document.getElementById('quiz-current-num');
    if (currentNumEl) currentNumEl.textContent = (this.quizCurrentIndex + 1).toString();

    // 更新答对/答错统计
    const correctEl = document.getElementById('quiz-session-correct');
    const wrongEl = document.getElementById('quiz-session-wrong');
    const { correctCount, wrongCount } = this.getQuizResultStats();
    if (correctEl) correctEl.textContent = correctCount.toString();
    if (wrongEl) wrongEl.textContent = wrongCount.toString();

    // 更新题目信息
    const idEl = document.getElementById('quiz-question-id');
    const titleEl = document.getElementById('quiz-question-title');
    const difficultyEl = document.getElementById('quiz-question-difficulty');
    const contentEl = document.getElementById('quiz-question-content');

    const cardIndex = Object.keys(PROBLEM_INFO).indexOf(problemId) + 1;
    if (idEl) idEl.textContent = `#${cardIndex}`;
    if (titleEl) titleEl.textContent = info.name;
    if (difficultyEl) {
      difficultyEl.textContent = info.difficulty === 'easy' ? '简单' : 
                                  info.difficulty === 'medium' ? '中等' : '困难';
      difficultyEl.className = `quiz-question-difficulty ${info.difficulty}`;
    }
    if (contentEl) {
      contentEl.innerHTML = info.description || `<p>${info.name}</p>`;
    }

    // 更新收藏按钮状态
    const favoriteBtn = document.getElementById('quiz-favorite');
    if (favoriteBtn) {
      const isFav = this.getFavoritedProblems().includes(problemId);
      favoriteBtn.classList.toggle('active', isFav);
    }
  }

  /**
   * 跳过当前题目
   */
  private skipQuizQuestion(): void {
    this.quizCurrentIndex++;
    this.loadQuizQuestion();
  }

  /**
   * 去解答当前题目
   */
  private goSolveCurrentQuestion(): void {
    const problemId = this.quizQueue[this.quizCurrentIndex];
    const info = PROBLEM_INFO[problemId];
    if (!info) return;

    const cardIndex = Object.keys(PROBLEM_INFO).indexOf(problemId) + 1;
    
    // 记住当前在刷题模式
    this.previousTab = 'quiz-session';
    
    // 打开编辑器页面
    this.openCodingPage(problemId, info.name, `#${cardIndex}`, info.difficulty);
  }

  /**
   * 切换刷题中的收藏
   */
  private toggleQuizFavorite(): void {
    const problemId = this.quizQueue[this.quizCurrentIndex];
    if (!problemId) return;
    
    this.toggleFavorite(problemId);
    
    const favoriteBtn = document.getElementById('quiz-favorite');
    if (favoriteBtn) {
      const isFav = this.getFavoritedProblems().includes(problemId);
      favoriteBtn.classList.toggle('active', isFav);
    }
  }

  /**
   * 退出刷题会话
   */
  private exitQuizSession(): void {
    this.quizSessionActive = false;
    this.stopQuizTimer();
    this.showQuizHome();
  }

  /**
   * 启动限时挑战计时器
   */
  private startQuizTimer(seconds: number): void {
    // 先停止之前的计时器（如果有）
    this.stopQuizTimer();
    
    this.quizTimeRemaining = seconds;
    this.updateTimerDisplay();
    
    // 显示计时器
    const timerEl = document.getElementById('quiz-timer');
    if (timerEl) timerEl.style.display = 'flex';
    
    // 开始倒计时
    this.quizTimerInterval = setInterval(() => {
      this.quizTimeRemaining--;
      this.updateTimerDisplay();
      
      if (this.quizTimeRemaining <= 0) {
        // 先停止计时器，防止重复触发
        this.stopQuizTimer();
        this.handleTimeUp();
      }
    }, 1000);
  }

  /**
   * 停止计时器
   */
  private stopQuizTimer(): void {
    if (this.quizTimerInterval) {
      clearInterval(this.quizTimerInterval);
      this.quizTimerInterval = null;
    }
    
    // 隐藏计时器
    const timerEl = document.getElementById('quiz-timer');
    if (timerEl) {
      timerEl.style.display = 'none';
      timerEl.classList.remove('warning', 'danger');
    }
  }

  /**
   * 更新计时器显示
   */
  private updateTimerDisplay(): void {
    const timerEl = document.getElementById('quiz-timer');
    const displayEl = document.getElementById('quiz-timer-display');
    
    if (!displayEl) return;
    
    const minutes = Math.floor(this.quizTimeRemaining / 60);
    const seconds = this.quizTimeRemaining % 60;
    displayEl.textContent = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    
    // 更新状态样式
    if (timerEl) {
      timerEl.classList.remove('warning', 'danger');
      if (this.quizTimeRemaining <= 30) {
        timerEl.classList.add('danger');
      } else if (this.quizTimeRemaining <= 60) {
        timerEl.classList.add('warning');
      }
    }
  }

  /**
   * 时间到处理
   */
  private handleTimeUp(): void {
    // 计时器已在调用前停止，直接显示结果
    this.app.log('时间到！挑战结束', 'warning');
    this.showQuizResult();
  }

  /**
   * 显示刷题首页
   */
  private showQuizHome(): void {
    document.querySelectorAll('.practice-view').forEach(v => v.classList.remove('active'));
    document.getElementById('practice-quiz')?.classList.add('active');
    
    // 显示TAB标签
    const tabsEl = document.querySelector('.practice-tabs') as HTMLElement;
    if (tabsEl) tabsEl.style.display = 'flex';
    
    this.updateQuizPage();
  }

  /**
   * 确认提交刷题
   */
  private confirmQuizSubmit(): void {
    // 检查未作答的题目数量
    const unansweredCount = this.quizQueue.length - this.quizAnsweredProblems.size;
    
    if (unansweredCount > 0) {
      // 有未作答的题，显示自定义确认弹窗
      this.showConfirmModal(
        `还有 ${unansweredCount} 道题未作答，确定要提交吗？`,
        () => this.showQuizResult()
      );
    } else {
      // 所有题都已作答，直接提交
      this.showQuizResult();
    }
  }

  /**
   * 显示自定义确认弹窗
   */
  private showConfirmModal(message: string, onConfirm: () => void): void {
    const modal = document.getElementById('custom-confirm-modal');
    const messageEl = document.getElementById('confirm-modal-message');
    const cancelBtn = document.getElementById('confirm-modal-cancel');
    const okBtn = document.getElementById('confirm-modal-ok');
    const backdrop = modal?.querySelector('.confirm-modal-backdrop');
    
    if (!modal || !messageEl || !cancelBtn || !okBtn) return;
    
    // 设置消息
    messageEl.textContent = message;
    
    // 显示弹窗
    modal.classList.add('show');
    
    // 清理之前的事件监听
    const newCancelBtn = cancelBtn.cloneNode(true) as HTMLElement;
    const newOkBtn = okBtn.cloneNode(true) as HTMLElement;
    cancelBtn.parentNode?.replaceChild(newCancelBtn, cancelBtn);
    okBtn.parentNode?.replaceChild(newOkBtn, okBtn);
    
    // 关闭弹窗函数
    const closeModal = () => {
      modal.classList.remove('show');
    };
    
    // 绑定事件
    newCancelBtn.addEventListener('click', closeModal);
    newOkBtn.addEventListener('click', () => {
      closeModal();
      onConfirm();
    });
    backdrop?.addEventListener('click', closeModal);
  }

  /**
   * 显示自定义信息弹窗（只有确定按钮）
   */
  private showAlertModal(title: string, message: string): void {
    const modal = document.getElementById('custom-alert-modal');
    const titleEl = document.getElementById('alert-modal-title');
    const messageEl = document.getElementById('alert-modal-message');
    const okBtn = document.getElementById('alert-modal-ok');
    const backdrop = modal?.querySelector('.confirm-modal-backdrop');
    
    if (!modal || !titleEl || !messageEl || !okBtn) return;
    
    // 设置内容
    titleEl.textContent = title;
    messageEl.textContent = message;
    
    // 显示弹窗
    modal.classList.add('show');
    
    // 清理之前的事件监听
    const newOkBtn = okBtn.cloneNode(true) as HTMLElement;
    okBtn.parentNode?.replaceChild(newOkBtn, okBtn);
    
    // 关闭弹窗函数
    const closeModal = () => {
      modal.classList.remove('show');
    };
    
    // 绑定事件
    newOkBtn.addEventListener('click', closeModal);
    backdrop?.addEventListener('click', closeModal);
  }

  /**
   * 显示Toast轻量级提示
   */
  private showToast(message: string, type: 'info' | 'success' | 'warning' | 'error' = 'info', duration: number = 2000): void {
    const container = document.getElementById('toast-container');
    if (!container) return;
    
    // 创建Toast元素
    const toast = document.createElement('div');
    toast.className = `toast ${type}`;
    
    // 图标映射
    const icons: Record<string, string> = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      error: '❌'
    };
    
    toast.innerHTML = `
      <span class="toast-icon">${icons[type]}</span>
      <span class="toast-message">${message}</span>
    `;
    
    container.appendChild(toast);
    
    // 触发显示动画
    requestAnimationFrame(() => {
      toast.classList.add('show');
    });
    
    // 自动消失
    setTimeout(() => {
      toast.classList.remove('show');
      toast.classList.add('hide');
      setTimeout(() => {
        toast.remove();
      }, 300);
    }, duration);
  }

  /**
   * 显示刷题结果成绩单
   */
  private showQuizResult(): void {
    // 停止计时器
    this.stopQuizTimer();
    
    // 隐藏刷题控制栏
    this.hideQuizControlBar();
    
    // 保存本轮刷题统计到localStorage
    this.saveQuizSessionStats();
    
    // 基于每道题的最终结果计算统计数据
    const { correctCount, wrongCount: savedWrongCount } = this.getQuizResultStats();
    const total = this.quizQueue.length;
    const savedCount = correctCount + savedWrongCount;  // 已保存的题目数
    const unansweredCount = total - savedCount;  // 未作答数
    // 答错数 = 保存为错误的题目数
    const wrongCount = savedWrongCount;
    const rate = total > 0 ? Math.round((correctCount / total) * 100) : 0;
    
    // 更新成绩单数据
    const modeLabelEl = document.getElementById('result-mode-label');
    const totalEl = document.getElementById('result-total');
    const correctEl = document.getElementById('result-correct');
    const wrongEl = document.getElementById('result-wrong');
    const unansweredEl = document.getElementById('result-unanswered');
    const rateEl = document.getElementById('result-rate');
    
    if (modeLabelEl) modeLabelEl.textContent = this.quizModeLabel;
    if (totalEl) totalEl.textContent = total.toString();
    if (correctEl) correctEl.textContent = correctCount.toString();
    if (wrongEl) wrongEl.textContent = wrongCount.toString();
    if (unansweredEl) unansweredEl.textContent = unansweredCount.toString();
    if (rateEl) rateEl.textContent = `${rate}%`;
    
    // 显示成绩单页面
    document.querySelectorAll('.practice-view').forEach(v => v.classList.remove('active'));
    document.getElementById('practice-quiz-result')?.classList.add('active');
    
    // 绑定按钮事件
    const backBtn = document.getElementById('result-back-btn');
    const retryBtn = document.getElementById('result-retry-btn');
    
    if (backBtn) {
      backBtn.onclick = () => {
        this.quizSessionActive = false;
        this.showQuizHome();
      };
    }
    
    if (retryBtn) {
      retryBtn.onclick = () => {
        // 重新开始同样模式
        this.startQuizMode(this.quizMode);
      };
    }
  }

  /**
   * 获取错题列表
   */
  private getWrongProblems(): string[] {
    try {
      const data = localStorage.getItem('wrong_problems');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  /**
   * 添加错题
   */
  private addWrongProblem(problemId: string): void {
    try {
      const wrongProblems = this.getWrongProblems();
      if (!wrongProblems.includes(problemId)) {
        wrongProblems.push(problemId);
        localStorage.setItem('wrong_problems', JSON.stringify(wrongProblems));
        console.log(`❌ 题目 ${problemId} 已加入错题本`);
      }
    } catch (e) {
      console.error('保存错题失败', e);
    }
  }

  /**
   * 移除错题（答对后）
   */
  private removeWrongProblem(problemId: string): void {
    try {
      let wrongProblems = this.getWrongProblems();
      if (wrongProblems.includes(problemId)) {
        wrongProblems = wrongProblems.filter(id => id !== problemId);
        localStorage.setItem('wrong_problems', JSON.stringify(wrongProblems));
        console.log(`✅ 题目 ${problemId} 已从错题本移除`);
      }
    } catch (e) {
      console.error('移除错题失败', e);
    }
  }

  /**
   * 数组洗牌
   */
  private shuffleArray<T>(array: T[]): T[] {
    const result = [...array];
    for (let i = result.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
  }

  /**
   * 筛选题目
   */
  private filterProblems(): void {
    const difficultyFilter = document.querySelector('.filter-btn[data-difficulty].active')?.getAttribute('data-difficulty') || 'all';
    const categoryFilter = document.querySelector('.filter-btn[data-category].active')?.getAttribute('data-category') || 'all';
    
    const cards = document.querySelectorAll('.problem-card');
    cards.forEach(card => {
      const cardEl = card as HTMLElement;
      const cardDifficulty = cardEl.dataset.difficulty;
      const cardCategory = cardEl.dataset.category;
      
      const matchDifficulty = difficultyFilter === 'all' || cardDifficulty === difficultyFilter;
      const matchCategory = categoryFilter === 'all' || cardCategory === categoryFilter;
      
      if (matchDifficulty && matchCategory) {
        cardEl.style.display = 'block';
      } else {
        cardEl.style.display = 'none';
      }
    });
  }

  /**
   * 查看答案
   */
  private showAnswer(): void {
    if (!this.currentProblemId) {
      this.app.log('请先选择一道题目', 'warning');
      return;
    }

    const langSelect = document.getElementById('code-language') as HTMLSelectElement;
    const lang = langSelect?.value || 'javascript';
    const answers = PROBLEM_ANSWERS[this.currentProblemId];
    
    if (answers) {
      // 优先使用当前语言的答案，否则用JS
      const answer = answers[lang] || answers['javascript'] || '暂无该语言的答案';
      this.setEditorCode(answer);
      this.updateCodeMirrorMode(lang);
      this.app.log('已加载参考答案', 'success');
    } else {
      this.setEditorCode('// 暂无该题目的参考答案');
      this.app.log('暂无该题目的答案', 'warning');
    }
  }

  /**
   * 测试 Judge0 API 连接
   */
  private async testJudge0Connection(): Promise<void> {
    const statusEl = document.getElementById('api-status');
    const statusHomeEl = document.getElementById('api-status-home');
    
    const updateStatus = (text: string, className: string, homeText?: string) => {
      if (statusEl) {
        statusEl.textContent = text;
        statusEl.className = `api-status ${className}`;
      }
      if (statusHomeEl) {
        statusHomeEl.textContent = homeText || text.replace('API: ', '');
        statusHomeEl.style.color = className === 'connected' ? 'var(--accent-success)' : 
                                   className === 'error' ? 'var(--accent-error)' : 'var(--accent-warning)';
      }
    };
    
    updateStatus('灵梦编辑器: 检测中...', 'testing', '检测中');
    this.app.log('JavaScript本地执行始终可用', 'success');
    
    // Wandbox API 检测（使用 list.json 端点）
    const apiUrl = 'https://wandbox.org/api/list.json';
    console.log(`%c=== 灵梦编辑器连接测试 ===%c`, 'color: #00ff00; font-weight: bold', 'color: inherit');
    console.log(`🔍 API地址: ${apiUrl}`);
    console.log(`🔍 当前时间: ${new Date().toISOString()}`);
    console.log(`🔍 浏览器: ${navigator.userAgent}`);
    console.log(`🔍 页面URL: ${window.location.href}`);
    this.app.log(`正在连接 Wandbox API...`, 'info');

    try {
      console.log(`📡 发送GET请求...`);
      const startTime = Date.now();
      
      // 最简化的fetch请求
      console.log(`📡 尝试最简化请求...`);
      const response = await fetch(apiUrl);
      
      const elapsed = Date.now() - startTime;
      
      console.log(`%c=== 响应详情 ===%c`, 'color: #00ff00', 'color: inherit');
      console.log(`📨 耗时: ${elapsed}ms`);
      console.log(`📨 HTTP状态码: ${response.status}`);
      console.log(`📨 状态文本: ${response.statusText}`);
      console.log(`📨 响应OK: ${response.ok}`);
      console.log(`📨 响应类型: ${response.type}`);
      console.log(`📨 响应URL: ${response.url}`);
      
      // 输出响应头
      console.log(`📨 响应头:`);
      response.headers.forEach((value, key) => {
        console.log(`   ${key}: ${value}`);
      });

      if (response.ok) {
        const data = await response.json();
        console.log(`✅ [灵梦编辑器] API连接成功!`);
        console.log(`✅ [灵梦编辑器] 版本信息:`, data);
        
        this.judge0Available = true;
        updateStatus('灵梦编辑器已就绪', 'connected', '已就绪');
        this.app.log(`灵梦编辑器已就绪！(耗时${elapsed}ms)`, 'success');
      } else {
        const errorText = await response.text();
        console.log(`❌ [灵梦编辑器] HTTP错误: ${response.status}`);
        console.log(`❌ [灵梦编辑器] 错误内容:`, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }
    } catch (error: any) {
      this.judge0Available = false;
      
      console.log(`%c=== 连接失败 ===%c`, 'color: #ff0000; font-weight: bold', 'color: inherit');
      console.log(`❌ 错误名称: ${error.name}`);
      console.log(`❌ 错误消息: ${error.message}`);
      console.log(`❌ 错误堆栈:`, error.stack);
      console.log(`❌ 完整错误对象:`, error);
      
      if (error.name === 'AbortError') {
        updateStatus('灵梦编辑器: 超时', 'error', '超时');
        this.app.log(`灵梦编辑器连接超时`, 'error');
      } else if (error.message.includes('Failed to fetch')) {
        updateStatus('灵梦编辑器: 离线模式', 'error', 'JS可用');
        this.app.log(`Wandbox服务无法连接，JavaScript本地执行仍可用`, 'warning');
        console.log(`💡 [提示] Wandbox服务器可能暂时不可用`);
        console.log(`   - JavaScript代码可在本地运行`);
        console.log(`   - 其他语言需等待服务恢复`);
      } else {
        updateStatus('灵梦编辑器: 无法连接', 'error', '无法连接');
        this.app.log(`灵梦编辑器连接失败: ${error.message}`, 'error');
      }
    }
  }

    /**
   * 运行代码
   */
  private async runCode(): Promise<void> {
    const langSelect = document.getElementById('code-language') as HTMLSelectElement;
    const outputEl = document.getElementById('code-output');
    
    if (!outputEl) return;

    const code = this.getEditorCode();
    const language = langSelect?.value || 'javascript';

    // 刷题模式：只要点击运行代码就算已作答
    if (this.quizSessionActive && this.currentProblemId) {
      this.quizAnsweredProblems.add(this.currentProblemId);
    }

    // 显示运行中状态（使用SVG图标而非表情）
    outputEl.innerHTML = `
      <div class="output-info output-running">
        <svg class="spinning-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10" stroke-opacity="0.25"/>
          <path d="M12 2a10 10 0 0 1 10 10" stroke-linecap="round"/>
        </svg>
        正在运行代码...
      </div>
    `;
    
    // 小屏设备：同步状态并切换到结果TAB
    this.syncOutputToSmallScreen();
    this.switchCodingTab('result');

    if (!this.judge0Available) {
      // 如果Wandbox不可用，尝试本地执行JS
      if (language === 'javascript') {
        this.runJavaScriptLocally(code, outputEl);
      } else {
        outputEl.innerHTML = `
          <div class="result-card error">
            <div class="result-header">
              <span class="result-title">连接失败</span>
            </div>
            <div class="result-body">
              <div class="result-tip">Wandbox API 无法连接</div>
              <div class="result-hint">网络可能无法访问Wandbox服务。JavaScript可以在本地运行，请切换语言。</div>
            </div>
          </div>
        `;
      }
      return;
    }

    try {
      // 调用Wandbox API
      const result = await this.executeWithWandbox(code, language);
      this.displayWandboxResult(result, outputEl);
    } catch (error: any) {
      outputEl.innerHTML = `
        <div class="result-card error">
          <div class="result-header">
            <span class="result-title">运行失败</span>
          </div>
          <div class="result-body">
            <div class="result-output error-text">${error.message}</div>
          </div>
        </div>
      `;
    }
  }

  /**
   * 本地执行JavaScript代码
   */
  private runJavaScriptLocally(code: string, outputEl: HTMLElement): void {
    console.log('⚡ 本地运行JS, 刷题模式状态:', {
      quizSessionActive: this.quizSessionActive,
      currentProblemId: this.currentProblemId,
      quizCurrentIndex: this.quizCurrentIndex
    });
    
    try {
      // 创建一个安全的执行环境
      const logs: string[] = [];
      const customConsole = {
        log: (...args: any[]) => logs.push(args.map(a => JSON.stringify(a)).join(' ')),
        error: (...args: any[]) => logs.push(`[ERROR] ${args.join(' ')}`),
        warn: (...args: any[]) => logs.push(`[WARN] ${args.join(' ')}`)
      };

      // 执行代码
      const wrappedCode = `
        (function(console) {
          ${code}
          // 如果有twoSum函数，测试它
          if (typeof twoSum === 'function') {
            console.log('测试: twoSum([2,7,11,15], 9)');
            console.log('结果:', twoSum([2,7,11,15], 9));
          }
        })
      `;
      const fn = eval(wrappedCode);
      fn(customConsole);

      if (logs.length > 0) {
        const output = logs.join('\n');
        // 校验答案
        const verification = this.verifyAnswer(output);
        
        if (verification.passed) {
          // 答案正确，标记完成
          if (this.currentProblemId) {
            this.markProblemCompleted(this.currentProblemId);
            this.removeWrongProblem(this.currentProblemId);
          }
          
          // 刷题模式：统计答对
          if (this.quizSessionActive) {
            this.recordQuizProblemResult(this.currentProblemId, true);
            
            // 判断是否是最后一题
            const isLastProblem = this.quizCurrentIndex >= this.quizQueue.length - 1;
            
            if (isLastProblem) {
              // 最后一题答对，提示用户可以交卷
              outputEl.innerHTML = `
                <div class="result-card success">
                  <div class="result-header">
                    <span class="result-title">答案正确</span>
                  </div>
                  <div class="result-body">
                    <div class="result-label">输出结果</div>
                    <pre class="result-output">${output}</pre>
                    <div class="result-hint">已完成全部题目，点击"保存答案"后提交</div>
                  </div>
                </div>
              `;
            } else {
              // 提示用户保存答案
              outputEl.innerHTML = `
                <div class="result-card success">
                  <div class="result-header">
                    <span class="result-title">答案正确</span>
                  </div>
                  <div class="result-body">
                    <div class="result-label">输出结果</div>
                    <pre class="result-output">${output}</pre>
                    <div class="result-hint">点击"保存答案"确认提交</div>
                  </div>
                </div>
              `;
            }
            return;
          }
          
          outputEl.innerHTML = `
            <div class="result-card success">
              <div class="result-header">
                <span class="result-title">答案正确</span>
              </div>
              <div class="result-body">
                <div class="result-label">输出结果</div>
                <pre class="result-output">${output}</pre>
              </div>
            </div>
          `;
        } else {
          // 刷题模式：答案错误统计
          if (this.quizSessionActive) {
            this.recordQuizProblemResult(this.currentProblemId, false);
            if (this.currentProblemId) {
              this.addWrongProblem(this.currentProblemId);
            }
          }
          
          outputEl.innerHTML = `
            <div class="result-card warning">
              <div class="result-header">
                <span class="result-title">运行成功</span>
              </div>
              <div class="result-body">
                <div class="result-label">输出结果</div>
                <pre class="result-output">${output}</pre>
                <div class="result-hint">提示：${verification.message}</div>
              </div>
            </div>
          `;
        }
      } else {
        // 刷题模式：没有输出也算答错
        if (this.quizSessionActive && this.currentProblemId) {
          this.recordQuizProblemResult(this.currentProblemId, false);
          this.addWrongProblem(this.currentProblemId);
        }
        
        outputEl.innerHTML = `
          <div class="result-card info">
            <div class="result-header">
              <span class="result-title">执行完成</span>
            </div>
            <div class="result-body">
              <div class="result-tip">代码已执行，但没有输出内容</div>
              <div class="result-hint">提示：使用 <code>console.log()</code> 来查看结果</div>
            </div>
          </div>
        `;
      }
    } catch (error: any) {
      // 刷题模式：运行出错也算答错
      if (this.quizSessionActive && this.currentProblemId) {
        this.recordQuizProblemResult(this.currentProblemId, false);
        this.addWrongProblem(this.currentProblemId);
      }
      
      outputEl.innerHTML = `
        <div class="result-card error">
          <div class="result-header">
            <span class="result-title">运行出错</span>
          </div>
          <div class="result-body">
            <div class="result-label">错误信息</div>
            <pre class="result-output error-text">${error.message}</pre>
            <div class="result-hint">提示：请检查代码语法是否正确</div>
          </div>
        </div>
      `;
    }
  }

  /**
   * 使用Wandbox执行代码
   */
  private async executeWithWandbox(code: string, language: string): Promise<any> {
    const compiler = LANGUAGE_MAP[language] || LANGUAGE_MAP['javascript'];

    // 提交代码到Wandbox
    const response = await fetch(WANDBOX_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code: code,
        compiler: compiler
      })
    });

    if (!response.ok) {
      throw new Error(`提交失败: HTTP ${response.status}`);
    }

    const result = await response.json();
    return result;
  }

  /**
   * 显示Wandbox运行结果
   */
  private displayWandboxResult(result: any, outputEl: HTMLElement): void {
    const stdout = result.program_output || '';
    const stderr = result.program_error || result.compiler_error || '';
    const status = result.status;
    
    // 成功执行且有输出
    if (stdout && !stderr && status === '0') {
      // 校验答案
      const verification = this.verifyAnswer(stdout);
      
      if (verification.passed) {
        // 答案正确，标记完成
        if (this.currentProblemId) {
          this.markProblemCompleted(this.currentProblemId);
          this.removeWrongProblem(this.currentProblemId);
        }
        
        // 刷题模式：统计答对
        if (this.quizSessionActive) {
          this.recordQuizProblemResult(this.currentProblemId, true);
          
          const isLastProblem = this.quizCurrentIndex >= this.quizQueue.length - 1;
          
          if (isLastProblem) {
            outputEl.innerHTML = `
              <div class="result-card success">
                <div class="result-header">
                  <span class="result-title">答案正确</span>
                </div>
                <div class="result-body">
                  <div class="result-label">输出结果</div>
                  <pre class="result-output">${this.escapeHtml(stdout)}</pre>
                  <div class="result-hint">已完成全部题目，点击"保存答案"后提交</div>
                </div>
              </div>
            `;
          } else {
            outputEl.innerHTML = `
              <div class="result-card success">
                <div class="result-header">
                  <span class="result-title">答案正确</span>
                </div>
                <div class="result-body">
                  <div class="result-label">输出结果</div>
                  <pre class="result-output">${this.escapeHtml(stdout)}</pre>
                  <div class="result-hint">点击"保存答案"确认提交</div>
                </div>
              </div>
            `;
          }
          return;
        }
        
        outputEl.innerHTML = `
          <div class="result-card success">
            <div class="result-header">
              <span class="result-title">答案正确</span>
            </div>
            <div class="result-body">
              <div class="result-label">输出结果</div>
              <pre class="result-output">${this.escapeHtml(stdout)}</pre>
            </div>
          </div>
        `;
      } else {
        // 刷题模式：答案错误统计
        if (this.quizSessionActive) {
          this.recordQuizProblemResult(this.currentProblemId, false);
          if (this.currentProblemId) {
            this.addWrongProblem(this.currentProblemId);
          }
        }
        
        outputEl.innerHTML = `
          <div class="result-card warning">
            <div class="result-header">
              <span class="result-title">运行成功</span>
            </div>
            <div class="result-body">
              <div class="result-label">输出结果</div>
              <pre class="result-output">${this.escapeHtml(stdout)}</pre>
              <div class="result-hint">提示：${verification.message}</div>
            </div>
          </div>
        `;
      }
      return;
    }

    // 有错误输出
    if (stderr) {
      if (this.quizSessionActive) {
        this.recordQuizProblemResult(this.currentProblemId, false);
        if (this.currentProblemId) {
          this.addWrongProblem(this.currentProblemId);
        }
      }
      
      outputEl.innerHTML = `
        <div class="result-card error">
          <div class="result-header">
            <span class="result-title">运行出错</span>
          </div>
          <div class="result-body">
            ${stdout ? `<div class="result-label">输出</div><pre class="result-output">${this.escapeHtml(stdout)}</pre>` : ''}
            <div class="result-label">错误信息</div>
            <pre class="result-output error-text">${this.escapeHtml(stderr)}</pre>
            <div class="result-hint">提示：仔细检查代码逻辑和语法</div>
          </div>
        </div>
      `;
      return;
    }

    // 无输出
    if (!stdout && !stderr) {
      if (this.quizSessionActive) {
        this.recordQuizProblemResult(this.currentProblemId, false);
        if (this.currentProblemId) {
          this.addWrongProblem(this.currentProblemId);
        }
      }
      
      outputEl.innerHTML = `
        <div class="result-card info">
          <div class="result-header">
            <span class="result-title">执行完成</span>
          </div>
          <div class="result-body">
            <div class="result-tip">代码已执行，但没有输出内容</div>
            <div class="result-hint">提示：添加 print/console.log 来查看结果</div>
          </div>
        </div>
      `;
      return;
    }

    // 其他情况
    outputEl.innerHTML = `
      <div class="result-card warning">
        <div class="result-header">
          <span class="result-title">执行完成</span>
        </div>
        <div class="result-body">
          <div class="result-label">输出结果</div>
          <pre class="result-output">${this.escapeHtml(stdout || '无输出')}</pre>
        </div>
      </div>
    `;
  }

  /**
   * HTML转义
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * 更新代码模板
   */
  private updateCodeTemplate(language: string): void {
    const templates: Record<string, string> = {
      javascript: `// 在这里编写你的代码
function twoSum(nums, target) {
    // 你的代码
    
}`,
      python: `# 在这里编写你的代码
def twoSum(nums, target):
    # 你的代码
    pass`,
      java: `// 在这里编写你的代码
class Main {
    public static int[] twoSum(int[] nums, int target) {
        // 你的代码
        return new int[]{0, 1};
    }
    
    public static void main(String[] args) {
        // 测试代码
    }
}`,
      cpp: `// 在这里编写你的代码
#include <iostream>
#include <vector>
using namespace std;

vector<int> twoSum(vector<int>& nums, int target) {
    // 你的代码
    return {0, 1};
}

int main() {
    // 测试代码
    return 0;
}`
    };

    this.setEditorCode(templates[language] || templates.javascript);
  }

  /**
   * 加载题目
   */
  private loadProblem(problemId: string): void {
    const info = PROBLEM_INFO[problemId];
    if (!info) {
      console.log(`加载题目失败: ${problemId} 不存在`);
      return;
    }

        // 更新题目描述内容
    const contentEl = document.getElementById('problem-content');
    if (contentEl) {
      const description = info.description || `暂无题目描述`;
      
      // 根据题目ID生成示例
      const examples = this.getProblemExamples(problemId);
      
      contentEl.innerHTML = `
        <div class="problem-description">
          <p>${description}</p>
        </div>
        <div class="problem-examples">
          <h4>示例</h4>
          ${examples}
        </div>
      `;
      
      // 同步题目内容到小屏视图
      const contentSmEl = document.getElementById('problem-content-sm');
      if (contentSmEl) {
        contentSmEl.innerHTML = contentEl.innerHTML;
      }
    }

    // 加载默认代码模板
    const langSelect = document.getElementById('code-language') as HTMLSelectElement;
    const lang = langSelect?.value || 'javascript';
    const template = this.getProblemCodeTemplate(problemId, lang);
    this.setEditorCode(template);
    this.updateCodeMirrorMode(lang);

    console.log(`加载题目: ${problemId}`);
    this.app.log(`加载题目: ${info.name}`, 'info');
  }

  /**
   * 获取题目示例
   */
  private getProblemExamples(problemId: string): string {
    // 优先从测试用例中获取示例描述
    const testCase = PROBLEM_TEST_CASES[problemId];
    if (testCase && testCase.description) {
      // 解析 testCase.description，格式通常为："输入 xxx，输出 xxx" 或 "输入 xxx，期望输出 xxx"
      const desc = testCase.description;
        
      // 尝试解析输入和输出
      let inputPart = '';
      let outputPart = '';
        
      // 支持多种格式："输入 ... 输出 ..." 或 "输入 ... 期望输出 ..."
      const outputMatch = desc.match(/[，,]\s*(期望输出|输出)[：:]?\s*(.+)$/i);
      const inputMatch = desc.match(/^输入[：:]?\s*(.+?)[，,]\s*(期望输出|输出)/i);
        
      if (inputMatch && outputMatch) {
        inputPart = inputMatch[1].trim();
        outputPart = outputMatch[2].trim();
        return `
          <div class="example-box">
            <p><strong>输入：</strong>${inputPart}</p>
            <p><strong>输出：</strong>${outputPart}</p>
          </div>`;
      } else {
        // 如果无法解析，直接显示整个描述
        return `
          <div class="example-box">
            <p>${desc}</p>
          </div>`;
      }
    }
      
    return '<div class="example-box"><p>暂无示例</p></div>';
  }

  /**
   * 获取实操大厅题目代码模板
   */
  private getProblemCodeTemplate(problemId: string, language: string): string {
    const templates: Record<string, Record<string, string>> = {
      'two-sum': {
        javascript: `// 两数之和
function twoSum(nums, target) {
    // 你的代码
    
}

// 测试
console.log(twoSum([2, 7, 11, 15], 9));`,
        python: `# 两数之和
def twoSum(nums, target):
    # 你的代码
    pass

# 测试
print(twoSum([2, 7, 11, 15], 9))`
      },
      'reverse-string': {
        javascript: `// 反转字符串
function reverseString(s) {
    // 你的代码
    
}

// 测试
console.log(reverseString(["h","e","l","l","o"]));`,
        python: `# 反转字符串
def reverseString(s):
    # 你的代码
    pass

# 测试
print(reverseString(["h","e","l","l","o"]))`
      },
      'palindrome': {
        javascript: `// 回文数
function isPalindrome(x) {
    // 你的代码
    
}

// 测试
console.log(isPalindrome(121));`,
        python: `# 回文数
def isPalindrome(x):
    # 你的代码
    pass

# 测试
print(isPalindrome(121))`
      }
    };
    
    // 返回对应模板，如果没有则返回默认模板
    const problemTemplates = templates[problemId];
    if (problemTemplates && problemTemplates[language]) {
      return problemTemplates[language];
    }
    
    // 默认模板
    const info = PROBLEM_INFO[problemId];
    const name = info?.name || problemId;
    if (language === 'python') {
      return `# ${name}
def solution():
    # 你的代码
    pass

# 测试
print(solution())`;
    }
    if (language === 'java') {
      return `// ${name}
import java.util.*;

class Main {
    public static void solution() {
        // 你的代码
        
    }
    
    public static void main(String[] args) {
        solution();
    }
}`;
    }
    if (language === 'cpp') {
      return `// ${name}
#include <iostream>
using namespace std;

void solution() {
    // 你的代码
    
}

int main() {
    solution();
    return 0;
}`;
    }
    return `// ${name}
function solution() {
    // 你的代码
    
}

// 测试
console.log(solution());`;
  }

  // ========================================
  // CodeMirror 编辑器相关
  // ========================================

  /**
   * 初始CodeMirror编辑器
   */
  private initCodeMirror(): void {
    const wrapper = document.getElementById('code-editor-wrapper');
    if (!wrapper) return;

    // 检查CodeMirror是否加载
    if (typeof (window as any).CodeMirror === 'undefined') {
      console.warn('CodeMirror 未加载，使用普通文本域');
      wrapper.innerHTML = `<textarea id="code-editor" class="code-textarea" spellcheck="false">// 在这里编写你的代码\nfunction twoSum(nums, target) {\n    // 你的代码\n}</textarea>`;
      return;
    }

    // 检测是否为小屏设备（使用屏幕高度判断，≤700px为小屏）
    const isSmallScreen = window.screen.height <= 700;
    
    // 读取编辑器设置
    const editorSettings = settingsModule.getEditorSettings();
    
    // 主题映射：设置中的主题名 -> CodeMirror主题名
    const themeMap: Record<string, string> = {
      'dracula': 'dracula',
      'monokai': 'monokai',
      'github-dark': 'material-darker',
      'one-dark': 'material-ocean'
    };
    const initialTheme = themeMap[editorSettings.theme] || 'dracula';
    
    // 创建CodeMirror实例
    this.codeMirrorEditor = (window as any).CodeMirror(wrapper, {
      value: `// 在这里编写你的代码
function twoSum(nums, target) {
    // 你的代码
    
}`,
      mode: 'javascript',
      theme: initialTheme,
      lineNumbers: editorSettings.lineNumbers,  // 从设置读取，重启后生效
      matchBrackets: editorSettings.matchBrackets,  // 括号匹配高亮
      autoCloseBrackets: true,
      indentUnit: 4,
      tabSize: 4,
      indentWithTabs: false,
      lineWrapping: editorSettings.lineWrapping,  // 自动换行
      styleActiveLine: editorSettings.styleActiveLine,  // 光标行高亮
      // 小屏设备优化配置
      inputStyle: isSmallScreen ? 'contenteditable' : 'textarea',  // 小屏使用contenteditable提升触摸体验
      viewportMargin: isSmallScreen ? 50 : 10,  // 小屏更大的视口边距
      // 代码补全配置
      hintOptions: {
        completeSingle: false,  // 不自动补全单个结果
        alignWithWord: true
      },
      extraKeys: {
        'Tab': (cm: any) => {
          cm.replaceSelection('    ', 'end');
        }
      }
    });

    // 初始化后延迟刷新，确保布局正确
    setTimeout(() => {
      if (this.codeMirrorEditor) {
        this.codeMirrorEditor.refresh();
      }
    }, 100);
    
    // 再次刷新，确保容器完全渲染
    setTimeout(() => {
      if (this.codeMirrorEditor) {
        this.codeMirrorEditor.refresh();
      }
    }, 500);

    // 小屏设备额外优化
    if (isSmallScreen) {
      // 触摸结束后确保光标可见
      this.codeMirrorEditor.on('touchend', () => {
        const cursor = this.codeMirrorEditor.getCursor();
        this.codeMirrorEditor.scrollIntoView(cursor);
      });
    }
    
    // 编辑器获得焦点时刷新布局（解决行号开关后渲染异常问题）
    this.codeMirrorEditor.on('focus', () => {
      setTimeout(() => {
        if (this.codeMirrorEditor) {
          this.codeMirrorEditor.refresh();
        }
      }, 50);
    });

    console.log(`✅ CodeMirror 编辑器已初始化 (小屏: ${isSmallScreen}, 屏幕高度: ${window.screen.height}px)`);
    
    // 立即应用字体大小设置
    const cmWrapper = this.codeMirrorEditor.getWrapperElement();
    if (cmWrapper) {
      cmWrapper.style.fontSize = `${editorSettings.fontSize}px`;
    }
    
    // 应用设置模块的配置
    this.applyEditorSettings();
    
    // 注册设置变更回调
    settingsModule.setEditorSettingsChangeCallback((settings) => {
      this.applyEditorSettingsToCodeMirror(settings);
    });
    
    // 输入时自动触发代码补全
    this.codeMirrorEditor.on('inputRead', (cm: any, change: any) => {
      // 检查设置中是否开启了自动补全
      const editorSettings = settingsModule.getEditorSettings();
      if (!editorSettings.autoComplete) return;
      
      // 只在输入字符时触发，不在删除或粘贴时触发
      if (change.origin === '+input' && change.text[0].match(/[a-zA-Z_]/)) {
        // 延迟触发，避免太频繁
        setTimeout(() => {
          if (!cm.state.completionActive) {
            (window as any).CodeMirror.commands.autocomplete(cm, null, { completeSingle: false });
          }
        }, 150);
      }
    });
  }

  /**
   * 更新CodeMirror语言模式
   */
  private updateCodeMirrorMode(language: string): void {
    if (!this.codeMirrorEditor) return;

    const modeMap: Record<string, string> = {
      'javascript': 'javascript',
      'python': 'python',
      'java': 'text/x-java',
      'cpp': 'text/x-c++src'
    };

    this.codeMirrorEditor.setOption('mode', modeMap[language] || 'javascript');
  }
  
  /**
   * 应用编辑器设置（初始化时调用）
   */
  private applyEditorSettings(): void {
    const settings = settingsModule.getEditorSettings();
    this.applyEditorSettingsToCodeMirror(settings);
  }
  
  /**
   * 应用编辑器设置到 CodeMirror
   */
  private applyEditorSettingsToCodeMirror(settings: { 
    theme: string; 
    fontSize: number; 
    lineNumbers: boolean;
    lineWrapping?: boolean;
    styleActiveLine?: boolean;
    matchBrackets?: boolean;
  }): void {
    if (!this.codeMirrorEditor) return;
    
    // 主题映射：设置中的主题名 -> CodeMirror主题名
    const themeMap: Record<string, string> = {
      'dracula': 'dracula',
      'monokai': 'monokai',
      'github-dark': 'material-darker',
      'one-dark': 'material-ocean'
    };
    
    // 应用主题
    const cmTheme = themeMap[settings.theme] || 'dracula';
    this.codeMirrorEditor.setOption('theme', cmTheme);
    
    // 行号设置不热更新，重启后生效
    
    // 应用自动换行
    if (settings.lineWrapping !== undefined) {
      this.codeMirrorEditor.setOption('lineWrapping', settings.lineWrapping);
    }
    
    // 应用光标行高亮
    if (settings.styleActiveLine !== undefined) {
      this.codeMirrorEditor.setOption('styleActiveLine', settings.styleActiveLine);
    }
    
    // 应用括号匹配高亮
    if (settings.matchBrackets !== undefined) {
      this.codeMirrorEditor.setOption('matchBrackets', settings.matchBrackets);
    }
    
    // 应用字体大小（通过CSS）
    const cmWrapper = this.codeMirrorEditor.getWrapperElement();
    if (cmWrapper) {
      cmWrapper.style.fontSize = `${settings.fontSize}px`;
      // 确保内部元素也应用字体大小
      const cmElement = cmWrapper.querySelector('.CodeMirror') as HTMLElement;
      if (cmElement) {
        cmElement.style.fontSize = `${settings.fontSize}px`;
      }
    }
    
    // 刷新编辑器布局（多次刷新确保行号区域正确渲染）
    this.codeMirrorEditor.refresh();
    setTimeout(() => {
      if (this.codeMirrorEditor) {
        this.codeMirrorEditor.refresh();
      }
    }, 50);
    setTimeout(() => {
      if (this.codeMirrorEditor) {
        this.codeMirrorEditor.refresh();
      }
    }, 150);
    
    console.log(`⚙️ 编辑器设置已应用: 主题=${cmTheme}, 字号=${settings.fontSize}px`);
  }

  /**
   * 获取编辑器代码
   */
  private getEditorCode(): string {
    if (this.codeMirrorEditor) {
      return this.codeMirrorEditor.getValue();
    }
    const textarea = document.getElementById('code-editor') as HTMLTextAreaElement;
    return textarea?.value || '';
  }

  /**
   * 设置编辑器代码
   */
  private setEditorCode(code: string): void {
    if (this.codeMirrorEditor) {
      this.codeMirrorEditor.setValue(code);
      // 设置后刷新确保显示正确
      setTimeout(() => {
        this.codeMirrorEditor.refresh();
      }, 10);
    } else {
      const textarea = document.getElementById('code-editor') as HTMLTextAreaElement;
      if (textarea) textarea.value = code;
    }
  }

  // ========================================
  // 答案校验与题目完成状态
  // ========================================

  /**
   * 校验输出是否匹配期望答案
   */
  private verifyAnswer(output: string): { passed: boolean; message: string } {
    if (!this.currentProblemId) {
      return { passed: false, message: '未选择题目' };
    }

    const testCase = PROBLEM_TEST_CASES[this.currentProblemId];
    if (!testCase) {
      return { passed: false, message: '该题目暂无测试用例' };
    }

    // 清理输出（去除空白、换行）
    const cleanOutput = output.trim().replace(/\s+/g, '');
    
    // 检查输出是否为空
    if (!cleanOutput) {
      return { passed: false, message: '代码没有输出结果，请使用 console.log() 输出答案' };
    }
    
    // 检查是否匹配任一期望输出
    for (const expected of testCase.expectedOutputs) {
      const cleanExpected = expected.trim().replace(/\s+/g, '');
      // 完全匹配或包含匹配（但期望值不能为空）
      if (cleanExpected && (cleanOutput === cleanExpected || cleanOutput.includes(cleanExpected))) {
        return { passed: true, message: '答案正确！' };
      }
    }

    return { passed: false, message: `输出不匹配。${testCase.description}` };
  }

  /**
   * 标记题目为已完成
   */
  private markProblemCompleted(problemId: string): void {
    const completed = this.getCompletedProblems();
    if (!completed.includes(problemId)) {
      completed.push(problemId);
      localStorage.setItem('completed_problems', JSON.stringify(completed));
      this.app.log(`恭喜！题目已标记为完成`, 'success');
      console.log(`✅ 题目 ${problemId} 已标记为完成`);
    }
  }

  /**
   * 获取已完成的题目列表
   */
  private getCompletedProblems(): string[] {
    try {
      const data = localStorage.getItem('completed_problems');
      return data ? JSON.parse(data) : [];
    } catch {
      return [];
    }
  }

  /**
   * 检查题目是否已完成
   */
  private isProblemCompleted(problemId: string): boolean {
    return this.getCompletedProblems().includes(problemId);
  }

  /**
   * 收藏数据结构（带时间戳）
   */
  private getFavoritesData(): { [problemId: string]: { timestamp: number } } {
    try {
      const data = localStorage.getItem('favorites_data');
      if (data) {
        return JSON.parse(data);
      }
      // 向后兼容：迁移旧数据
      const oldData = localStorage.getItem('favorited_problems');
      if (oldData) {
        const oldList = JSON.parse(oldData) as string[];
        const newData: { [problemId: string]: { timestamp: number } } = {};
        oldList.forEach((id, index) => {
          newData[id] = { timestamp: Date.now() - (oldList.length - index) * 60000 };
        });
        localStorage.setItem('favorites_data', JSON.stringify(newData));
        return newData;
      }
      return {};
    } catch {
      return {};
    }
  }

  /**
   * 获取收藏的题目列表（按时间排序）
   */
  private getFavoritedProblems(): string[] {
    const data = this.getFavoritesData();
    return Object.entries(data)
      .sort((a, b) => b[1].timestamp - a[1].timestamp)
      .map(([id]) => id);
  }

  /**
   * 获取收藏时间
   */
  private getFavoriteTime(problemId: string): number | null {
    const data = this.getFavoritesData();
    return data[problemId]?.timestamp || null;
  }

  /**
   * 检查题目是否已收藏
   */
  private isProblemFavorited(problemId: string): boolean {
    return this.getFavoritedProblems().includes(problemId);
  }

  /**
   * 切换题目收藏状态
   */
  private toggleFavorite(problemId: string): boolean {
    const data = this.getFavoritesData();
    let isFavorited: boolean;
    
    if (data[problemId]) {
      delete data[problemId];
      isFavorited = false;
      console.log(`☆ 取消收藏: ${problemId}`);
    } else {
      data[problemId] = { timestamp: Date.now() };
      isFavorited = true;
      console.log(`⭐ 收藏题目: ${problemId}`);
    }
    
    localStorage.setItem('favorites_data', JSON.stringify(data));
    // 同步旧格式（兼容）
    localStorage.setItem('favorited_problems', JSON.stringify(Object.keys(data)));
    return isFavorited;
  }

  /**
   * 初始化收藏按钮状态
   */
  private initFavoriteButtons(): void {
    const favorites = this.getFavoritedProblems();
    
    // 设置收藏按钮初始状态
    favorites.forEach(problemId => {
      const card = document.querySelector(`.problem-card[data-problem="${problemId}"]`);
      if (card) {
        card.classList.add('favorited');
        const btn = card.querySelector('.favorite-btn');
        if (btn) btn.classList.add('active');
      }
    });
    
    // 绑定收藏按钮点击事件
    const favoriteBtns = document.querySelectorAll('.favorite-btn');
    favoriteBtns.forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();  // 阻止冒泡到卡片点击
        const target = e.currentTarget as HTMLElement;
        const problemId = target.dataset.problem;
        if (!problemId) return;
        
        const isFavorited = this.toggleFavorite(problemId);
        const card = target.closest('.problem-card');
        
        if (isFavorited) {
          target.classList.add('active');
          card?.classList.add('favorited');
        } else {
          target.classList.remove('active');
          card?.classList.remove('favorited');
        }
      });
    });
  }
}
